import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  runAnalyzer, runAnalyzerAdvanced, getAnalyzerBlocks, previewAnalyzerBlock,
  getAnalyzerResults, getAnalyzerAdvancedResults, getAnalyzerAdvancedProgress,
  getAnalyzerBatches, deleteAnalyzerResult, deleteAnalyzerBatch, deleteAnalyzerBulk, deleteAnalyzerFiltered,
  saveAnalyzerResult, unsaveAnalyzerResult, getStarredIds,
  getAnalyzerResult, getAnalyzerResultPeriods,
  getAnalyzerSearches, saveAnalyzerSearch, deleteAnalyzerSearch, updateAnalyzerSearchComment,
  getAnalyzerProgress, recalculateAnalyzerResult, recalculateAnalyzerBatch, getRecalculateBatchProgress,
  recalculateAnalyzerAll, cancelRecalculateAll, getRecalculateProgress,
  getUserSettings, saveUserSettings, getTickersList,
  getAnalyzerRegimes, recalculateRegimes,
  getDataCatalog, getDataSectors,
  addStrategyToBot, getRunningNames,
  cancelAnalyzerBatch,
  getTradingTargets, pushResultToTrading, LS_PUSH_TARGET,
  getUniverseCount, getTickerLists, getDelistingCoverage,
  generateAnalyzerPeriods, recheckSelectedRows, getParkSpeed,
} from '../api';
import BacktestDetail from './BacktestDetail';
import TickerDetail   from './TickerDetail';
import { useAnalyzerFilters } from './analyzer/useAnalyzerFilters';
import FilterBar    from './analyzer/FilterBar';
import FilterPanel  from './analyzer/FilterPanel';
import ResultsBar   from './analyzer/ResultsBar';
import ResultsTable from './analyzer/ResultsTable';
import Pagination   from './analyzer/Pagination';
import PeriodsPopup from './analyzer/PeriodsPopup';
import { DEFAULT_COLUMNS, DEFAULT_PAGE_SIZE } from './analyzer/shared';
import StablePresetButton from './analyzer/StablePresetButton';
import { MC_PRESETS, canonCapBuckets } from './analyzer/capBuckets';
import { usePersistentPageSize } from '../hooks/usePersistentPageSize';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { buildTickerTradeContext } from '../lib/tradeContext';

// id плашки сітки, відкритої в таблиці результатів (переживає reload).
const LS_ACTIVE_BATCH_TAG = 'analyzer_active_batch_tag';

// ── Light theme tokens ─────────────────────────────────────────────────────────
const C = {
  pageBg:    '#F1F5F9',
  cardBg:    '#FFFFFF',
  border:    '#E2E8F0',
  borderMd:  '#CBD5E1',
  text:      '#0F172A',
  textMd:    '#475569',
  textSm:    '#94A3B8',
  primary:   '#2563EB',
  primaryLt: '#EFF6FF',
  success:   '#16A34A',
  danger:    '#DC2626',
  warning:   '#D97706',
  shadow:    '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd:  '0 4px 12px rgba(0,0,0,0.10)',
};

const S = {
  page:    { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text },
  wrap:    { maxWidth: 1500, margin: '0 auto', padding: '24px 28px' },
  card:    { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow },
  formCard:  { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: C.shadow },
  formTitle: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 16 },
  grid2:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' },
  grid3:     { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 20px' },
  grid4:     { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px 20px' },
  label:     { display: 'block', fontSize: 12, fontWeight: 500, color: C.textMd, marginBottom: 5 },
  input:     {
    width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, boxSizing: 'border-box', outline: 'none',
  },
  select:    {
    width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, boxSizing: 'border-box', cursor: 'pointer',
  },
  checkRow:  { display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 4 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, color: C.text },
  btnRow:    { display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' },
  btnPrimary:{ padding: '9px 22px', background: C.primary, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  btnOutline:{ padding: '9px 18px', background: 'transparent', color: C.primary, border: `1px solid ${C.primary}`, borderRadius: 7, fontWeight: 500, fontSize: 13, cursor: 'pointer' },
  btnSm:     { padding: '5px 10px', background: 'transparent', color: C.primary, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmDanger:{ padding: '5px 10px', background: 'transparent', color: C.danger, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  tableWrap: { overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' },
  table:     { borderCollapse: 'collapse', minWidth: '100%', fontSize: 12 },
  th: {
    padding: '9px 10px', background: '#F8FAFC', border: `1px solid ${C.border}`,
    color: C.textMd, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2, cursor: 'pointer', userSelect: 'none',
  },
  thFixed: {
    padding: '9px 10px', background: '#F1F5F9', border: `1px solid ${C.border}`,
    color: C.textMd, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: { padding: '8px 10px', border: `1px solid ${C.border}`, color: C.text, whiteSpace: 'nowrap', verticalAlign: 'middle' },
  tdFixed: { padding: '8px 10px', border: `1px solid ${C.border}`, color: C.text, whiteSpace: 'nowrap', verticalAlign: 'middle' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: color === 'green' ? '#DCFCE7' : color === 'red' ? '#FEE2E2' : '#F1F5F9',
    color: color === 'green' ? C.success : color === 'red' ? C.danger : C.textMd,
  }),
  pagRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: `1px solid ${C.border}` },
  saveRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  savedTag:{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: C.primaryLt, color: C.primary, borderRadius: 99, fontSize: 12, cursor: 'pointer', border: `1px solid #BFDBFE` },
};

// ── Strategy presets ───────────────────────────────────────────────────────────
const STRATEGIES = [
  {
    id: 'mr_deep', emoji: '⭐', name: 'MR-Deep',
    win: '84.2%', monthly: '+0.49%', sharpe: '0.586', dd: '6.9%',
    defaults: { xBuy: 0.5, tHoursBuy: 24, ySell: 1.0, numCompanies: 10, maxPos: 10 },
  },
  {
    id: 'momentum_hold', emoji: '📈', name: 'MomentumHold',
    win: '62.1%', monthly: '+1.66%', sharpe: '1.041', dd: '9.8%',
    defaults: { xBuy: 0.5, tHoursBuy: 120, ySell: 2.0, numCompanies: 10, maxPos: 10 },
  },
  {
    id: 'base_extended', emoji: '🏆', name: 'Base-Extended',
    win: '56.0%', monthly: '+1.45%', sharpe: '1.538', dd: '5.76%',
    defaults: { xBuy: 3.0, tHoursBuy: 168, ySell: 10.0, numCompanies: 20, maxPos: 20 },
  },
  {
    id: 'momentum_adv', emoji: '🔬', name: 'MomentumAdv',
    win: '59.6%', monthly: '+0.78%', sharpe: '0.510', dd: '12.0%',
    defaults: { xBuy: 0.5, tHoursBuy: 120, ySell: 2.0, numCompanies: 10, maxPos: 10 },
  },
  {
    id: 'parallel', emoji: '⚡', name: 'Паралельний',
    win: '62–84%', monthly: '+4.89%', sharpe: '2.40', dd: '~8%',
    defaults: { xBuy: 0.5, tHoursBuy: 24, ySell: 1.0, numCompanies: 20, maxPos: 20 },
  },
  {
    id: 'hourly', emoji: '⏱️', name: 'Hourly',
    win: '~41%', monthly: '+2.69%', sharpe: '?', dd: '?',
    defaults: { xBuy: 1.0, tHoursBuy: 3, ySell: 1.0, numCompanies: 20, maxPos: 20 },
  },
];

const DEFAULT_SECTORS = [
  'Basic Materials', 'Communication Services', 'Consumer Cyclical',
  'Consumer Defensive', 'Energy', 'Financial Services', 'Healthcare',
  'Industrials', 'Real Estate', 'Technology', 'Utilities',
];
const DEFAULT_SELL_WINDOW_RANGE = { from: 1, to: 7, step: 2 };
// PAGE_SIZE_OPTS and DEFAULT_PAGE_SIZE imported from analyzer/shared.js

const DAILY_PERIOD_OPTS  = [1, 2, 3, 5, 7, 10, 14, 21, 30];
const HOURLY_PERIOD_OPTS = [1, 2, 3, 4, 6, 8, 12, 24, 48, 72];
// For minute data t_hours is still in hours (backend converts to bars)
const MINUTE_5M_PERIOD_OPTS  = [0.083, 0.25, 0.5, 1, 2, 4, 8, 24];  // 5m,15m,30m,1h,2h,4h,8h,24h
const MINUTE_1M_PERIOD_OPTS  = [0.017, 0.083, 0.25, 0.5, 1];         // 1m,5m,15m,30m,1h

function fmtPeriodLabel(v, dataType) {
  if (dataType === '5m' || dataType === '1m') {
    if (v < 1) {
      const mins = Math.round(v * 60);
      return `${mins}m`;
    }
    return `${v}h`;
  }
  return String(v);
}

// Converts raw sell_window_days value to human string.
// For hourly: val is in hours → 24h→"1д", 12h→"12г"
// For daily: val is in days  → "Nд"
function formatSellWindow(val, dataType) {
  if (!val && val !== 0) return '—';
  if (dataType === 'hourly' || dataType === '5m' || dataType === '1m') {
    const h = Number(val);
    if (h >= 24) return `${Math.round(h / 24)}д`;
    return `${h}г`;
  }
  return `${val}д`;
}


// ── Defaults ──────────────────────────────────────────────────────────────────
// Index universes. SP*/NDX come from the membership snapshot (data/sp_membership.csv);
// NASDAQ (Composite) is derived from the exchange column — see _index_members in
// web/backend/routers/backtesting.py.
const SP_INDICES = [
  { key: 'SP500',  label: 'S&P 500' },
  { key: 'SP400',  label: 'S&P 400' },
  { key: 'SP600',  label: 'S&P 600' },
  { key: 'NDX',    label: 'NASDAQ 100' },
  { key: 'NASDAQ', label: 'NASDAQ Composite' },
];
const SP1500_KEYS = ['SP500', 'SP400', 'SP600'];

const DEFAULT_FORM = {
  startTime: '2025-10-01', stopTime: '2026-02-28',
  // marginableOnly прибрано з форми 26.08: файл marginability.csv не існує ні на
  // несті, ні на проді, тож галочка нічого не фільтрувала. Бекенд-параметр
  // marginable_only лишається — ним користується бот.
  country: ['USA'], sector: ['Technology'], index: [],
  marketCapMin: 10000000, marketCapMax: 10000000000000,
  // Multi-select cap presets. [] + capCustom=false = "All" (no cap filter).
  // Several may be active at once — the universe keeps a ticker matching ANY of them.
  marketCapBuckets: [], capCustom: false,
  betaMin: '', betaMax: '',
  minAvgVolume: '',
  initialCapital: 100000,
};

const DEFAULT_ADDITIONAL_PERIODS = [];

// Правило відсіву за періодами — людською мовою, з поточними числами.
// 🚨 Назва галочки «Тільки прибуткові в усіх періодах» лишилась з часів, коли
// полів порогу й допуску не було: при −20% і 10% вона казала неправду, а
// фактичне правило було «не більше 10 збиткових із 101, жоден гірший за −20%».
// Кількість пробачених рахується так само, як у ядрі (cull_allowed_losses):
// від УСІХ періодів разом із головним вікном, з округленням ВНИЗ.
export function describeCullRule(floorStr, tolStr, nAdditional) {
  const floor = floorStr !== '' && !Number.isNaN(parseFloat(floorStr)) ? parseFloat(floorStr) : 0;
  const tol = tolStr !== '' && !Number.isNaN(parseFloat(tolStr)) ? parseFloat(tolStr) : 0;
  const total = (Number(nAdditional) || 0) + 1;
  const allowed = tol > 0 ? Math.floor((total * tol) / 100) : 0;
  const losses = allowed === 0
    ? `жодного збиткового з ${total}`
    : `не більше ${allowed} збиткових з ${total}`;
  const depth = floor < 0 ? `, жоден гірший за ${floor}%` : '';
  return `${losses}${depth}`;
}

function capBucketsPayload(form) {
  const b = canonCapBuckets(form.marketCapBuckets);
  return form.capCustom ? [...b, 'custom'] : b;
}

// Short label for the results table / saved-row meta.
function capTypeLabel(form) {
  const b = canonCapBuckets(form.marketCapBuckets);
  if (form.capCustom && !b.length) return 'custom';
  if (!b.length) return 'All';
  return form.capCustom ? [...b, 'custom'].join('+') : b.join('+');
}

// Пріоритет черги купівлі (менеджер 2026-08-19). Ключі мусять збігатися з
// _BUY_PRIORITY_CODES у web/backend/routers/backtesting.py.
// Кілька значень = вісь перебору: кожне множить кількість комбінацій.
const BUY_PRIORITIES = [
  { key: 'dip',      label: 'Глибина падіння',
    tip: 'Як було завжди: першими купуються папери, що найглибше просіли під власною середньою. Якщо фільтр падіння вимкнено — першими йдуть ті, що найсильніше зросли.' },
  { key: 'turnover', label: 'Більший обіг',
    tip: 'Першими купуються найбільш торговані папери (медіанний денний обіг у доларах). Увага: обіг майже не міняється в часі, тож черга перетворюється на майже сталий список найбільших компаній.' },
  { key: 'spike',    label: 'Сплеск обігу',
    tip: 'Першими купуються папери з незвично високою активністю: обіг попереднього дня, поділений на медіанний. Саме це відповідає ідеї «сплеск інтересу — більший шанс росту»; обіг береться за попередній день, поточний ще формується.' },
];

// What the backend receives: picked presets + the "custom" sentinel for the From/To pair.
// Empty ⇒ backend falls back to the single marketCapMin..Max range (= no filter for All).
// 🚨 Назви кошиків зводяться до канонічних ОДИН раз, тут.
//
// Бек зберігає їх У ВЕРХНЬОМУ регістрі (`_build_cap_bucket_sets` робить .upper()),
// а кнопки у формі підписані 'Mega'/'Large'/'Mid'/'Small'. Тож рядок, скопійований
// у форму, приносив 'MEGA', кнопка 'Mega' його НЕ впізнавала (порівняння було
// чутливе до регістру), не світилась активною — і клік ДОПИСУВАВ другий запис
// замість того, щоб зняти перший. Звідси «MEGA + LARGE + MID + SMALL + Mega +
// Large + Mid» у параметрах стратегії.
//
// На обрахунок це не впливало: `_cap_ranges` у рушії зводить список у множину з
// .upper(), тож дублікати давали ті самі діапазони. Ламався лише вигляд і
// поведінка кнопок.
// Підказка до лічильника тікерів: з чого складається число (див. universe-count).
function universeCountTitle(info) {
  if (!info) return 'Скільки тікерів піде в розрахунок';
  if (!info.exact) {
    return `Кандидатів за фільтрами: ${info.list_count}.\n`
      + 'Перевірку покриття дат для цього типу даних лічильник не робить — у розрахунку може бути менше.';
  }
  const fmt = (d) => (d || '').split('-').reverse().join('.');
  const withDead = info.delisted_count != null;
  const lines = [`У розрахунку: ${info.count} тікерів на період ${fmt(info.start)} – ${fmt(info.end)}.`];
  // Зниклі — ЧАСТИНА числа вище, а не окрема група: пишемо розклад, щоб ніхто
  // не складав їх докупи (так і сталося 28.09: 6737 + 2000 ≠ 9367).
  if (withDead) {
    lines.push(`  • торгуються до кінця періоду: ${info.count - info.delisted_count}`);
    lines.push(`  • зникли з біржі протягом періоду: ${info.delisted_count}`);
  }
  const dropped = info.list_count - info.count;
  lines.push(`Кандидатів за фільтрами: ${info.list_count}`
    + (withDead ? ' (разом з усіма компаніями, що зникли з біржі з 2016 року).' : '.'));
  if (dropped > 0) {
    lines.push(`Не входять у розрахунок: ${dropped} — не торгувались у цьому періоді `
      + '(зникли з біржі раніше, вийшли на біржу пізніше, дані обриваються) або не проходять фільтри в ньому.');
  }
  return lines.join('\n');
}

const DEFAULT_FIXED = { xBuy: 0.5, tHoursBuy: 24, ySell: 1.0, posSize: 5 };

const DEFAULT_RANGE = {
  xBuy:     { from: 0.5, to: 3.0, step: 0.5 },
  tHoursBuy:{ from: 1,   to: 72,  step: 6   },
  ySell:    { from: 0.5, to: 3.0, step: 0.5 },
  posSize:  { from: 2,   to: 10,  step: 2   },
};

// DEFAULT_COLUMNS imported from analyzer/shared.js

// FILTER_TYPES moved to analyzer/shared.js

// Helpers moved to analyzer/shared.js (fmtNum, fmtUsd, fmtPct, renderRegime, renderTickers, etc.)

function countRange(mode, from, to, step) {
  if (mode === 'fixed' || mode === 'same') return 1;
  if (!step || step <= 0 || from == null || to == null) return 1;
  // Add 1e-9 epsilon to match backend's `while v <= to + 1e-9` logic and
  // avoid off-by-one from floating-point division (e.g. 11/0.1 = 109.999...)
  return Math.max(1, Math.floor((to - from) / step + 1e-9) + 1);
}

// True if a range or fixed value contains `target` (within step alignment).
// Used for advanced mechanisms (rotation/opportunity/overpriced) where the
// backend always emits one OFF representative; if the user's range already
// contains the OFF default, no extra config is added.
function rangeContains(mode, fixed, range, target, tol = 1e-6) {
  if (mode === 'fixed') {
    const v = parseFloat(fixed);
    return Number.isFinite(v) && Math.abs(v - target) < tol;
  }
  const f = parseFloat(range?.from);
  const t = parseFloat(range?.to);
  const s = parseFloat(range?.step);
  if (!Number.isFinite(f) || !Number.isFinite(t)) return false;
  if (!Number.isFinite(s) || s <= 0) return Math.abs(f - target) < tol;
  if (target < f - tol || target > t + tol) return false;
  const k = (target - f) / s;
  return Math.abs(k - Math.round(k)) < tol;
}

// Count for two-flag ON/OFF pattern (buy_cap, htc). Each flag independently
// contributes its dimension; overlap (ON range already contains OFF default) is
// subtracted once to avoid double-counting.
function dimFromTwoFlags(includeOn, includeOff, mode, fixed, range, defOff = 0) {
  const onCount  = includeOn  ? countRange(mode, range?.from, range?.to, range?.step) : 0;
  const offCount = includeOff ? 1 : 0;
  const overlap  = (includeOn && includeOff && rangeContains(mode, fixed, range, defOff)) ? 1 : 0;
  const total = onCount + offCount - overlap;
  return Math.max(1, total); // 0 → 1 (safety: backend always emits fallback OFF)
}

// ── Tooltip component ─────────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 4 }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 14, height: 14, borderRadius: '50%', fontSize: 10, fontWeight: 700,
          background: '#CBD5E1', color: '#475569', cursor: 'help', lineHeight: 1,
        }}
      >?</span>
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6, zIndex: 9999, minWidth: 220, maxWidth: 320,
          background: '#1E293B', color: '#F1F5F9', fontSize: 11, lineHeight: 1.5,
          padding: '8px 10px', borderRadius: 7, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          whiteSpace: 'normal', pointerEvents: 'none',
        }}>
          {text}
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderWidth: 5, borderStyle: 'solid', borderColor: '#1E293B transparent transparent transparent' }} />
        </div>
      )}
    </span>
  );
}

// ── RangeField component ──────────────────────────────────────────────────────
function RangeField({ label, tooltip, paramKey, mode, fixed, range, onModeToggle, onFixed, onRange, step = '0.1', integer = false }) {
  const isRange = mode === 'range';
  const parse   = v => integer ? parseInt(v) : Number(v);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>{label}{tooltip && <Tooltip text={tooltip} />}</label>
        <button
          onClick={onModeToggle}
          style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none',
            background: isRange ? '#DBEAFE' : '#F1F5F9',
            color: isRange ? C.primary : C.textMd, fontWeight: 600,
          }}>
          {isRange ? 'Діапазон' : 'Фіксоване'}
        </button>
      </div>
      {!isRange ? (
        <input type="number" step={step} style={S.input} value={fixed}
          onChange={e => onFixed(parse(e.target.value))} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
            <input type="number" step={step} style={S.input} value={range.from}
              onChange={e => onRange({ ...range, from: parse(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
            <input type="number" step={step} style={S.input} value={range.to}
              onChange={e => onRange({ ...range, to: parse(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
            <input type="number" step={step} style={S.input} value={range.step}
              onChange={e => onRange({ ...range, step: parse(e.target.value) })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── PeriodField component — dropdown-based period selector ────────────────────
function PeriodField({ dataType, mode, fixed, range, onModeToggle, onFixed, onRange }) {
  const isRange = mode === 'range';
  const opts = dataType === 'daily' ? DAILY_PERIOD_OPTS
             : dataType === '5m'   ? MINUTE_5M_PERIOD_OPTS
             : dataType === '1m'   ? MINUTE_1M_PERIOD_OPTS
             : HOURLY_PERIOD_OPTS;
  const unit = dataType === 'daily' ? 'днів' : 'год';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
          Вікно входу (T-Buy, {unit})
          <Tooltip text="Часовий проміжок для оцінки зростання ціни перед купівлею. Наприклад, 24 год = перевіряємо чи акція зросла на X% за останні 24 год. Менше значення — реагуємо на короткострокові рухи, більше — на тривалі тренди." />
        </label>
        <button
          onClick={onModeToggle}
          style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none',
            background: isRange ? '#DBEAFE' : '#F1F5F9',
            color: isRange ? C.primary : C.textMd, fontWeight: 600,
          }}>
          {isRange ? 'Діапазон' : 'Фіксоване'}
        </button>
      </div>
      {!isRange ? (
        <input
          type="number"
          min={1}
          max={720}
          step={1}
          style={S.input}
          value={fixed}
          onChange={e => onFixed(Number(e.target.value))}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
            <input type="number" min={1} max={720} step={1} style={S.input} value={range.from}
              onChange={e => onRange({ ...range, from: Number(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
            <input type="number" min={1} max={720} step={1} style={S.input} value={range.to}
              onChange={e => onRange({ ...range, to: Number(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
            <input type="number" step="0.083" min="0.017" style={S.input} value={range.step}
              onChange={e => onRange({ ...range, step: Number(e.target.value) })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── SellWindowField component — identical to PeriodField but for sell window ──
function SellWindowField({ dataType, mode, fixed, range, onModeToggle, onFixed, onRange }) {
  const isRange = mode === 'range';
  const isSame  = mode === 'same';
  const opts = dataType === 'daily' ? DAILY_PERIOD_OPTS
             : dataType === '5m'   ? MINUTE_5M_PERIOD_OPTS
             : dataType === '1m'   ? MINUTE_1M_PERIOD_OPTS
             : HOURLY_PERIOD_OPTS;
  const unit = dataType === 'daily' ? 'днів' : 'год';
  const modeLabel = isSame ? '= T-Buy' : isRange ? 'Діапазон' : 'Фіксоване';
  const modeBg    = isSame ? '#DCFCE7' : isRange ? '#DBEAFE' : '#F1F5F9';
  const modeColor = isSame ? '#15803D' : isRange ? C.primary : C.textMd;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
          Вікно виходу (T-Sell, {unit})
          <Tooltip text="Часовий проміжок для перевірки умов продажу (падіння або відсутність росту). «= T-Buy» — використовує те саме вікно, що й для входу (GPU-сумісно). Менше значення = частіший контроль і швидший вихід, більше = більший буфер часу до рішення про продаж." />
        </label>
        <button
          onClick={onModeToggle}
          style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none',
            background: modeBg, color: modeColor, fontWeight: 600,
          }}>
          {modeLabel}
        </button>
      </div>
      {isSame ? (
        <div style={{ ...S.input, color: C.textSm, fontSize: 12, display: 'flex', alignItems: 'center', pointerEvents: 'none', userSelect: 'none' }}>
          Те саме, що T-Buy (GPU)
        </div>
      ) : !isRange ? (
        <input
          type="number"
          min={1}
          max={720}
          step={1}
          style={S.input}
          value={fixed}
          onChange={e => onFixed(Number(e.target.value))}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
            <input type="number" min={1} max={720} step={1} style={S.input} value={range.from}
              onChange={e => onRange({ ...range, from: Number(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
            <input type="number" min={1} max={720} step={1} style={S.input} value={range.to}
              onChange={e => onRange({ ...range, to: Number(e.target.value) })} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
            <input type="number" step="0.083" min="0.017" style={S.input} value={range.step}
              onChange={e => onRange({ ...range, step: Number(e.target.value) })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── SortableTh component ─────────────────────────────────────────────────────
// ── Filter helpers ────────────────────────────────────────────────────────────
function matchDateFilter(dateStr, filter) {
  if (!dateStr) return false;
  const d = String(dateStr).slice(0, 10);
  if (filter.datePreset) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rowDate = new Date(d);
    switch (filter.datePreset) {
      case 'today':
        return rowDate.toDateString() === today.toDateString();
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return rowDate.toDateString() === yesterday.toDateString();
      }
      case 'last_week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return rowDate >= weekAgo && rowDate <= today;
      }
      default: return true;
    }
  }
  const from = filter.from ? String(filter.from).slice(0, 10) : null;
  const to   = filter.to   ? String(filter.to).slice(0, 10)   : null;
  if (from && d < from) return false;
  if (to   && d > to)   return false;
  return true;
}

function matchFilter(row, filter) {
  const p = row.params   || {};
  const m = row.metrics  || {};
  switch (filter.field) {
    case 'monthly_pct': {
      const val = m.monthly_return ?? row.monthly_return ?? 0;
      return val >= (filter.from ?? -Infinity) && val <= (filter.to ?? +Infinity);
    }
    case 'yearly_pct': {
      const val = m.yearly_return ?? row.yearly_return ?? 0;
      return val >= (filter.from ?? -Infinity) && val <= (filter.to ?? +Infinity);
    }
    case 'return_pct': {
      const val = m.total_return ?? row.total_return ?? 0;
      return val >= (filter.from ?? -Infinity) && val <= (filter.to ?? +Infinity);
    }
    case 'win_rate': {
      const val = (m.win_rate ?? row.win_rate ?? 0) * 100;
      return val >= (filter.from ?? -Infinity) && val <= (filter.to ?? +Infinity);
    }
    case 'result_date':
      return matchDateFilter(row.created_at, filter);
    case 'start_period':
      return matchDateFilter(p.start_time ?? row.start_time, filter);
    case 'end_period':
      return matchDateFilter(p.stop_time ?? row.stop_time, filter);
    case 'data_type':
      return !filter.value || (p.data_type ?? row.data_type) === filter.value;
    case 'id': {
      const input = (filter.value || '').trim();
      if (!input) return true;
      const rowId = parseInt(row.id);
      if (isNaN(rowId)) return false;
      const parts = input.split(',').map(p => p.trim());
      for (const part of parts) {
        if (part.includes('-')) {
          const [fromStr, toStr] = part.split('-');
          const from = parseInt(fromStr);
          const to   = parseInt(toStr);
          if (!isNaN(from) && !isNaN(to) && rowId >= from && rowId <= to) return true;
          continue;
        }
        const single = parseInt(part);
        if (!isNaN(single) && rowId === single) return true;
      }
      return false;
    }
    default:
      return true;
  }
}

function applyFilters(rows, filters) {
  if (!filters.length) return rows;
  return rows.filter(row => {
    let result = matchFilter(row, filters[0]);
    for (let i = 1; i < filters.length; i++) {
      const match = matchFilter(row, filters[i]);
      result = filters[i].operator === 'or' ? result || match : result && match;
    }
    return result;
  });
}

// formatFilterLabel moved to analyzer/shared.js
// FilterRow component moved to analyzer/FilterRow.js

// FilterRow and SortTh moved to analyzer/FilterRow.js and analyzer/ResultsTable.js

// ── Parallel defaults ─────────────────────────────────────────────────────────
const DEFAULT_PARALLEL = {
  split: 60,
  momentum: { window_h: 35, buy_pct: 0.5, sell_pct: 2.0, num_cos: [12], pos_size: 5.0 },
  mr:       { window_h: 7,  entry_drop_pct: 0.5, take_profit_pct: 1.0, stop_loss_pct: 15.0, num_cos: [8], pos_size: 5.0 },
};

// ── ParallelParams component ──────────────────────────────────────────────────
const NUM_CO_OPTS_PAR = [1, 2, 3, 5, 8, 10, 12, 15, 20];

function ParallelParams({ split, onSplitChange, momentum, onMomentum, mr, onMr, capital }) {
  const momCap = Math.round(capital * split / 100);
  const mrCap  = Math.round(capital * (100 - split) / 100);

  const fieldStyle = { width: '100%', padding: '7px 9px', border: '1px solid #E2E8F0', borderRadius: 7, fontSize: 13, color: '#0F172A', background: '#fff', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: '#475569', marginBottom: 5 };

  function MomField({ label, fkey, step = 0.1 }) {
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <input type="number" step={step} style={fieldStyle} value={momentum[fkey]}
          onChange={e => onMomentum({ ...momentum, [fkey]: Number(e.target.value) })} />
      </div>
    );
  }

  function MrField({ label, fkey, step = 0.1 }) {
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <input type="number" step={step} style={fieldStyle} value={mr[fkey]}
          onChange={e => onMr({ ...mr, [fkey]: Number(e.target.value) })} />
      </div>
    );
  }

  function toggleMomCo(v) {
    const cur = momentum.num_cos || [];
    onMomentum({ ...momentum, num_cos: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v].sort((a,b)=>a-b) });
  }

  function toggleMrCo(v) {
    const cur = mr.num_cos || [];
    onMr({ ...mr, num_cos: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v].sort((a,b)=>a-b) });
  }

  return (
    <div>
      {/* Capital split slider */}
      <div style={{ marginBottom: 18, padding: '14px 18px', background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>Розподіл капіталу</span>
          <span style={{ fontSize: 12, color: '#475569' }}>
            MomentumHold: <b style={{ color: '#2563EB' }}>${momCap.toLocaleString()}</b> ({split}%)
            &nbsp;|&nbsp;
            MR-Deep: <b style={{ color: '#16A34A' }}>${mrCap.toLocaleString()}</b> ({100 - split}%)
          </span>
        </div>
        <input type="range" min={10} max={90} step={5} value={split}
          onChange={e => onSplitChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: '#2563EB' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
          <span>10% MomentumHold</span><span>50/50</span><span>90% MomentumHold</span>
        </div>
      </div>

      {/* Two blocks side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* MomentumHold block */}
        <div style={{ border: '2px solid #BFDBFE', borderRadius: 10, padding: '14px 16px', background: '#EFF6FF' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2563EB', marginBottom: 12 }}>
            📈 MomentumHold — {split}% капіталу (${momCap.toLocaleString()})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
            <MomField label="Вікно спостереження (год)" fkey="window_h" step={1} />
            <MomField label="Поріг зростання (BUY %)" fkey="buy_pct" />
            <MomField label="Поріг падіння (SELL %)" fkey="sell_pct" />
            <MomField label="Розмір позиції (% капіталу)" fkey="pos_size" />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Кількість компаній (макс. позицій)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 10px', marginTop: 4 }}>
              {NUM_CO_OPTS_PAR.map(v => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 13, color: '#0F172A' }}>
                  <input type="checkbox" checked={(momentum.num_cos || []).includes(v)} onChange={() => toggleMomCo(v)} />
                  {v}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* MR-Deep block */}
        <div style={{ border: '2px solid #BBF7D0', borderRadius: 10, padding: '14px 16px', background: '#F0FDF4' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#16A34A', marginBottom: 12 }}>
            ⭐ MR-Deep — {100 - split}% капіталу (${mrCap.toLocaleString()})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
            <MrField label="Вікно спостереження (год)" fkey="window_h" step={1} />
            <MrField label="Поріг падіння для входу (%)" fkey="entry_drop_pct" />
            <MrField label="Фіксація прибутку (%)" fkey="take_profit_pct" />
            <MrField label="Стоп-лос (%)" fkey="stop_loss_pct" />
            <MrField label="Розмір позиції (% капіталу)" fkey="pos_size" />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Кількість компаній (макс. позицій)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 10px', marginTop: 4 }}>
              {NUM_CO_OPTS_PAR.map(v => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 13, color: '#0F172A' }}>
                  <input type="checkbox" checked={(mr.num_cos || []).includes(v)} onChange={() => toggleMrCo(v)} />
                  {v}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AccordionBlock & SubBlock — used by Advanced mode ────────────────────────
function AccordionBlock({ title, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '11px 16px', background: C.primaryLt, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{title}</span>
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

// Вісь перебору для ОДНОГО скалярного поля (плече, поріг обігу). Вимкнена —
// у тіло запиту не їде нічого, і сітка не множиться: тіло запиту виглядає рівно
// так, як до появи цих полів. Увімкнена — фіксоване значення поля вище
// ігнорується, замість нього перебирається діапазон.
function AxisRangeRow({ label, range, setRange, step, count, note }) {
  const set = (k) => (e) => setRange(prev => ({
    ...prev, [k]: e.target.value === '' ? '' : Number(e.target.value),
  }));
  return (
    <div style={{
      border: `1px dashed ${range.enabled ? C.primary : C.border}`, borderRadius: 6,
      padding: '8px 10px', marginBottom: 8, background: range.enabled ? C.primaryLt : 'transparent',
    }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: C.textMd }}>
        <input
          type="checkbox" checked={!!range.enabled}
          onChange={e => setRange(prev => ({ ...prev, enabled: e.target.checked }))}
        />
        {label}
        <Tooltip text={note} />
      </label>
      {range.enabled && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {[['from', 'від'], ['to', 'до'], ['step', 'крок']].map(([k, cap]) => (
            <label key={k} style={{ fontSize: 11, color: C.textMd }}>
              {cap}<br />
              <input
                type="number" step={step} value={range[k]} onChange={set(k)}
                style={{ width: 110, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, marginTop: 2 }}
              />
            </label>
          ))}
          <span style={{ fontSize: 11, color: count > 1 ? C.primary : C.textMd, paddingBottom: 6, fontWeight: count > 1 ? 600 : 400 }}>
            {count} знач. → сітка ×{count}
          </span>
        </div>
      )}
    </div>
  );
}

function SubBlock({ label, enabled, onToggle, hint, warning, children }) {
  return (
    <div style={{ border: `1px solid ${enabled ? C.primary : C.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: enabled ? '#FAFBFF' : C.cardBg }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
        <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)}
          style={{ accentColor: C.primary, width: 14, height: 14 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: enabled ? C.primary : C.text }}>{label}</span>
      </label>
      {hint && (
        <div style={{ fontSize: 12, color: C.textMd, marginBottom: 12, padding: '7px 10px', background: '#F0F9FF', borderRadius: 6, borderLeft: `3px solid ${C.primary}` }}>
          {hint}
        </div>
      )}
      {warning && (
        <div style={{ fontSize: 12, color: C.warning, marginBottom: 12, padding: '7px 10px', background: '#FFFBEB', borderRadius: 6, borderLeft: `3px solid ${C.warning}` }}>
          {warning}
        </div>
      )}
      <div style={{ opacity: enabled ? 1 : 0.45, pointerEvents: enabled ? 'auto' : 'none' }}>
        {children}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * «±» біля числового поля, де число буває від'ємним.
 *
 * Навіщо: на планшеті поле type="number" відкриває цифрову клавіатуру, у якій
 * немає знака «−» (повідомлено 25.09: «Відкидати, якщо період гірший за −30»
 * неможливо ввести). Кнопка міняє знак уже введеного числа.
 */
function SignToggle({ value, onChange, disabled }) {
  const flip = () => {
    const v = String(value ?? '').trim();
    if (v === '' || Number(v) === 0) return;
    onChange(v.startsWith('-') ? v.slice(1) : `-${v}`);
  };
  return (
    <button type="button" onClick={flip} disabled={disabled}
            title="Змінити знак (на планшеті в цифровій клавіатурі немає «−»)"
            style={{ fontSize: 12, lineHeight: 1, padding: '3px 7px', borderRadius: 5,
                     border: '1px solid #D1D5DB', background: '#F8FAFC', color: '#374151',
                     cursor: disabled ? 'not-allowed' : 'pointer' }}>
      ±
    </button>
  );
}


// Поле панелі смарт-пошуку — той самий вигляд, що в двоетапного пошуку.
const SMART_INPUT = { width: 72, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text };

export default function StrategyAnalyzer({ initialResultId, initialTicker, navigateTo: navTo }) {
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [dataType, setDataType]     = useState('hourly');
  const [selectedStrategy, setSelectedStrategy] = useState('mr_deep');
  const [engine, setEngine]                     = useState('numba');
  const [dataCatalog, setDataCatalog] = useState(null);
  const [paramMode, setParamMode]   = useState({ xBuy: 'fixed', tHoursBuy: 'fixed', ySell: 'fixed', posSize: 'fixed' });
  const [fixed, setFixed]           = useState(DEFAULT_FIXED);
  const [rangeP, setRangeP]         = useState(DEFAULT_RANGE);
  const [parallelSplit, setParallelSplit] = useState(DEFAULT_PARALLEL.split);
  const [momentumParams, setMomentumParams] = useState(DEFAULT_PARALLEL.momentum);
  const [mrParams, setMrParams]     = useState(DEFAULT_PARALLEL.mr);
  const [numCompanies, setNumCompanies] = useState(20);
  const [nCompaniesMode, setNCompaniesMode] = useState('fixed');
  const [nCompaniesRange, setNCompaniesRange] = useState({ min: 5, max: 20, step: 5 });
  const [sellWindowDays, setSellWindowDays] = useState(3);
  const [sellWindowMode, setSellWindowMode] = useState('same');
  const [sellWindowRange, setSellWindowRange] = useState(DEFAULT_SELL_WINDOW_RANGE);
  const [pageSize, setPageSize]     = usePersistentPageSize('pg_analyzer', DEFAULT_PAGE_SIZE);
  const [searchName, setSearchName] = useState('');
  // Коментар до сітки: пишеться у плашку «📊 Створено …» при запуску,
  // далі правиться кнопкою «i» на плашці.
  const [runComment, setRunComment] = useState('');
  const [commentEdit, setCommentEdit] = useState(null); // { id, name, text, saving }
  const [results, setResults]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [page, setPage]             = useState(0);
  const [loading, setLoading]       = useState(false);
  const [runningBatch, setRunningBatch] = useState(null);  // {batch_id, count}
  // Виміряна швидкість парку на останній сітці — для оцінки часу під формою.
  // Ендпоінт адмінський: для інших користувачів лишається стара оцінка.
  const [parkSpeed, setParkSpeed] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = () => getParkSpeed()
      .then(r => { if (alive) setParkSpeed(r.data?.speed || null); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const [runningBotNames, setRunningBotNames] = useState([]);  // names of mock-running strategies
  const [runMockStatus, setRunMockStatus] = useState(null);    // null | 'starting' | 'done' | 'error'
  const [selectedId, setSelectedId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [tickerView, setTickerView] = useState(null);  // {ticker, buyTrade, sellTrade, fromDate, toDate, dataType}
  const [savedSearches, setSavedSearches] = useState([]);
  const [sortCol, setSortCol]       = useState('monthly_return');
  const [sortDir, setSortDir]       = useState(-1);  // -1 = desc, +1 = asc
  const [collapsedBatches, setCollapsedBatches] = useLocalStorage('analyzer_collapsedBatches', {});
  const [riskProt, setRiskProt]     = useState({
    enabled: false,
    sell_overnight: true,
    sell_weekend: true,
    max_loss_to_sell_pct: 0.5,
  });
  const [sellMode, setSellMode]     = useState('rolling');
  const [stagnation, setStagnation] = useState({ enabled: false, min_pct: { mode: 'single', value: -0.5, min: -2, max: -0.5, step: 0.5 }, max_pct: { mode: 'single', value: 0.5, min: 0.5, max: 2, step: 0.5 }, bars: { mode: 'single', value: 30, min: 20, max: 60, step: 10 } });
  const [intraday, setIntraday]     = useState({ enabled: false, pct: 3.0 });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [reinvest, setReinvest]         = useState(true);  // true (Cash) | false (Fixed) | 'protected'
  const [hardStopPct, setHardStopPct]               = useState(0);
  const [hardStopCooldownBars, setHardStopCooldownBars] = useState(0);
  const [usePeakWindowBuy, setUsePeakWindowBuy]     = useState(false);
  const [usePeakWindowSell, setUsePeakWindowSell]   = useState(false);
  // Lazy-init from localStorage so periods survive an immediate refresh (the
  // backend autosave is debounced 1500ms → a fast reload would otherwise lose
  // freshly-added periods). Backend settings override this on mount when present.
  const [additionalPeriods, setAdditionalPeriods] = useState(() => {
    try {
      const raw = localStorage.getItem('analyzer_additional_periods');
      const arr = raw ? JSON.parse(raw) : null;
      return Array.isArray(arr) ? arr : DEFAULT_ADDITIONAL_PERIODS;
    } catch (_) { return DEFAULT_ADDITIONAL_PERIODS; }
  });
  // Mirror to localStorage instantly on every change (no debounce).
  useEffect(() => {
    try { localStorage.setItem('analyzer_additional_periods', JSON.stringify(additionalPeriods)); }
    catch (_) {}
  }, [additionalPeriods]);

  // Named period-set presets (#3) — saved separately so long period lists can be
  // re-applied across searches. Persisted to backend (analyzer_ui) + localStorage.
  const [periodPresets, setPeriodPresets] = useState(() => {
    try {
      const raw = localStorage.getItem('analyzer_period_presets');
      const arr = raw ? JSON.parse(raw) : null;
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  });
  const [periodPresetName, setPeriodPresetName] = useState('');
  // Генератор перевірочних вікон. Дефолт — те, що менеджер вимірював 07.09:
  // 100 вікон по року з випадковою добавкою 1-100 днів. Добавка робить вікна
  // різної довжини, тож підсумок не тримається на одній конкретній тривалості.
  const [genCfg, setGenCfg] = useState(() => {
    try {
      const raw = localStorage.getItem('analyzer_period_gen_cfg');
      const o = raw ? JSON.parse(raw) : null;
      return o && typeof o === 'object' ? o
        : { months: 12, count: 100, extraEnabled: true, extraFrom: 1, extraTo: 100, mode: 'random' };
    } catch (_) { return { months: 12, count: 100, extraEnabled: true, extraFrom: 1, extraTo: 100, mode: 'random' }; }
  });
  useEffect(() => {
    try { localStorage.setItem('analyzer_period_gen_cfg', JSON.stringify(genCfg)); }
    catch (_) {}
  }, [genCfg]);
  const [genBusy,  setGenBusy]  = useState(false);
  const [genError, setGenError] = useState('');
  const [genNote,  setGenNote]  = useState('');
  // Які партії розгорнуті списком. Сто рядків з полями дат нечитабельні, тож
  // згенеровані вікна лежать згорнутими, а редагувати їх поштучно й не треба —
  // партія або приймається цілком, або прибирається цілком.
  const [genOpen, setGenOpen] = useState({});

  const generatePeriods = useCallback(async () => {
    setGenBusy(true); setGenError(''); setGenNote('');
    try {
      const res = await generateAnalyzerPeriods({
        start:              form.startTime,
        end:                form.stopTime,
        period_months:      Number(genCfg.months),
        n_periods:          Number(genCfg.count),
        extra_days_enabled: !!genCfg.extraEnabled,
        extra_days_from:    Number(genCfg.extraFrom) || 0,
        extra_days_to:      Number(genCfg.extraTo) || 0,
        mode:               genCfg.mode === 'sweep' ? 'sweep' : 'random',
        // Точки входу, уже зайняті набором. Без них кожне натискання починало
        // рахунок заново: виміряно на 1000 вікон — 647 різних стартів замість
        // 1000, тобто третина партій повторювала чужу точку входу.
        //
        // 🚨 ТІЛЬКИ ВІКНА ТІЄЇ САМОЇ ДОВЖИНИ. Річне й дворічне вікно з однієї
        // точки входу — це РІЗНІ вікна, і обидва законні. Без цієї умови другий
        // перебір (іншою довжиною) повертав би «вільних точок входу не
        // лишилось», тобто дедуп забороняв би саме те, заради чого набір і
        // збирають кількома партіями. Ручні вікна не рахуються: вони поставлені
        // свідомо й довжини в них немає.
        exclude_starts:     additionalPeriods
                              .filter(p => p && p.start_date && p.generated
                                        && String(p.months) === String(genCfg.months))
                              .map(p => p.start_date),
      });
      const d = res.data || {};
      // Партія ДОПИСУЄТЬСЯ, а не заміщає: набір із 300 вікон збирається трьома
      // натисканнями (рік, два, три) — саме той спосіб перевірки, який показав,
      // що на довших вікнах стратегія доходна майже скрізь.
      setAdditionalPeriods(prev => [...prev, ...(d.periods || [])]);
      const parts = [(d.mode === 'sweep' ? 'Перебрано всі точки входу: ' : '')
                     + `додано ${(d.periods || []).length} вікон × ${d.period_months} міс.`];
      if (d.clamped_end) parts.push(`праву межу обрізано даними до ${d.clamped_end}`);
      parts.push(`зерно ${d.seed}`);
      setGenNote(parts.join(' · '));
    } catch (e) {
      setGenError(e?.response?.data?.detail || e.message || 'Не вдалось згенерувати');
    } finally {
      setGenBusy(false);
    }
  }, [form.startTime, form.stopTime, genCfg, additionalPeriods]);

  // 🚨 ПЕРІОДИ ПОЗА ГОЛОВНИМ ВІКНОМ (спіймано 24.09). Генератор бере діапазон
  // головного вікна В МИТЬ натискання; змінили вікно потім — періоди лишились
  // старими, і ніхто нічого не сказав. Сітка з вікном 2025-10…2026-02 і 1074
  // вікнами 2021…2026 пішла на ОДИН координатор: знімок для парку збирається
  // лише під головне вікно. Тому запуск advanced-сітки тут блокується, а
  // виправлення — одна кнопка (сервер перевіряє те саме й відмовить теж).
  const periodsOutside = useMemo(() => {
    const s0 = (form.startTime || '').slice(0, 10);
    const s1 = (form.stopTime || '').slice(0, 10);
    const valid = additionalPeriods.filter(p => p && p.start_date && p.end_date);
    if (!s0 || !s1 || valid.length === 0) return { count: 0 };
    let count = 0, lo = s0, hi = s1;
    for (const p of valid) {
      const a = String(p.start_date).slice(0, 10), b = String(p.end_date).slice(0, 10);
      if (a < s0 || b > s1) count += 1;
      if (a < lo) lo = a;
      if (b > hi) hi = b;
    }
    return { count, total: valid.length, lo, hi };
  }, [additionalPeriods, form.startTime, form.stopTime]);
  useEffect(() => {
    try { localStorage.setItem('analyzer_period_presets', JSON.stringify(periodPresets)); }
    catch (_) {}
  }, [periodPresets]);

  function savePeriodPreset() {
    const name = periodPresetName.trim();
    if (!name) { alert('Введи назву набору періодів'); return; }
    const valid = additionalPeriods.filter(p => p.start_date && p.end_date);
    const hasMain = form.startTime && form.stopTime;
    if (valid.length === 0 && !hasMain) { alert('Немає заповнених періодів для збереження'); return; }
    const main = hasMain ? { start_date: form.startTime, end_date: form.stopTime } : undefined;
    setPeriodPresets(prev => {
      const without = prev.filter(x => x.name !== name);   // overwrite same-name
      return [...without, { name, main, periods: valid }];
    });
    setPeriodPresetName('');
  }
  function importPeriodPreset(name) {
    const preset = periodPresets.find(x => x.name === name);
    if (preset && Array.isArray(preset.periods)) {
      setAdditionalPeriods(preset.periods.map(p => ({ ...p })));
    }
    if (preset?.main?.start_date && preset?.main?.end_date) {
      setForm(f => ({ ...f, startTime: preset.main.start_date, stopTime: preset.main.end_date }));
    }
  }
  function deletePeriodPreset(name) {
    setPeriodPresets(prev => prev.filter(x => x.name !== name));
  }
  const [progress, setProgress]               = useState(null);  // {current, total, progress} or null
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [recalcProgress, setRecalcProgress]   = useState(null); // null | {current, total, progress, status}
  const [recalcConfirm, setRecalcConfirm]     = useState(false);
  const [recalcingId, setRecalcingId]         = useState(null); // single-row recalc in progress
  const [recalcSelectedProgress, setRecalcSelectedProgress] = useState(null); // null | {current, total}
  const [checkedIds, setCheckedIds]           = useState(new Set());
  const [starredIds, setStarredIds]           = useState(new Set());
  const [periodsPopupRow, setPeriodsPopupRow] = useState(null);
  // Вікна попапа вантажаться окремо: список їх не возить (55 КБ на рядок).
  // null = ще їдуть, [] = їх немає.
  const [periodsPopupData, setPeriodsPopupData] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [activeSearchId, setActiveSearchId]   = useState(null); // id of active saved filter tag
  const savedSearchesLoadedRef = useRef(false);
  const [quickIdSearch, setQuickIdSearch]         = useState('');
  const [settingsSavedAt, setSettingsSavedAt] = useState(null); // timestamp of last save
  const [deletingFiltered, setDeletingFiltered] = useState(false);
  const [batches, setBatches]                 = useState([]);     // [{batch_id, created_at, total, completed, ...}]
  const [batchFilter, setBatchFilter]         = useState('');     // '' = all batches
  const [showBatchPanel, setShowBatchPanel]   = useLocalStorage('analyzer_showBatchPanel', false);
  const [availableSectors, setAvailableSectors] = useState(DEFAULT_SECTORS);
  const [pushStatus, setPushStatus] = useState({});  // {[id]: 'loading'|'ok'|'error'}
  const [tradingTargets, setTradingTargets] = useState([]);      // [{label,url}] live bots
  const [pushTarget, setPushTarget] = useState(() => localStorage.getItem(LS_PUSH_TARGET) || '');
  // Live-bot push targets (admin) — fetch once, default the selection to the first.
  useEffect(() => {
    if (localStorage.getItem('role') !== 'admin') return;
    getTradingTargets()
      .then((res) => {
        const list = res.data?.targets || [];
        setTradingTargets(list);
        setPushTarget((prev) => (prev && list.some((t) => t.label === prev)) ? prev : (list[0]?.label || ''));
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (pushTarget) localStorage.setItem(LS_PUSH_TARGET, pushTarget);
  }, [pushTarget]);

  // Покриття делістингових — з сервера, не зашите у форму (див. Блок 8).
  const [delistCoverage, setDelistCoverage] = useState({});
  useEffect(() => {
    getDelistingCoverage()
      .then((res) => setDelistCoverage(res.data || {}))
      .catch(() => {});
  }, []);

  // ── Advanced strategy mode — автоматично визначається по увімкнутих механізмах ──
  // Block 1a: Buy Cap
  const [buyCapEnabled, setBuyCapEnabled]   = useState(false);
  const [buyCapOffEnabled, setBuyCapOffEnabled] = useState(true);
  const [buyCapMode,    setBuyCapMode]      = useState('fixed');
  const [buyCapFixed,   setBuyCapFixed]     = useState(10.0);
  const [buyCapRange,   setBuyCapRange]     = useState({ from: 5.0,  to: 20.0, step: 2.5 });
  // Block 1b: Overpriced
  const [overpricedEnabled, setOverpricedEnabled] = useState(false);
  const [ovpPctMode,  setOvpPctMode]  = useState('fixed');
  const [ovpPctFixed, setOvpPctFixed] = useState(5.0);
  const [ovpPctRange, setOvpPctRange] = useState({ from: 2.0,  to: 15.0, step: 2.0 });
  const [ovpPerMode,  setOvpPerMode]  = useState('fixed');
  const [ovpPerFixed, setOvpPerFixed] = useState(48);
  const [ovpPerRange, setOvpPerRange] = useState({ from: 24,   to: 120,  step: 24  });
  // Block 1c: Dip Filter
  const [dipEnabled, setDipEnabled]   = useState(false);
  const [dipPctMode,  setDipPctMode]  = useState('fixed');
  const [dipPctFixed, setDipPctFixed] = useState(5.0);
  const [dipPctRange, setDipPctRange] = useState({ from: 2.0,  to: 12.0, step: 2.0 });
  const [dipPerMode,  setDipPerMode]  = useState('fixed');
  const [dipPerFixed, setDipPerFixed] = useState(24);
  const [dipPerRange, setDipPerRange] = useState({ from: 12,   to: 72,   step: 12  });
  // Block 2a: Rotation
  const [rotEnabled, setRotEnabled]       = useState(false);
  const [rotHoldMode,    setRotHoldMode]   = useState('fixed');
  const [rotHoldFixed,   setRotHoldFixed]  = useState(3);
  const [rotHoldRange,   setRotHoldRange]  = useState({ from: 1,   to: 12,  step: 1   });
  const [rotFactorMode,  setRotFactorMode] = useState('fixed');
  const [rotFactorFixed, setRotFactorFixed]= useState(2.0);
  const [rotFactorRange, setRotFactorRange]= useState({ from: 1.5, to: 4.0, step: 0.5 });
  // Block 2b: Opportunity
  const [oppEnabled, setOppEnabled]       = useState(false);
  const [oppHoldMode,  setOppHoldMode]   = useState('fixed');
  const [oppHoldFixed, setOppHoldFixed]  = useState(1);
  const [oppHoldRange, setOppHoldRange]  = useState({ from: 1,   to: 6,   step: 1   });
  const [oppGainMode,  setOppGainMode]   = useState('fixed');
  const [oppGainFixed, setOppGainFixed]  = useState(2.0);
  const [oppGainRange, setOppGainRange]  = useState({ from: 0.5, to: 5.0, step: 0.5 });
  // Block 2c/2d: продаж під покупку (менеджер 15.10)
  const [swapDeferEnabled,    setSwapDeferEnabled]    = useState(false);
  const [swapDeferOffEnabled, setSwapDeferOffEnabled] = useState(true);
  const [swapTpEnabled,       setSwapTpEnabled]       = useState(false);
  const [swapTpOffEnabled,    setSwapTpOffEnabled]    = useState(true);
  const [swapTpMode,  setSwapTpMode]   = useState('fixed');
  const [swapTpFixed, setSwapTpFixed]  = useState(80);
  const [swapTpRange, setSwapTpRange]  = useState({ from: 60, to: 90, step: 10 });
  // Block 3: Hold to Cost під опціями (менеджер 15.10)
  const [swapHtcEnabled,    setSwapHtcEnabled]    = useState(false);
  const [swapHtcOffEnabled, setSwapHtcOffEnabled] = useState(true);
  // Buy% на виході — правило «не виріс» (менеджер 29.10). Два прапорці, як у HTC:
  // «вхід + утримання» (правило діє, як до 29.10) і «лише вхід» (Buy% тільки купує).
  // Дефолт форми — лише вхід: менеджер вирішив прибрати правило з нових пошуків.
  const [noGrowthOn,  setNoGrowthOn]  = useState(false);
  const [noGrowthOff, setNoGrowthOff] = useState(true);
  // Block 3: Hold to Cost
  const [htcEnabled, setHtcEnabled]       = useState(false);
  // Hold to Cost від беззбитковості (менеджер 02.11): поріг = ціна купівлі +
  // комісії купівлі й продажу + спред + націнка %. Дефолт форми — увімкнено, 0.1%.
  const [htcCostEnabled, setHtcCostEnabled] = useState(true);
  const [htcCostMarkup,  setHtcCostMarkup]  = useState(0.1);
  const [htcOffEnabled, setHtcOffEnabled] = useState(true);
  const [htcBarsMode,  setHtcBarsMode]   = useState('fixed');
  const [htcBarsFixed, setHtcBarsFixed]  = useState(48);
  const [htcBarsRange, setHtcBarsRange]  = useState({ from: 12,  to: 120, step: 12  });
  // Block 4: Take Profit
  const [tpImmediateEnabled, setTpImmediateEnabled] = useState(false);
  const [tpLetItRunEnabled,  setTpLetItRunEnabled]  = useState(false);
  const [tpPctMode,  setTpPctMode]        = useState('fixed');
  const [tpPctFixed, setTpPctFixed]       = useState(10.0);
  const [tpPctRange, setTpPctRange]       = useState({ from: 5.0, to: 30.0, step: 5.0 });
  const [tpExpiryMode,  setTpExpiryMode]  = useState('fixed');
  const [tpExpiryFixed, setTpExpiryFixed] = useState(0);
  const [tpExpiryRange, setTpExpiryRange] = useState({ from: 0, to: 2160, step: 720 });
  // Block 5: Маржинальне плече 2:1 (fixed-only скаляри; numba-only, GPU виключено)
  const [marginEnabled, setMarginEnabled] = useState(false);
  const [marginCfg, setMarginCfg] = useState({
    cap: 2.0, maintPct: 25, delevStartPct: 30, delevStopPct: 35, interestAprPct: 6, minPrice: 5,
  });
  // Плече і поріг обігу як ОСІ перебору (власник 28.08). Довго жили лише на
  // сторінці результату («Драбина умов»), бо разом множать сітку до ×480;
  // повертати їх у форму вирішено свідомо — до кластера.
  const [marginCapRange, setMarginCapRange] = useState({
    enabled: false, from: 1.1, to: 2.0, step: 0.1,
  });
  // Block 6: Реалізм ліквідності R2 (fixed-only скаляри; numba-only, GPU виключено)
  // Дефолт УВІМКНЕНО: нові пошуки мають враховувати обіг і ліміт заявки за замовчуванням.
  // Це дефолт ЛИШЕ форми — ядро, лендинг, бот і віртуальні торги прапорця не шлють.
  const [liqEnabled, setLiqEnabled] = useState(true);
  const [liqCfg, setLiqCfg] = useState({
    minAdvUsd: 1750000, maxFlatBarPct: 60, positionAdvCapPct: 1,
    advWindowDays: 20, impactBpsPerPct: 10,
  });
  const [minAdvRange, setMinAdvRange] = useState({
    enabled: false, from: 1000000, to: 10000000, step: 1000000,
  });
  const [impactEnabled, setImpactEnabled] = useState(false);
  // Block 7: Архівна капіталізація R4 (numba-only, GPU виключено).
  // Дефолт ВИМКНЕНО — на відміну від R2 цей шар ще не перевірений на всьому
  // корпусі збережених результатів; вмикається свідомо.
  const [pitEnabled, setPitEnabled] = useState(false);
  const [delistEnabled, setDelistEnabled] = useState(false);
  const [reorgEnabled, setReorgEnabled] = useState(false);
  const [perBarUniverse, setPerBarUniverse] = useState(false);
  const [sliceEnabled, setSliceEnabled] = useState(false);
  const [dynNEnabled, setDynNEnabled] = useState(false);
  const [partialSells, setPartialSells] = useState(false);
  const [dynNMode, setDynNMode] = useState(0);
  const [sliceCfg, setSliceCfg] = useState({ minPositionUsd: 350, minSharePrice: 1.75 });
  const [pitRequireData, setPitRequireData] = useState(true);
  // Дві осі менеджера 2026-08-19. Обидві порожні/одиничні за замовчуванням —
  // кількість комбінацій у наявних збережених формах не змінюється.
  const [buyPriorities, setBuyPriorities] = useState(['dip']);
  // ДОДАТКОВІ набори капіталізації (перший набір — той, що у блоці всесвіту).
  // Кожен елемент — масив назв кошиків: ['MEGA','LARGE','MID'].
  const [capBucketSets, setCapBucketSets] = useState([]);

  // strategyType: 'advanced' якщо будь-який механізм увімкнено, інакше 'base'
  const strategyType = useMemo(
    () => (buyCapEnabled || overpricedEnabled || dipEnabled || rotEnabled || oppEnabled || swapDeferEnabled || swapTpEnabled || htcEnabled || tpImmediateEnabled || tpLetItRunEnabled)
      ? 'advanced' : 'base',
    [buyCapEnabled, overpricedEnabled, dipEnabled, rotEnabled, oppEnabled, swapDeferEnabled, swapTpEnabled, htcEnabled, tpImmediateEnabled, tpLetItRunEnabled]
  );

  const recalcPollRef = useRef(null);
  const recalcSelectedPollRef = useRef(null);
  const pollRef = useRef(null);
  const strategyTypeRef = useRef('base'); // mirrors strategyType — оновлюється в useEffect нижче
  const sortRef = useRef({ col: 'monthly_return', dir: -1 });
  const settingsLoadedRef = useRef(false);   // true after backend settings applied
  const saveTimerRef      = useRef(null);    // debounce timer for saving

  // ── Filters hook (URL-synced) ─────────────────────────────────────────────
  const {
    filters,
    appliedFilters,
    filtersRef,
    hasPendingChanges,
    showFilterPanel,
    setShowFilterPanel,
    addFilter,
    removeFilter,
    updateFilter,
    toggleOperator: toggleOperatorFn,
    resetFilters,
    applyFilters: applyFiltersHook,
    setFiltersDirect,
  } = useAnalyzerFilters();

  // Wrap applyFilters to also clear the active saved-filter tag
  const applyFilters = useCallback(() => {
    setActiveSearchId(null);
    applyFiltersHook();
  }, [applyFiltersHook]);

  // ── Stable preset ─────────────────────────────────────────────────────────
  const [lastStablePreset, setLastStablePreset] = useState(null); // 'min'|'ideal'|'custom'|null
  const lastStableThresholdsRef = useRef(null);

  const applyStablePreset = useCallback((t, presetName) => {
    const now = Date.now();
    lastStableThresholdsRef.current = t;
    setFiltersDirect([
      { id: now+1, field: 'prof_months', operator: 'and', type: 'numrange', from: t.prof_months, to: null, datePreset: null, value: null },
      { id: now+2, field: 'worst_month', operator: 'and', type: 'numrange', from: t.worst_month, to: null, datePreset: null, value: null },
      { id: now+3, field: 'calmar',      operator: 'and', type: 'numrange', from: t.calmar,      to: null, datePreset: null, value: null },
      { id: now+4, field: 'sharpe',      operator: 'and', type: 'numrange', from: t.sharpe,      to: null, datePreset: null, value: null },
    ]);
    setActiveSearchId(null);
    setLastStablePreset(presetName);
  }, [setFiltersDirect]);

  const applyStableFallback = useCallback(() => {
    lastStableThresholdsRef.current = null;
    setSortCol('profitable_months_pct');
    setSortDir(-1);
    sortRef.current = { col: 'profitable_months_pct', dir: -1 };
    setFiltersDirect([]);
    setActiveSearchId(null);
    setLastStablePreset(null);
  }, [setFiltersDirect]);

  // ── Regime filter ────────────────────────────────────────────────────────
  const [regimeFilter, setRegimeFilter]   = useState([]);
  const [regimes, setRegimes]             = useState(null);
  const [regimesLoading, setRegimesLoading] = useState(false);
  const regimesPollRef = useRef(null);

  // ── Ticker mode (all-by-filters vs specific tickers) ─────────────────────
  const [tickerMode, setTickerMode]           = useState('all');
  const [selectedTickers, setSelectedTickers] = useState([]);
  // Live count of tickers the backtest will ACTUALLY use (shown before running).
  // Не лише фільтри: сервер проганяє ті самі правила, що й завантажувач бектесту
  // (покриття дат вікна, [8] делістинги, [9] пер-барна належність, ≥10 барів).
  // До 28.09 тут був сьогоднішній перелік: 4902 проти 3181 реально на 2021-2026.
  const [universeCount, setUniverseCount]        = useState(null);
  const [universeInfo, setUniverseInfo]          = useState(null);
  const [universeCountLoading, setUniverseCountLoading] = useState(false);
  // Найглибше вікно назад по ВСІХ комбінаціях форми — дзеркало _warmup_bars
  // (backtesting/compute.py) з максимумом, як _wb у воркері. Від нього залежить
  // лише поріг «≥10 барів у зрізі» (виміряно: 1 тікер із 4861), тож дзеркало
  // тримає ті самі поля, що форма надсилає в тілі запуску.
  const rangeMax = (r) => {
    const from = Number(r?.from), to = Number(r?.to), step = Number(r?.step);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
    if (!(step > 0) || to < from) return from;
    return from + Math.floor((to - from) / step + 1e-9) * step;
  };
  const isAdvancedForm = strategyType === 'advanced';
  const warmupBarsMax = (() => {
    const c = [0];
    c.push(Math.trunc(paramMode.tHoursBuy === 'range' ? rangeMax(rangeP.tHoursBuy) : Number(fixed.tHoursBuy) || 0));
    if (sellWindowMode === 'range') c.push(Math.trunc(rangeMax(sellWindowRange)));
    else if (sellWindowMode === 'fixed') c.push(Math.trunc(Number(sellWindowDays) || 0));
    if (stagnation.enabled) {
      const h = stagnation.bars.mode === 'single'
        ? Number(stagnation.bars.value) : rangeMax({ from: stagnation.bars.min, to: stagnation.bars.max, step: stagnation.bars.step });
      if (h > 0) c.push(Math.max(1, Math.trunc((h / 24) * 6.5)));
    }
    if (isAdvancedForm) {
      if (dipEnabled) c.push(Math.trunc(dipPerMode === 'range' ? rangeMax(dipPerRange) : Number(dipPerFixed) || 0));
      if (overpricedEnabled) c.push(Math.trunc(ovpPerMode === 'range' ? rangeMax(ovpPerRange) : Number(ovpPerFixed) || 0));
      if (liqEnabled) c.push(Math.trunc(Number(liqCfg.advWindowDays) * 7) + 7);
    }
    return Math.max(0, ...c.filter(Number.isFinite));
  })();
  useEffect(() => {
    if (tickerMode === 'specific' && selectedTickers.length === 0) {
      setUniverseCount(0);
      setUniverseCountLoading(false);
      return;
    }
    setUniverseCountLoading(true);
    const ctrl = new AbortController();
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const h = setTimeout(() => {
      getUniverseCount({
        data_type:        dataType,
        country:          form.country,
        sector:           form.sector,
        index:            form.index,
        market_cap_min:   num(form.marketCapMin),
        market_cap_max:   num(form.marketCapMax),
        market_cap_buckets: capBucketsPayload(form),
        beta_min:         num(form.betaMin),
        beta_max:         num(form.betaMax),
        min_avg_volume:   num(form.minAvgVolume),
        // In 'specific' mode the count reflects the chosen list AFTER filters layer on top.
        specific_tickers: tickerMode === 'specific' ? selectedTickers : [],
        start_time:       form.startTime || null,
        stop_time:        form.stopTime || null,
        // Шари надсилаються рівно як у тілі запуску: лише для advanced.
        delisting_enabled:    isAdvancedForm && delistEnabled,
        pit_universe_enabled: isAdvancedForm && pitEnabled,
        pit_require_data:     isAdvancedForm && pitEnabled ? pitRequireData : null,
        per_bar_universe:     isAdvancedForm && perBarUniverse,
        warmup_bars:          warmupBarsMax,
      }, { signal: ctrl.signal })
        .then(r => { setUniverseCount(r.data?.count ?? null); setUniverseInfo(r.data || null); })
        .catch(() => {})
        .finally(() => setUniverseCountLoading(false));
    }, 450);
    return () => { clearTimeout(h); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, tickerMode, selectedTickers, form.country, form.sector, form.index,
      form.marketCapMin, form.marketCapMax, form.marketCapBuckets, form.capCustom,
      form.betaMin, form.betaMax, form.minAvgVolume, form.startTime, form.stopTime,
      isAdvancedForm, delistEnabled, pitEnabled, pitRequireData, perBarUniverse, warmupBarsMax]);
  const [tickerSearch, setTickerSearch]       = useState('');
  const [allTickersList, setAllTickersList]   = useState([]);
  const [dropdownOpen, setDropdownOpen]       = useState(false);
  const dropdownRef = useRef(null);
  // Bulk paste of a ticker list (e.g. the saved legacy 1313 universe) into "Обрані акції".
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteMsg,  setPasteMsg]  = useState('');
  // Saved ticker lists (server-side) selectable in one click.
  const [savedLists, setSavedLists] = useState([]);
  useEffect(() => { getTickerLists().then(r => setSavedLists(r.data?.lists || [])).catch(() => {}); }, []);
  const loadSavedList = useCallback((list) => {
    const known = new Set(allTickersList.map(t => t.ticker));
    const valid = (list.tickers || []).map(t => t.toUpperCase()).filter(t => known.has(t));
    setSelectedTickers(valid);
    setPasteMsg(known.size === 0
      ? 'Список тікерів ще завантажується — спробуйте за мить'
      : `Завантажено «${list.label}»: ${valid.length}${(list.tickers || []).length > valid.length ? ` · пропущено (немає даних): ${(list.tickers || []).length - valid.length}` : ''}`);
  }, [allTickersList]);
  const applyPastedTickers = useCallback(() => {
    const known = new Set(allTickersList.map(t => t.ticker));
    if (known.size === 0) { setPasteMsg('Список тікерів ще завантажується — спробуйте за мить'); return; }
    const raw    = (pasteText || '').toUpperCase().split(/[^A-Z0-9.\-]+/).filter(Boolean);
    const uniq   = [...new Set(raw)];
    const valid  = uniq.filter(t => known.has(t));
    const unknown = uniq.filter(t => !known.has(t));
    setSelectedTickers(valid);
    setPasteMsg(
      valid.length === 0
        ? 'Жоден тікер не знайдено в доступних даних'
        : `Додано ${valid.length}${unknown.length ? ` · пропущено (немає даних): ${unknown.length}` : ''}`
    );
  }, [pasteText, allTickersList]);
  const [saveTopN, setSaveTopN]               = useState('1000');
  const [minReturnPct, setMinReturnPct]       = useState('');
  // «Відсіювати за періодами» (колишня «Тільки прибуткові в усіх періодах») — відсів під час рахунку.
  const [cullNegativePeriods, setCullNegativePeriods] = useState(false);
  // Дві планки менеджера (27.08). Поріг: період, гірший за нього, вбиває
  // одразу. Допуск: скільки періодів дозволено мати в мінусі (але не нижче
  // порогу) — у відсотках від їх кількості. Обидва 0 = стара поведінка.
  const [cullFloorPct, setCullFloorPct] = useState('0');
  const [cullLossTolPct, setCullLossTolPct] = useState('0');
  // Двоетапний пошук (менеджер 28.09): усі комбінації на першій сотні періодів,
  // топ-N за Avg. Yr — на решті. Див. web/backend/services/two_stage.py.
  // ── ПЕРЕБІР ПО БЛОКУ (менеджер 07.10): рядки готової сітки × нові значення
  // обраних осей. Значення осей — з форми, решта параметрів і вікно — з блоку.
  const [blockOpen, setBlockOpen]       = useState(false);
  const [blocks, setBlocks]             = useState([]);
  const [blockAxesDefs, setBlockAxesDefs] = useState([]);
  const [blockBatchId, setBlockBatchId] = useState('');
  const [blockAxes, setBlockAxes]       = useState([]);
  const [blockPreview, setBlockPreview] = useState(null);   // відповідь preview або {error}
  const [blockPreviewing, setBlockPreviewing] = useState(false);
  const [twoStageEnabled, setTwoStageEnabled] = useState(false);
  // Смарт-пошук (власник 19.10): не весь простір, а поколіннями навколо
  // найкращих знайдених. Див. web/backend/services/smart_search.py.
  const [smartEnabled, setSmartEnabled]       = useState(false);
  const [smartBudget, setSmartBudget]         = useState('30000');
  const [smartGenSize, setSmartGenSize]       = useState('1000');
  const [smartExplorePct, setSmartExplorePct] = useState('50');
  const [smartMetric, setSmartMetric]         = useState('avg_yr');
  const [smartTopK, setSmartTopK]             = useState('20');
  const [twoStagePeriods, setTwoStagePeriods] = useState('100');
  const [twoStageTopN, setTwoStageTopN] = useState('1000');
  // Діє лише коли періодів більше, ніж на етапі 1: інакше етапу 2 нема чого
  // рахувати, і бекенд однаково пустив би звичайний пошук.
  const twoStageK = parseInt(twoStagePeriods, 10) || 0;
  const twoStageN = parseInt(twoStageTopN, 10) || 0;
  const twoStageActive = strategyType === 'advanced' && twoStageEnabled
    && twoStageK > 0 && twoStageN > 0 && additionalPeriods.length > twoStageK;

  // ── Reset detail view when URL navigates back to /analyzer list ─────────────
  useEffect(() => {
    if (!initialResultId) {
      setSelectedId(null);
      setSelectedData(null);
      setTickerView(null);
    }
  }, [initialResultId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load from URL (initialResultId / initialTicker) ──────────────────────────
  useEffect(() => {
    if (!initialResultId) return;
    getAnalyzerResult(Number(initialResultId))
      .then(res => {
        setSelectedData(res.data);
        setSelectedId(Number(initialResultId));
        if (initialTicker) {
          const bData = res.data;
          let allTickerTrades = (bData.trades || []).filter(t => t.ticker === initialTicker);
          let tradeParams = bData.params || {};

          // Fallback: ticker only exists in an additional period (period_details),
          // e.g. when navigating directly to /analyzer/:id/:ticker via URL or refresh.
          if (allTickerTrades.length === 0 && bData.period_details) {
            for (const pDetail of Object.values(bData.period_details)) {
              const pTrades = (pDetail.trades || []).filter(t => t.ticker === initialTicker);
              if (pTrades.length > 0) {
                allTickerTrades = pTrades;
                // Period rows carry their own start/stop — merge over the row params
                // so the chart window follows the period the trade belongs to.
                if (pDetail.params) tradeParams = { ...tradeParams, ...pDetail.params };
                break;
              }
            }
          }

          // Same window as clicking the ticker inside BacktestDetail: anchored to
          // the backtest period with the strategy's lookback (≥ half a year) of
          // pre-history before the entry — not to the trades themselves.
          setTickerView(buildTickerTradeContext({
            ticker:     initialTicker,
            trades:     allTickerTrades,
            params:     tradeParams,
            meta:       bData._meta || {},
            warmupBars: bData._meta?.warmup_bars ?? 0,
          }));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load results ─────────────────────────────────────────────────────────────
  const pageSizeRef       = useRef(pageSize);
  const quickIdRef        = useRef('');
  // filtersRef provided by useAnalyzerFilters hook above
  const batchFilterRef    = useRef('');
  const starredIdsRef     = useRef(new Set());
  const showFavoritesRef  = useRef(false);

  // Build filter params from current state (shared by loadResults + handleDeleteFiltered)
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (batchFilterRef.current) params.batch_id = batchFilterRef.current;
    if (quickIdRef.current.trim()) params.id_filter = quickIdRef.current.trim();
    if (showFavoritesRef.current) {
      const ids = [...starredIdsRef.current];
      if (ids.length > 0) params.id_filter = ids.join(',');
    }
    for (const f of filtersRef.current) {
      if (f.field === 'id' && f.value) {
        params.id_filter = f.value.trim();
        // id filter is globally unique — batch_id would incorrectly narrow results
        delete params.batch_id;
      } else if (f.field === 'monthly_pct') {
        if (f.from != null) params.min_monthly = f.from;
        if (f.to   != null) params.max_monthly = f.to;
      } else if (f.field === 'yearly_pct') {
        if (f.from != null) params.min_yearly = f.from;
        if (f.to   != null) params.max_yearly = f.to;
      } else if (f.field === 'avg_yearly') {
        if (f.from != null) params.min_avg_yearly = f.from;
        if (f.to   != null) params.max_avg_yearly = f.to;
      } else if (f.field === 'periods') {
        if (f.from != null) params.min_periods = f.from;
        if (f.to   != null) params.max_periods = f.to;
      } else if (f.field === 'return_pct') {
        if (f.from != null) params.min_return = f.from;
        if (f.to   != null) params.max_return = f.to;
      } else if (f.field === 'win_rate') {
        if (f.from != null) params.min_win_rate = f.from;
        if (f.to   != null) params.max_win_rate = f.to;
      } else if (f.field === 'sharpe') {
        if (f.from != null) params.min_sharpe = f.from;
        if (f.to   != null) params.max_sharpe = f.to;
      } else if (f.field === 'max_dd') {
        if (f.from != null) params.min_max_dd = f.from;
        if (f.to   != null) params.max_max_dd = f.to;
      } else if (f.field === 'prof_months') {
        if (f.from != null) params.min_prof_months = f.from;
        if (f.to   != null) params.max_prof_months = f.to;
      } else if (f.field === 'worst_month') {
        if (f.from != null) params.min_worst_month = f.from;
        if (f.to   != null) params.max_worst_month = f.to;
      } else if (f.field === 'calmar') {
        if (f.from != null) params.min_calmar = f.from;
        if (f.to   != null) params.max_calmar = f.to;
      } else if (f.field === 'data_type' && f.value) {
        params.data_type_filter = f.value;
      } else if (f.field === 'result_date') {
        if (f.datePreset === 'today') {
          const d = new Date().toISOString().slice(0, 10);
          params.result_date_from = d; params.result_date_to = d;
        } else if (f.datePreset === 'yesterday') {
          const d = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          params.result_date_from = d; params.result_date_to = d;
        } else if (f.datePreset === 'last_week') {
          params.result_date_from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
          params.result_date_to   = new Date().toISOString().slice(0, 10);
        } else {
          if (f.from) params.result_date_from = f.from;
          if (f.to)   params.result_date_to   = f.to;
        }
      } else if (f.field === 'start_period') {
        if (f.datePreset === 'today') {
          const d = new Date().toISOString().slice(0, 10);
          params.start_period_from = d; params.start_period_to = d;
        } else if (f.datePreset === 'yesterday') {
          const d = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          params.start_period_from = d; params.start_period_to = d;
        } else if (f.datePreset === 'last_week') {
          params.start_period_from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
          params.start_period_to   = new Date().toISOString().slice(0, 10);
        } else {
          if (f.from) params.start_period_from = f.from;
          if (f.to)   params.start_period_to   = f.to;
        }
      } else if (f.field === 'end_period') {
        if (f.datePreset === 'today') {
          const d = new Date().toISOString().slice(0, 10);
          params.end_period_from = d; params.end_period_to = d;
        } else if (f.datePreset === 'yesterday') {
          const d = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          params.end_period_from = d; params.end_period_to = d;
        } else if (f.datePreset === 'last_week') {
          params.end_period_from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
          params.end_period_to   = new Date().toISOString().slice(0, 10);
        } else {
          if (f.from) params.end_period_from = f.from;
          if (f.to)   params.end_period_to   = f.to;
        }
      } else if (f.field === 'status' && f.value) {
        params.status = f.value;
      }
    }
    return params;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Номер найсвіжішого запиту таблиці — див. замок від гонки в loadResults.
  const reqSeqRef = useRef(0);

  const loadResults = useCallback(async (pg = 0, sc, sd, ps) => {
    // 🚨 ЗАМОК ВІД ГОНКИ. Перемикання збереженого набору стріляє кількома
    // ефектами одразу (batchFilter, appliedFilters, savedSearch), і кожен
    // викликає loadResults. Без цього лічильника таблицю перезаписує та
    // відповідь, що прийшла ОСТАННЬОЮ, а не та, що відповідає поточному стану:
    // рядки показувались на секунду й змінювались на «No results yet».
    const myReq = ++reqSeqRef.current;
    const isStale = () => myReq !== reqSeqRef.current;

    const col     = sc !== undefined ? sc : sortRef.current.col;
    const dir     = sd !== undefined ? sd : sortRef.current.dir;
    const curSize = ps !== undefined ? ps : pageSizeRef.current;
    const lim     = curSize === 'all' ? 100000 : Number(curSize);
    const off     = curSize === 'all' ? 0 : pg * lim;
    setLoading(true);
    setResults([]); // очищаємо старі дані → скелетон завжди показується під час завантаження
    try {
      const params = { limit: lim, offset: off, sort_col: col, sort_dir: dir > 0 ? 'asc' : 'desc',
                       ...buildFilterParams() };

      // Favorites filter — return empty immediately if no favorites starred
      if (showFavoritesRef.current && [...starredIdsRef.current].length === 0) {
        if (isStale()) return;
        setResults([]);
        setTotal(0);
        return;
      }

      let items = [];
      if (strategyTypeRef.current === 'advanced') {
        const res = await getAnalyzerAdvancedResults(params);
        if (isStale()) return;            // відповідь на застарілий запит
        items = res.data.results || [];
        setResults(items);
        setTotal(res.data.total || 0);
      } else {
        const res = await getAnalyzerResults(params);
        if (isStale()) return;            // відповідь на застарілий запит
        items = res.data.items || [];
        setResults(items);
        setTotal(res.data.total || 0);
        if (res.data.grand_total != null) setGrandTotal(res.data.grand_total);
      }

      // Sync local starredIds set with server-side is_saved flag for the rows
      // currently visible. Rows not in this response keep their local star —
      // they may exist on other pages. Authoritative state is now in
      // analyzer_results.is_saved (see migrate_is_saved.sql).
      const serverSaved = new Set(items.filter(r => r.is_saved).map(r => r.id));
      const visibleIds  = new Set(items.map(r => r.id));
      setStarredIds(prev => {
        let changed = false;
        const next = new Set(prev);
        for (const id of visibleIds) {
          if (serverSaved.has(id) && !next.has(id)) { next.add(id);    changed = true; }
          if (!serverSaved.has(id) && next.has(id)) { next.delete(id); changed = true; }
        }
        return changed ? next : prev;
      });
    } catch (err) {
      console.error('[loadResults] error:', err);
    } finally {
      // Скелетон гасить лише найсвіжіший запит: інакше застарілий, що
      // завершився пізніше, зняв би індикатор із того, який ще триває.
      if (!isStale()) setLoading(false); // eslint-disable-line react-hooks/exhaustive-deps
    }
  }, []);

  const loadSavedSearches = useCallback(async () => {
    try {
      const res = await getAnalyzerSearches();
      // Масив або нічого. `res.data || []` тут не досить: коли бекенд
      // недоступний, проксі віддає HTML-рядок, і він «істинний» — далі
      // `.filter` кидає «is not a function» і кабінет чорніє.
      setSavedSearches(Array.isArray(res.data) ? res.data : []);
      savedSearchesLoadedRef.current = true;
    } catch (_) {}
  }, []);

  // ── Load batches list ──────────────────────────────────────────────────────
  const loadBatches = useCallback(async () => {
    try {
      const res = await getAnalyzerBatches();
      setBatches(res.data || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadResults(0); loadSavedSearches(); loadBatches(); }, [loadResults, loadSavedSearches, loadBatches]);

  // ── Відкритий блок сітки переживає перезавантаження сторінки ──────────────
  // Пам'ятаємо лише плашку сітки («📊 Створено …»): після reload / закритої
  // вкладки на планшеті таблиця відкривається на тому ж блоці, а не на «усіх».
  // Писати починаємо лише ПІСЛЯ спроби відновлення — інакше початковий
  // null стер би збережене ще до того, як список плашок прийшов.
  const restoredBatchTagRef = useRef(false);
  useEffect(() => {
    if (restoredBatchTagRef.current || !savedSearchesLoadedRef.current) return;
    restoredBatchTagRef.current = true;
    let id = null;
    try { id = localStorage.getItem(LS_ACTIVE_BATCH_TAG); } catch (_) {}
    if (!id || activeSearchId !== null) return;
    const s = savedSearches.find(x => String(x.id) === id && x.params?._type === 'batch');
    if (s) handleLoadSearch(s);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearches]);
  useEffect(() => {
    if (!restoredBatchTagRef.current) return;
    const s = activeSearchId != null && savedSearches.find(x => x.id === activeSearchId);
    try {
      if (s && s.params?._type === 'batch') localStorage.setItem(LS_ACTIVE_BATCH_TAG, String(s.id));
      else localStorage.removeItem(LS_ACTIVE_BATCH_TAG);
    } catch (_) {}
  }, [activeSearchId, savedSearches]);

  // Sync strategyTypeRef and reload results when mode changes
  useEffect(() => {
    strategyTypeRef.current = strategyType;
    setPage(0);
    loadResults(0);
  }, [strategyType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── On mount: resume any in-progress jobs (survives page refresh) ─────────
  useEffect(() => {
    async function resumeInProgress() {
      // 1. Grid search: look for running rows to find active batch_id
      try {
        const res = await getAnalyzerResults({ status: 'running', limit: 1, offset: 0 });
        const runningRow = (res.data.items || [])[0];
        if (runningRow?.batch_id) {
          // Verify the batch is actually still running on the server
          try {
            const progRes = await getAnalyzerProgress(runningRow.batch_id);
            const progStatus = progRes.data?.status;
            if (progStatus && !['completed', 'failed', 'cancelled'].includes(progStatus)) {
              setRunningBatch({ batch_id: runningRow.batch_id, count: res.data.total });
              startPolling(runningRow.batch_id);
            }
          } catch (_) {
            // Progress endpoint returned 404 or error — batch is not active, skip
          }
        }
      } catch (_) {}

      // 2. Recalculate All
      try {
        const res = await getRecalculateProgress();
        const info = res.data;
        if (info.status === 'running' || info.status === 'loading') {
          setRecalcProgress(info);
          recalcPollRef.current = setInterval(async () => {
            try {
              const r2 = await getRecalculateProgress();
              setRecalcProgress(r2.data);
              if (['completed','failed','cancelled'].includes(r2.data.status)) {
                clearInterval(recalcPollRef.current);
                recalcPollRef.current = null;
                await loadResults(0);
                setTimeout(() => setRecalcProgress(null), 3000);
              }
            } catch (_) {}
          }, 800);
        }
      } catch (_) {}

      // 3. Recalculate Selected (batch)
      try {
        const res = await getRecalculateBatchProgress();
        const info = res.data;
        if (info.status === 'running' || info.status === 'loading') {
          setRecalcSelectedProgress(info);
          recalcSelectedPollRef.current = setInterval(async () => {
            try {
              const r2 = await getRecalculateBatchProgress();
              const d = r2.data;
              if (d.total > 0) setRecalcSelectedProgress(d);
              if (['completed','failed','idle'].includes(d.status)) {
                clearInterval(recalcSelectedPollRef.current);
                recalcSelectedPollRef.current = null;
                await loadResults(0);
                setTimeout(() => setRecalcSelectedProgress(null), 2000);
              }
            } catch (_) {}
          }, 800);
        }
      } catch (_) {}
    }
    resumeInProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── If 'all' is selected but total exceeds 1000 — auto-switch to 1000 ──────
  useEffect(() => {
    if (pageSize === 'all' && total > 1000) {
      handlePageSizeChange(1000);
    }
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Quick ID search → reload from backend with debounce ───────────────────
  useEffect(() => {
    quickIdRef.current = quickIdSearch;
    if (quickIdSearch.trim()) setActiveSearchId(null);
    const timer = setTimeout(() => { setPage(0); loadResults(0); }, 300);
    return () => clearTimeout(timer);
  }, [quickIdSearch, loadResults]);

  // ── Field filters → reload only when filters are APPLIED (not on every keystroke) ──
  // Діє і в режимі обраного: buildFilterParams кладе обране в id_filter, а
  // фільтри звужують його — інакше змінений фільтр не міняв би таблицю.
  useEffect(() => {
    setPage(0);
    loadResults(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // ── Clear stable preset indicator if user modified the 4 preset filters ──
  useEffect(() => {
    if (lastStablePreset == null) return;
    const t = lastStableThresholdsRef.current;
    if (!t) { setLastStablePreset(null); return; }
    const checks = { prof_months: t.prof_months, worst_month: t.worst_month, calmar: t.calmar, sharpe: t.sharpe };
    const allMatch = Object.entries(checks).every(([field, val]) =>
      appliedFilters.some(f => f.field === field && f.from === val)
    );
    if (!allMatch) setLastStablePreset(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // ── Batch filter → sync ref + reload ──────────────────────────────────────
  useEffect(() => {
    batchFilterRef.current = batchFilter;
    if (showFavoritesRef.current) return; // favorites mode owns the results — don't overwrite
    setPage(0);
    loadResults(0);
  }, [batchFilter, loadResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Favorites: sync starredIds ref (no reload — just toggling a star) ────────
  useEffect(() => {
    starredIdsRef.current = starredIds;
    // Only reload if favorites filter is currently active
    if (showFavoritesRef.current) {
      setPage(0);
      loadResults(0);
    }
  }, [starredIds, loadResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Favorites filter toggle → sync ref + reload ───────────────────────────
  const favMountedRef = useRef(false);
  useEffect(() => {
    showFavoritesRef.current = showFavoritesOnly;
    if (!favMountedRef.current) { favMountedRef.current = true; return; }
    setPage(0);
    loadResults(0);
  }, [showFavoritesOnly, loadResults]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getDataCatalog().then(r => setDataCatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    getDataSectors().then(r => {
      if (Array.isArray(r.data) && r.data.length > 0) setAvailableSectors(r.data);
    }).catch(() => {});
  }, []);

  // Load market regimes; if missing — auto-trigger recalculate + poll
  useEffect(() => {
    if (regimesPollRef.current) { clearInterval(regimesPollRef.current); regimesPollRef.current = null; }

    getAnalyzerRegimes(dataType).then(res => {
      const data = res.data;
      if (data?.intervals?.length) {
        setRegimes(data);
        setRegimesLoading(false);
      } else {
        // No data → auto-start calculation
        setRegimesLoading(true);
        recalculateRegimes().catch(() => {});
        regimesPollRef.current = setInterval(() => {
          getAnalyzerRegimes(dataType).then(r => {
            if (r.data?.intervals?.length) {
              setRegimes(r.data);
              setRegimesLoading(false);
              clearInterval(regimesPollRef.current);
              regimesPollRef.current = null;
            }
          }).catch(() => {});
        }, 3000);
      }
    }).catch(() => {});

    return () => { if (regimesPollRef.current) { clearInterval(regimesPollRef.current); regimesPollRef.current = null; } };
  }, [dataType]);

  // Load full tickers list for specific-ticker picker
  useEffect(() => {
    getTickersList()
      .then(res => setAllTickersList(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Persist settings to backend (debounced) ───────────────────────────────
  // 🚨 Зірочки сюди НЕ пишуться (08.10): їхнє єдине джерело — is_saved у рядках
  // (GET /api/analyzer/results/starred). Обʼєкт analyzer_ui браузер переписує
  // ЦІЛКОМ, і пристрій, що не дочекався налаштувань, затирав ним список ★.
  function scheduleSave(settings) {
    if (!settingsLoadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveUserSettings({ analyzer_ui: settings })
        .then(() => {
          setSettingsSavedAt(Date.now());
          setTimeout(() => setSettingsSavedAt(null), 2000);
        })
        .catch(() => {});
    }, 1500);
  }

  // ── Знімок параметрів пошуку ───────────────────────────────────────────────
  // Один список полів на три місця: автозбереження форми (analyzer_ui), знімок,
  // що кладеться в плашку сітки при запуску, і кнопка «скопіювати параметри
  // пошуку у форму» на цій плашці. Список таблиці (колонки, сортування,
  // зірочки) сюди навмисно не входить — це не параметри пошуку.
  function buildFormSnapshot() {
    return {
      form, dataType, selectedStrategy,
      fixed, paramMode, rangeP,
      numCompanies, nCompaniesMode, nCompaniesRange, sellWindowDays, sellWindowMode, sellWindowRange,
      parallelSplit, momentumParams, mrParams,
      sellMode, stagnation, intraday, riskProt,
      advancedOpen, reinvest,
      tickerMode, selectedTickers,
      regimeFilter,
      hardStopPct, hardStopCooldownBars,
      usePeakWindowBuy, usePeakWindowSell,
      buyCapEnabled, buyCapOffEnabled, buyCapMode, buyCapFixed, buyCapRange,
      overpricedEnabled, ovpPctMode, ovpPctFixed, ovpPctRange, ovpPerMode, ovpPerFixed, ovpPerRange,
      dipEnabled, dipPctMode, dipPctFixed, dipPctRange, dipPerMode, dipPerFixed, dipPerRange,
      rotEnabled, rotHoldMode, rotHoldFixed, rotHoldRange, rotFactorMode, rotFactorFixed, rotFactorRange,
      oppEnabled, oppHoldMode, oppHoldFixed, oppHoldRange, oppGainMode, oppGainFixed, oppGainRange,
      swapDeferEnabled, swapDeferOffEnabled, swapTpEnabled, swapTpOffEnabled, swapTpMode, swapTpFixed, swapTpRange,
      swapHtcEnabled, swapHtcOffEnabled, noGrowthOn, noGrowthOff,
      htcCostEnabled, htcCostMarkup,
      htcEnabled, htcOffEnabled, htcBarsMode, htcBarsFixed, htcBarsRange,
      tpImmediateEnabled, tpLetItRunEnabled, tpPctMode, tpPctFixed, tpPctRange, tpExpiryMode, tpExpiryFixed, tpExpiryRange,
      marginEnabled, marginCfg, marginCapRange, minAdvRange,
      liqEnabled, liqCfg, impactEnabled, pitEnabled, pitRequireData, delistEnabled, reorgEnabled, perBarUniverse,
      buyPriorities, capBucketSets,
      sliceEnabled, sliceCfg, dynNEnabled, dynNMode, partialSells, partialSells, dynNEnabled, dynNMode,
      additionalPeriods, periodPresets,
      cullNegativePeriods, cullFloorPct, cullLossTolPct,
      twoStageEnabled, twoStagePeriods, twoStageTopN,
      smartEnabled, smartBudget, smartGenSize, smartExplorePct, smartMetric, smartTopK,
    };
  }

  // `withPresets=false` — для копіювання з плашки: бібліотека збережених
  // періодів належить людині, а не сітці, і старий знімок її не перезаписує.
  function applyFormSnapshot(ui, { withPresets = true } = {}) {
    if (ui.selectedStrategy)   setSelectedStrategy(ui.selectedStrategy);
    if (ui.form)               setForm(f => ({ ...f, ...ui.form }));
    if (ui.fixed)              setFixed(prev => ({ ...prev, ...ui.fixed }));
    if (ui.paramMode)          setParamMode(prev => ({ ...prev, ...ui.paramMode }));
    if (ui.rangeP)             setRangeP(prev => ({ ...prev, ...ui.rangeP }));
    if (ui.numCompanies != null)   setNumCompanies(ui.numCompanies);
    if (ui.nCompaniesMode)         setNCompaniesMode(ui.nCompaniesMode);
    if (ui.nCompaniesRange)        setNCompaniesRange(ui.nCompaniesRange);
    if (ui.sellWindowDays != null) setSellWindowDays(ui.sellWindowDays);
    if (ui.sellWindowMode)         setSellWindowMode(ui.sellWindowMode);
    if (ui.sellWindowRange)        setSellWindowRange(ui.sellWindowRange);
    if (ui.parallelSplit != null)  setParallelSplit(ui.parallelSplit);
    if (ui.momentumParams)         setMomentumParams(prev => ({ ...prev, ...ui.momentumParams }));
    if (ui.mrParams)               setMrParams(prev => ({ ...prev, ...ui.mrParams }));
    if (ui.sellMode)               setSellMode(ui.sellMode);
    if (ui.stagnation)             setStagnation(prev => ({ ...prev, ...ui.stagnation }));
    if (ui.intraday)               setIntraday(prev => ({ ...prev, ...ui.intraday }));
    if (ui.riskProt)               setRiskProt(prev => ({ ...prev, ...ui.riskProt }));
    if (ui.advancedOpen != null)   setAdvancedOpen(ui.advancedOpen);
    if (ui.reinvest != null)       setReinvest(ui.reinvest);
    if (ui.tickerMode)             setTickerMode(ui.tickerMode);
    if (ui.selectedTickers?.length > 0) setSelectedTickers(ui.selectedTickers);
    if (ui.regimeFilter?.length > 0) setRegimeFilter(ui.regimeFilter);
    if (ui.hardStopPct != null)              setHardStopPct(ui.hardStopPct);
    if (ui.hardStopCooldownBars != null)     setHardStopCooldownBars(ui.hardStopCooldownBars);
    if (ui.usePeakWindowBuy != null)         setUsePeakWindowBuy(ui.usePeakWindowBuy);
    if (ui.usePeakWindowSell != null)        setUsePeakWindowSell(ui.usePeakWindowSell);
    if (ui.buyCapEnabled != null)            setBuyCapEnabled(ui.buyCapEnabled);
    if (ui.buyCapOffEnabled != null)         setBuyCapOffEnabled(ui.buyCapOffEnabled);
    if (ui.buyCapMode)                       setBuyCapMode(ui.buyCapMode);
    if (ui.buyCapFixed != null)              setBuyCapFixed(ui.buyCapFixed);
    if (ui.buyCapRange)                      setBuyCapRange(prev => ({ ...prev, ...ui.buyCapRange }));
    if (ui.overpricedEnabled != null)        setOverpricedEnabled(ui.overpricedEnabled);
    if (ui.ovpPctMode)                       setOvpPctMode(ui.ovpPctMode);
    if (ui.ovpPctFixed != null)              setOvpPctFixed(ui.ovpPctFixed);
    if (ui.ovpPctRange)                      setOvpPctRange(prev => ({ ...prev, ...ui.ovpPctRange }));
    if (ui.ovpPerMode)                       setOvpPerMode(ui.ovpPerMode);
    if (ui.ovpPerFixed != null)              setOvpPerFixed(ui.ovpPerFixed);
    if (ui.ovpPerRange)                      setOvpPerRange(prev => ({ ...prev, ...ui.ovpPerRange }));
    if (ui.dipEnabled != null)               setDipEnabled(ui.dipEnabled);
    if (ui.dipPctMode)                       setDipPctMode(ui.dipPctMode);
    if (ui.dipPctFixed != null)              setDipPctFixed(ui.dipPctFixed);
    if (ui.dipPctRange)                      setDipPctRange(prev => ({ ...prev, ...ui.dipPctRange }));
    if (ui.dipPerMode)                       setDipPerMode(ui.dipPerMode);
    if (ui.dipPerFixed != null)              setDipPerFixed(ui.dipPerFixed);
    if (ui.dipPerRange)                      setDipPerRange(prev => ({ ...prev, ...ui.dipPerRange }));
    if (ui.rotEnabled != null)               setRotEnabled(ui.rotEnabled);
    if (ui.rotHoldMode)                      setRotHoldMode(ui.rotHoldMode);
    if (ui.rotHoldFixed != null)             setRotHoldFixed(ui.rotHoldFixed);
    if (ui.rotHoldRange)                     setRotHoldRange(prev => ({ ...prev, ...ui.rotHoldRange }));
    if (ui.rotFactorMode)                    setRotFactorMode(ui.rotFactorMode);
    if (ui.rotFactorFixed != null)           setRotFactorFixed(ui.rotFactorFixed);
    if (ui.rotFactorRange)                   setRotFactorRange(prev => ({ ...prev, ...ui.rotFactorRange }));
    if (ui.oppEnabled != null)               setOppEnabled(ui.oppEnabled);
    if (ui.oppHoldMode)                      setOppHoldMode(ui.oppHoldMode);
    if (ui.oppHoldFixed != null)             setOppHoldFixed(ui.oppHoldFixed);
    if (ui.oppHoldRange)                     setOppHoldRange(prev => ({ ...prev, ...ui.oppHoldRange }));
    if (ui.oppGainMode)                      setOppGainMode(ui.oppGainMode);
    if (ui.oppGainFixed != null)             setOppGainFixed(ui.oppGainFixed);
    if (ui.oppGainRange)                     setOppGainRange(prev => ({ ...prev, ...ui.oppGainRange }));
    if (ui.swapDeferEnabled != null)         setSwapDeferEnabled(ui.swapDeferEnabled);
    if (ui.swapDeferOffEnabled != null)      setSwapDeferOffEnabled(ui.swapDeferOffEnabled);
    if (ui.swapTpEnabled != null)            setSwapTpEnabled(ui.swapTpEnabled);
    if (ui.swapTpOffEnabled != null)         setSwapTpOffEnabled(ui.swapTpOffEnabled);
    if (ui.swapTpMode)                       setSwapTpMode(ui.swapTpMode);
    if (ui.swapTpFixed != null)              setSwapTpFixed(ui.swapTpFixed);
    if (ui.swapTpRange)                      setSwapTpRange(prev => ({ ...prev, ...ui.swapTpRange }));
    if (ui.swapHtcEnabled != null)           setSwapHtcEnabled(ui.swapHtcEnabled);
    if (ui.swapHtcOffEnabled != null)        setSwapHtcOffEnabled(ui.swapHtcOffEnabled);
    if (ui.noGrowthOn != null)               setNoGrowthOn(ui.noGrowthOn);
    if (ui.noGrowthOff != null)              setNoGrowthOff(ui.noGrowthOff);
    if (ui.htcCostEnabled != null)           setHtcCostEnabled(ui.htcCostEnabled);
    if (ui.htcCostMarkup != null)            setHtcCostMarkup(ui.htcCostMarkup);
    if (ui.htcEnabled != null)               setHtcEnabled(ui.htcEnabled);
    if (ui.htcOffEnabled != null)            setHtcOffEnabled(ui.htcOffEnabled);
    if (ui.htcBarsMode)                      setHtcBarsMode(ui.htcBarsMode);
    if (ui.htcBarsFixed != null)             setHtcBarsFixed(ui.htcBarsFixed);
    if (ui.htcBarsRange)                     setHtcBarsRange(prev => ({ ...prev, ...ui.htcBarsRange }));
    if (ui.tpImmediateEnabled != null)        setTpImmediateEnabled(ui.tpImmediateEnabled);
    if (ui.tpLetItRunEnabled != null)         setTpLetItRunEnabled(ui.tpLetItRunEnabled);
    if (ui.tpPctMode)                        setTpPctMode(ui.tpPctMode);
    if (ui.tpPctFixed != null)               setTpPctFixed(ui.tpPctFixed);
    if (ui.tpPctRange)                       setTpPctRange(prev => ({ ...prev, ...ui.tpPctRange }));
    if (ui.tpExpiryMode)                     setTpExpiryMode(ui.tpExpiryMode);
    if (ui.tpExpiryFixed != null)            setTpExpiryFixed(ui.tpExpiryFixed);
    if (ui.tpExpiryRange)                    setTpExpiryRange(prev => ({ ...prev, ...ui.tpExpiryRange }));
    if (ui.marginEnabled != null)            setMarginEnabled(ui.marginEnabled);
    if (ui.liqEnabled != null)               setLiqEnabled(ui.liqEnabled);
    if (ui.liqCfg)                           setLiqCfg(prev => ({ ...prev, ...ui.liqCfg }));
    if (ui.impactEnabled != null)            setImpactEnabled(ui.impactEnabled);
    if (ui.pitEnabled != null)               setPitEnabled(ui.pitEnabled);
    if (ui.delistEnabled != null)            setDelistEnabled(ui.delistEnabled);
    if (ui.reorgEnabled != null)             setReorgEnabled(ui.reorgEnabled);
    if (ui.perBarUniverse != null)           setPerBarUniverse(ui.perBarUniverse);
    if (ui.sliceEnabled != null)             setSliceEnabled(ui.sliceEnabled);
    if (ui.dynNEnabled != null)              setDynNEnabled(ui.dynNEnabled);
    if (ui.partialSells != null)             setPartialSells(ui.partialSells);
    if (ui.dynNMode != null)                 setDynNMode(ui.dynNMode);
    if (ui.sliceCfg)                         setSliceCfg(ui.sliceCfg);
    if (ui.pitRequireData != null)           setPitRequireData(ui.pitRequireData);
    if (Array.isArray(ui.buyPriorities) && ui.buyPriorities.length) setBuyPriorities(ui.buyPriorities);
    if (Array.isArray(ui.capBucketSets))     setCapBucketSets(ui.capBucketSets);
    if (ui.marginCfg)                        setMarginCfg(prev => ({ ...prev, ...ui.marginCfg }));
    if (ui.marginCapRange)                   setMarginCapRange(prev => ({ ...prev, ...ui.marginCapRange }));
    if (ui.minAdvRange)                      setMinAdvRange(prev => ({ ...prev, ...ui.minAdvRange }));
    if (ui.cullNegativePeriods != null)      setCullNegativePeriods(!!ui.cullNegativePeriods);
    if (ui.cullFloorPct != null)            setCullFloorPct(String(ui.cullFloorPct));
    if (ui.cullLossTolPct != null)          setCullLossTolPct(String(ui.cullLossTolPct));
    if (ui.twoStageEnabled != null)         setTwoStageEnabled(!!ui.twoStageEnabled);
    if (ui.twoStagePeriods != null)         setTwoStagePeriods(String(ui.twoStagePeriods));
    if (ui.twoStageTopN != null)            setTwoStageTopN(String(ui.twoStageTopN));
    if (ui.smartEnabled != null)            setSmartEnabled(!!ui.smartEnabled);
    if (ui.smartBudget != null)             setSmartBudget(String(ui.smartBudget));
    if (ui.smartGenSize != null)            setSmartGenSize(String(ui.smartGenSize));
    if (ui.smartExplorePct != null)         setSmartExplorePct(String(ui.smartExplorePct));
    if (ui.smartMetric)                     setSmartMetric(String(ui.smartMetric));
    if (ui.smartTopK != null)               setSmartTopK(String(ui.smartTopK));
    if (Array.isArray(ui.additionalPeriods)) setAdditionalPeriods(ui.additionalPeriods);
    if (withPresets && Array.isArray(ui.periodPresets)) setPeriodPresets(ui.periodPresets);
  }

  // ── Load settings from backend on mount ───────────────────────────────────
  // Зірочки — окремо й першими: їх не затирає жоден стан браузера.
  useEffect(() => {
    getStarredIds()
      .then(r => {
        const ids = (r.data?.ids || []).map(Number);
        starredIdsRef.current = new Set(ids);
        setStarredIds(new Set(ids));
      })
      .catch(err => console.error('[starred] load failed:', err));
  }, []);

  // 🚨 settingsLoadedRef стає true ЛИШЕ після УСПІШНОГО завантаження (08.10).
  // Раніше він ставився у finally — тобто й після помилки (планшет, мережа,
  // таймаут під навантаженням), і перша ж зміна стану записувала на сервер
  // форму за замовчуванням поверх збереженої. Тепер: повтори з паузою, а поки
  // налаштування не прийшли — автозбереження мовчить.
  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    let timer = null;
    const load = () => getUserSettings().then(res => {
      if (cancelled) return;
      if (!res.data || typeof res.data !== 'object') throw new Error('settings: not an object');
      const ui = res.data.analyzer_ui;
      if (ui) {
        if (ui.visibleColumns) {
          // Dip і Take Profit розділені на дві колонки кожен (26.08). Без цього
          // перенесення в того, хто мав їх увімкненими, вони б просто зникли —
          // і це читалось би як поломка, а не як нові налаштування.
          const saved = { ...ui.visibleColumns };
          if (saved.dip != null && saved.dip_pct == null) {
            saved.dip_pct = saved.dip; saved.dip_bars = saved.dip;
          }
          if (saved.take_profit != null && saved.tp_pct == null) {
            saved.tp_pct = saved.take_profit; saved.tp_bars = saved.take_profit;
          }
          setVisibleColumns(prev => ({ ...DEFAULT_COLUMNS, ...prev, ...saved }));
        }
        if (ui.showFavoritesOnly != null) {
          showFavoritesRef.current = !!ui.showFavoritesOnly;
          setShowFavoritesOnly(!!ui.showFavoritesOnly);
        }
        if (ui.sortCol)            { setSortCol(ui.sortCol); sortRef.current = { ...sortRef.current, col: ui.sortCol }; }
        if (ui.sortDir != null)    { setSortDir(ui.sortDir); sortRef.current = { ...sortRef.current, dir: ui.sortDir }; }
        if (ui.pageSize != null)   { setPageSize(ui.pageSize); pageSizeRef.current = ui.pageSize; }
        applyFormSnapshot(ui);
        // Reload table with restored sort settings
        if (ui.sortCol || ui.sortDir != null || ui.pageSize != null) {
          const col = ui.sortCol || sortRef.current.col;
          const dir = ui.sortDir ?? sortRef.current.dir;
          const ps  = ui.pageSize ?? pageSizeRef.current;
          loadResults(0, col, dir, ps);
        }
      }
      settingsLoadedRef.current = true;
    }).catch(err => {
      if (cancelled) return;
      attempt += 1;
      console.error(`[settings] load failed (спроба ${attempt}), автозбереження вимкнене:`, err);
      timer = setTimeout(load, Math.min(30000, 2000 * attempt));
    });
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save all relevant state whenever it changes ─────────────────────
  useEffect(() => {
    scheduleSave({
      visibleColumns,
      showFavoritesOnly,
      sortCol, sortDir,
      pageSize,
      ...buildFormSnapshot(),
    });
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    visibleColumns, showFavoritesOnly,
    sortCol, sortDir, pageSize,
    form, dataType, selectedStrategy,
    fixed, paramMode, rangeP,
    numCompanies, nCompaniesMode, nCompaniesRange, sellWindowDays, sellWindowMode, sellWindowRange,
    parallelSplit, momentumParams, mrParams,
    sellMode, stagnation, intraday, riskProt,
    advancedOpen, reinvest,
    tickerMode, selectedTickers,
    regimeFilter,
    hardStopPct, hardStopCooldownBars,
    usePeakWindowBuy, usePeakWindowSell,
    buyCapEnabled, buyCapOffEnabled, buyCapMode, buyCapFixed, buyCapRange,
    overpricedEnabled, ovpPctMode, ovpPctFixed, ovpPctRange, ovpPerMode, ovpPerFixed, ovpPerRange,
    dipEnabled, dipPctMode, dipPctFixed, dipPctRange, dipPerMode, dipPerFixed, dipPerRange,
    rotEnabled, rotHoldMode, rotHoldFixed, rotHoldRange, rotFactorMode, rotFactorFixed, rotFactorRange,
    oppEnabled, oppHoldMode, oppHoldFixed, oppHoldRange, oppGainMode, oppGainFixed, oppGainRange,
    swapDeferEnabled, swapDeferOffEnabled, swapTpEnabled, swapTpOffEnabled, swapTpMode, swapTpFixed, swapTpRange,
    swapHtcEnabled, swapHtcOffEnabled, noGrowthOn, noGrowthOff,
    htcCostEnabled, htcCostMarkup,
    htcEnabled, htcOffEnabled, htcBarsMode, htcBarsFixed, htcBarsRange,
    tpImmediateEnabled, tpLetItRunEnabled, tpPctMode, tpPctFixed, tpPctRange, tpExpiryMode, tpExpiryFixed, tpExpiryRange,
    marginEnabled, marginCfg, marginCapRange, minAdvRange,
    liqEnabled, liqCfg, impactEnabled, pitEnabled, pitRequireData, delistEnabled, reorgEnabled, perBarUniverse,
    buyPriorities, capBucketSets,
    sliceEnabled, sliceCfg,
    additionalPeriods, periodPresets,
    cullNegativePeriods, cullFloorPct, cullLossTolPct,
    twoStageEnabled, twoStagePeriods, twoStageTopN,
    smartEnabled, smartBudget, smartGenSize, smartExplorePct, smartMetric, smartTopK,
  ]);

  // ── Filtered tickers for dropdown ────────────────────────────────────────────
  const filteredTickers = useMemo(() => {
    const q = tickerSearch.toLowerCase().trim();
    const list = allTickersList.filter(t =>
      !q ||
      t.ticker.toLowerCase().includes(q) ||
      t.name.toLowerCase().includes(q) ||
      (t.sector || '').toLowerCase().includes(q)
    );
    // Selected always on top
    const sel  = list.filter(t => selectedTickers.includes(t.ticker));
    const rest = list.filter(t => !selectedTickers.includes(t.ticker));
    return [...sel, ...rest];
  }, [allTickersList, tickerSearch, selectedTickers]);

  // ── Попап періодів: вікна їдуть окремим запитом ──────────────────────────
  // Рядок списку більше не несе `periods_results` — 55 КБ на рядок при тому,
  // що масив потрібен рівно одному рядку, і лише коли на нього натиснули.
  const openPeriodsPopup = useCallback(async (r) => {
    setPeriodsPopupRow(r);
    // Рядок, оновлений на місці (після дописування вікон), масив уже має —
    // тоді запит не потрібен.
    if (Array.isArray(r.periods_results)) { setPeriodsPopupData(r.periods_results); return; }
    setPeriodsPopupData(null);
    try {
      const res = await getAnalyzerResultPeriods(r.id);
      setPeriodsPopupData(Array.isArray(res.data?.periods_results) ? res.data.periods_results : []);
    } catch (err) {
      console.error('[openPeriodsPopup] error:', err);
      setPeriodsPopupData([]);
    }
  }, []);

  // ── Polling for running batch ─────────────────────────────────────────────────
  function startPolling(batchId) {
    if (pollRef.current) clearInterval(pollRef.current);
    setProgress({ current: 0, total: 1, progress: 0 });

    let errCount = 0;
    let tickCount = 0;

    pollRef.current = setInterval(async () => {
      tickCount++;

      // Safety: stop after 10 min regardless
      if (tickCount > 300) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunningBatch(null);
        setLoading(false);
        setProgress(null);
        loadResults(0);
        loadBatches();
        return;
      }

      try {
        // 1. Poll progress endpoint
        let progressIsDone = false;
        const isAdv = strategyTypeRef.current === 'advanced';
        try {
          const progRes = isAdv
            ? await getAnalyzerAdvancedProgress(batchId)
            : await getAnalyzerProgress(batchId);
          progressIsDone = ['completed', 'failed', 'cancelled'].includes(progRes.data?.status);
          setProgress(progRes.data);
        } catch (_) {}

        // 2. Check batch status (limit ≤ 500 — FastAPI constraint).
        // 🚨 Для advanced-пошуку цю вибірку НЕ беремо взагалі. Нижче написано,
        // що їй не можна вірити (GPU-simple рядки заповнюють вибірку, поки
        // GPU-full ще рахує), тобто 500 рядків їхали щоп'ять секунд, і рішення
        // за ними все одно не ухвалювалось. Саме advanced-пошук і є той
        // випадок, коли сторінка стоїть, тож зайвий запит тут найдорожчий.
        let batchItems = [];
        if (!isAdv) {
          const batchRes = await getAnalyzerResults({ batch_id: batchId, limit: 500, offset: 0 });
          batchItems = batchRes.data.items || [];
        }
        errCount = 0;

        // Stop when progress says done OR all visible batch items are finished.
        // For advanced jobs trust only the backend Redis status (see above).
        const isDone = progressIsDone
          || (!isAdv && batchItems.length > 0 && !batchItems.some(r => r.status === 'running'));

        if (isDone) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunningBatch(null);
          setLoading(false);
          setProgress(null);
          if (!isAdv) setBatchFilter('');  // clear batch filter → show all results (base only)
          if (!isAdv) loadBatches();
        }

        // 3. Refresh display table — filter by batchId so new rows are always visible
        const { col, dir } = sortRef.current;
        const curPS = pageSizeRef.current;
        const pollLim = curPS === 'all' ? 100000 : Number(curPS);
        if (isAdv) {
          const res = await getAnalyzerAdvancedResults({
            limit: pollLim, offset: 0,
            sort_col: col, sort_dir: dir > 0 ? 'asc' : 'desc',
            batch_id: batchId,
          });
          const items = res.data.results || [];
          setResults(items);
          setTotal(res.data.total || 0);
        } else {
          const res = await getAnalyzerResults({
            limit: pollLim, offset: 0,
            sort_col: col, sort_dir: dir > 0 ? 'asc' : 'desc',
            batch_id: batchId,
          });
          setResults(res.data.items || []);
          setTotal(res.data.total || 0);
        }
      } catch (_) {
        errCount++;
        // Stop only after 3 consecutive errors
        if (errCount >= 3) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunningBatch(null);
          setLoading(false);
          setProgress(null);
        }
      }
    }, 5000);
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Strategy selection ────────────────────────────────────────────────────────
  function selectStrategy(strat) {
    setSelectedStrategy(strat.id);
    const d = strat.defaults;
    setFixed(prev => ({ ...prev, xBuy: d.xBuy, tHoursBuy: d.tHoursBuy, ySell: d.ySell }));
    setNumCompanies(d.numCompanies || 20);
    if (strat.id === 'parallel') {
      setParallelSplit(DEFAULT_PARALLEL.split);
      setMomentumParams(DEFAULT_PARALLEL.momentum);
      setMrParams(DEFAULT_PARALLEL.mr);
    }
  }

  // ── Param mode toggle ─────────────────────────────────────────────────────────
  function toggleMode(key) {
    setParamMode(m => ({ ...m, [key]: m[key] === 'fixed' ? 'range' : 'fixed' }));
  }

  // ── Combination counter ───────────────────────────────────────────────────────
  const nCompaniesCount = nCompaniesMode === 'range'
    ? countRange('range', nCompaniesRange.min, nCompaniesRange.max, nCompaniesRange.step)
    : 1;

  const stagnationBarsCount = (() => {
    if (!stagnation.enabled) return 1;
    if (stagnation.bars.mode === 'range') {
      const { min, max, step } = stagnation.bars;
      if (step <= 0 || min > max) return 1;
      return Math.floor((max - min) / step + 1e-9) + 1;
    }
    return 1;
  })();
  const stagnationMinPctCount = (() => {
    if (!stagnation.enabled || stagnation.min_pct.mode !== 'range') return 1;
    const { min, max } = stagnation.min_pct;
    const step = parseFloat(stagnation.min_pct.step);
    if (!step || step <= 0 || min > max) return 1;
    return Math.floor((max - min) / step + 1e-9) + 1;
  })();
  const stagnationMaxPctCount = (() => {
    if (!stagnation.enabled || stagnation.max_pct.mode !== 'range') return 1;
    const { min, max } = stagnation.max_pct;
    const step = parseFloat(stagnation.max_pct.step);
    if (!step || step <= 0 || min > max) return 1;
    return Math.floor((max - min) / step + 1e-9) + 1;
  })();

  // Boolean/discrete dimensions (always expanded by backend regardless of UI checkbox values)
  const stagnationDim = stagnation.enabled
    ? 1 + stagnationBarsCount * stagnationMinPctCount * stagnationMaxPctCount  // OFF + ON combos
    : 1;
  const rpDim    = riskProt.enabled ? 5 : 1;   // OFF + 4 (overnight×weekend) combos
  const hsDim    = hardStopPct > 0  ? 2 : 1;   // 0 (disabled) + user value

  const baseCombos = selectedStrategy === 'parallel'
    ? Math.max(1, (momentumParams.num_cos?.length || 1)) * Math.max(1, (mrParams.num_cos?.length || 1))
    : (
    countRange(paramMode.xBuy, rangeP.xBuy.from, rangeP.xBuy.to, rangeP.xBuy.step) *
    countRange(paramMode.tHoursBuy, rangeP.tHoursBuy.from, rangeP.tHoursBuy.to, rangeP.tHoursBuy.step) *
    countRange(paramMode.ySell, rangeP.ySell.from, rangeP.ySell.to, rangeP.ySell.step) *
    countRange(sellWindowMode, sellWindowRange.from, sellWindowRange.to, sellWindowRange.step) *
    nCompaniesCount
  );

  // Advanced mechanism dimensions match backend `_build_advanced_combinations`
  // pair-based expansion: cartesian({user values}) ∪ {OFF}. Single-axis
  // mechanisms (buy_cap, htc) get +1 OFF unless user's range already includes 0.
  const buyCapDim = dimFromTwoFlags(buyCapEnabled, buyCapOffEnabled,
    buyCapMode, buyCapFixed, buyCapRange, 0.0);
  const ovpDim = overpricedEnabled
    ? countRange(ovpPctMode, ovpPctRange?.from, ovpPctRange?.to, ovpPctRange?.step) *
      countRange(ovpPerMode, ovpPerRange?.from, ovpPerRange?.to, ovpPerRange?.step)
    : 1;
  // Dip is always-on when enabled — no OFF pair, just cartesian of user values.
  const dipDim = dipEnabled
    ? countRange(dipPctMode, dipPctRange.from, dipPctRange.to, dipPctRange.step) *
      countRange(dipPerMode, dipPerRange.from, dipPerRange.to, dipPerRange.step)
    : 1;
  const rotDim = rotEnabled
    ? countRange(rotHoldMode, rotHoldRange?.from, rotHoldRange?.to, rotHoldRange?.step) *
      countRange(rotFactorMode, rotFactorRange?.from, rotFactorRange?.to, rotFactorRange?.step)
    : 1;
  const oppDim = oppEnabled
    ? countRange(oppHoldMode, oppHoldRange?.from, oppHoldRange?.to, oppHoldRange?.step) *
      countRange(oppGainMode, oppGainRange?.from, oppGainRange?.to, oppGainRange?.step)
    : 1;
  const htcDim = dimFromTwoFlags(htcEnabled, htcOffEnabled,
    htcBarsMode, htcBarsFixed, htcBarsRange, 0);
  const tpAnyEnabled = tpImmediateEnabled || tpLetItRunEnabled;
  const tpDim = tpAnyEnabled
    ? countRange(tpPctMode, tpPctRange?.from, tpPctRange?.to, tpPctRange?.step) *
      countRange(tpExpiryMode, tpExpiryRange?.from, tpExpiryRange?.to, tpExpiryRange?.step) *
      (tpImmediateEnabled && tpLetItRunEnabled ? 2 : 1)
    : 1;

  // Продаж під покупку: дзеркало _build_swap_defer_dim / _build_swap_tp_fracs.
  // Частка TP без самого TP згортається бекендом до «вимкнено».
  const swapDeferDim = swapDeferEnabled ? (swapDeferOffEnabled ? 2 : 1) : 1;
  const swapTpDim = (swapTpEnabled && tpAnyEnabled)
    ? Math.max(1, countRange(swapTpMode, swapTpRange?.from, swapTpRange?.to, swapTpRange?.step)
                  + (swapTpOffEnabled ? 1 : 0))
    : 1;

  // HTC під опціями множить лише варіанти З увімкненим HTC (бекенд згортає
  // галочку до «вимкнено», коли HTC немає): обидві галочки → ON-варіанти ×2.
  const htcOnCount = htcEnabled
    ? countRange(htcBarsMode, htcBarsRange?.from, htcBarsRange?.to, htcBarsRange?.step) : 0;
  const htcDimEff = (htcEnabled && swapHtcEnabled && swapHtcOffEnabled)
    ? htcDim + htcOnCount : htcDim;

  // Buy% на виході: дзеркало _build_no_growth_dim — обидва прапорці = ×2.
  const noGrowthDim = (strategyType === 'advanced' && noGrowthOn && noGrowthOff) ? 2 : 1;

  const boolCombos = selectedStrategy === 'parallel' ? 1 : (
    stagnationDim * rpDim * hsDim *
    buyCapDim * ovpDim * dipDim * rotDim * oppDim * htcDimEff * tpDim *
    swapDeferDim * swapTpDim * noGrowthDim
  );

  // Дві осі менеджера 2026-08-19 множать сітку так само, як будь-яка інша вісь.
  // Пріоритет рахується лише при увімкненому блоці ліквідності — інакше в тіло
  // запиту йде одне значення ['dip'].
  const prioDim   = liqEnabled ? Math.max(1, buyPriorities.length) : 1;
  const capSetDim = capBucketSets.length ? capBucketSets.length + 1 : 1;

  // Плече і поріг обігу — такі самі осі, як усі інші. Множать сітку лише коли
  // ввімкнений і сам шар, і прапорець діапазону: без діапазону це скаляр.
  const marginCapDim = (marginEnabled && marginCapRange.enabled)
    ? countRange('range', Number(marginCapRange.from), Number(marginCapRange.to), Number(marginCapRange.step))
    : 1;
  const minAdvDim = (liqEnabled && minAdvRange.enabled)
    ? countRange('range', Number(minAdvRange.from), Number(minAdvRange.to), Number(minAdvRange.step))
    : 1;

  // totalCombos = queue items (= combos). Periods are sub-sims inside each combo,
  // not separate queue entries — keeps pre-run counter consistent with backend progress.
  const totalCombos = baseCombos * boolCombos * prioDim * capSetDim * marginCapDim * minAdvDim;
  // Смарт-пошук діє, лише коли простір більший за бюджет: інакше повний
  // перебір дешевший і точніший (бекенд тоді сам переходить на нього).
  const smartBudgetN = parseInt(smartBudget, 10) || 0;
  const smartActive  = strategyType === 'advanced' && smartEnabled
    && smartBudgetN >= 100 && totalCombos > smartBudgetN;
  const runCombos    = smartActive ? smartBudgetN : totalCombos;

  // ── GPU eligibility + time estimate ───────────────────────────────────────────
  // Mirrors worker.py _can_use_gpu_web() + _can_use_gpu_adv() conditions.
  // GPU requires > 50 combos (worker hard-coded threshold).
  // Worker routes per-combo: some may go GPU, others CPU (e.g. hsDim=2 combos).
  const sellWindowFixed = sellWindowMode === 'fixed' ? Number(sellWindowDays) : null;
  const tHoursBuyFixed  = paramMode.tHoursBuy === 'fixed' ? Number(fixed.tHoursBuy) : null;
  // 'same' mode = identical to buy window → always GPU-compatible
  // 'fixed' mode → GPU only if value matches fixed tHoursBuy (or tHoursBuy is a range per-combo)
  const sellWindowMatchesBuy = (
    sellWindowMode === 'same' ||
    (sellWindowMode !== 'range' &&
     (sellWindowFixed == null || tHoursBuyFixed == null || sellWindowFixed === tHoursBuyFixed))
  );

  // Base params GPU-compatible (ignoring bool multipliers like hardStop/riskProt variants)
  const canUseGpuBase = (
    totalCombos > 50 &&
    selectedStrategy !== 'parallel' &&
    additionalPeriods.length === 0 &&
    !stagnation.enabled &&
    !intraday.enabled &&
    !buyCapEnabled &&
    !rotEnabled &&
    !oppEnabled &&
    !swapDeferEnabled &&
    !swapTpEnabled &&
    !swapHtcEnabled &&
    !overpricedEnabled &&
    !dipEnabled &&
    !htcEnabled &&
    !marginEnabled &&
    !liqEnabled &&
    !pitEnabled &&
    !delistEnabled &&
    !reorgEnabled &&
    // лише прості акції діють ЗАВЖДИ (рішення власника 16.09), а їхніх заборон входу
    // (SPAC до злиття, вікно спецдивіденду) GPU-ядра не мають → завжди процесор
    false &&
    !perBarUniverse &&
    !sliceEnabled &&
    !dynNEnabled &&
    !partialSells &&
    sellWindowMatchesBuy
  );

  // Bool multipliers that create CPU-only variants alongside GPU variants:
  // hardStopPct > 0 → hsDim=2: first variant (stop=0) → GPU, second (stop=N) → CPU
  // riskProt overnight/weekend → rpDim>1: first variant (off) → GPU, rest → CPU
  const _hs = parseFloat(hardStopPct || 0) > 0;
  const _rp = riskProt.enabled && (riskProt.sell_overnight || riskProt.sell_weekend);
  const hasCpuBoolVariants = _hs || _rp;

  const canUseGpu   = canUseGpuBase && !hasCpuBoolVariants; // all combos → GPU
  const mixedGpuCpu = canUseGpuBase && hasCpuBoolVariants;  // some GPU, some CPU

  // ── Осі, які МОВЧАТЬ під Take Profit «одразу» з нескінченним терміном ──────
  // Механізм структурний, не збіг: у механізмі 1 TP ніколи не ставить
  // `tp_reached` (він продає), а при терміні 0 правило озброєне на КОЖНОМУ
  // барі — отже `tp_suppressing` теж. Drop%/No-growth/Stagnation не
  // виконуються жодного разу (advanced_backtest.py, фаза 1), і перебір за
  // цими осями дає однакові рядки до цента.
  // ⚠️ Стосується ТІЛЬКИ [4a] з терміном 0. У механізму 2 після досягнення TP
  // правила вмикаються назад, а скінченний термін знімає глушіння сам —
  // виміряно, що там осі живі.
  const tpMutesSellRules = (
    tpImmediateEnabled &&
    (tpExpiryMode === 'fixed'
      ? Number(tpExpiryFixed) === 0
      : Number(tpExpiryRange.from) === 0)
  );
  // 🚨 Вісь визначається ТИМИ САМИМИ лічильниками, що й число комбінацій, а не
  // прапорцем «режим=діапазон». Інакше помилки в обидва боки: вироджений
  // діапазон (from == to) дав би хибну тривогу, а ввімкнена стагнація з
  // фіксованими параметрами — хибну тишу, бо вона й так додає варіант OFF
  // (stagnationDim = 1 + …). Для Hold to Cost те саме робить dimFromTwoFlags,
  // який ще й знає про діапазон, що вже містить 0.
  const tpMutedAxes = !tpMutesSellRules ? [] : [
    countRange(paramMode.ySell, rangeP.ySell.from, rangeP.ySell.to, rangeP.ySell.step) > 1
      && 'поріг падіння (Sell%)',
    countRange(sellWindowMode, sellWindowRange.from, sellWindowRange.to, sellWindowRange.step) > 1
      && 'вікно продажу',
    htcDim > 1        && 'Hold to Cost [3]',
    stagnationDim > 1 && 'Stagnation [2]',
  ].filter(Boolean);
  // Обидва механізми разом: глушіння стосується лише половини сітки — тієї, де
  // mode=1. Половина з [4b] рахується нормально.
  const tpMutedHalfOnly = tpMutesSellRules && tpLetItRunEnabled;

  // ── Драбина пріоритетів продажу для блоку [3] ──────────────────────────────
  // Показуємо лише щаблі, ввімкнені у формі (так уже поводився рядок
  // Stagnation), інакше текст описував би правила, яких у цій сітці немає.
  // ⚠️ hard_stop у сітці — вісь: роутер завжди додає варіант 0 і лише за ним
  // значення користувача (_hs_dim). Тому щабель показуємо, коли користувач
  // узагалі задав поріг.
  const ladderHs   = parseFloat(hardStopPct || 0) > 0;
  const ladderStag = stagnation.enabled;
  const nBase = 1 + (ladderHs ? 1 : 0) + (tpAnyEnabled ? 1 : 0);
  const htcSuppressesLabel = ladderStag ? `${nBase}-${nBase + 1}` : `${nBase}`;
  const htcExemptLabel = [ladderHs && 'Hard Stop', tpAnyEnabled && 'Take Profit']
    .filter(Boolean).join(' і ');

  const timeEstimate = (() => {
    if (totalCombos <= 1) return null;
    let secs;
    let basis = '';
    // 🚨 ОЦІНКА — З ВИМІРЯНОЇ ШВИДКОСТІ ПАРКУ, А НЕ З КОНСТАНТ. Стара формула
    // (137 комбінацій/с і 3 мс на вікно — числа з березня, одна машина, без
    // шарів і без відсіву) на сітці 39 200 × 1074 вікна обіцяла ~35 год, а
    // парк за останні години рухався на 100-240 комбінацій за годину.
    // Масштаб за кількістю вікон — наближення: відсів робить вартість
    // комбінації нелінійною, тож це порядок величини, а не розклад.
    const nWin = 1 + additionalPeriods.length;
    if (!canUseGpu && parkSpeed?.avg_rate) {
      const perComboWin = parkSpeed.avg_rate * (1 + (parkSpeed.n_periods || 0));
      secs = runCombos * nWin / perComboWin;
      basis = `за середньою швидкістю парку на сітці «${parkSpeed.name || parkSpeed.bid}»: `
            + `${Math.round(parkSpeed.avg_rate * 3600).toLocaleString('uk-UA')} комбінацій/год при `
            + `${parkSpeed.n_periods || 0} вікнах; перераховано на ${additionalPeriods.length} вікон. `
            + 'Відсів робить вартість нелінійною — це порядок величини.'
            // Навмисно НЕ перераховуємо на два етапи: лінійна модель «за
            // вікнами» пообіцяла б ~×7, а на живій сітці 24.09 (39 200 × 1074,
            // −30 %, допуск 25 %) оцінка — ×1.5-2: відсіяні й так гинуть рано.
            + (twoStageActive ? ' Двоетапний пошук: оцінка для одного етапу на всіх періодах; '
              + 'реально швидше, наскільки — залежить від порогів відсіву.' : '');
    } else if (canUseGpu) {
      secs = runCombos / 500_000;
    } else if (mixedGpuCpu) {
      // GPU handles baseCombos (disabled variant), CPU handles the rest
      secs = baseCombos / 500_000 + (runCombos - baseCombos) / 137;
    } else {
      secs = runCombos / 137;
    }
    if (!basis) {
      // Additional periods run as fast numpy sub-sims (~3ms each)
      secs += runCombos * additionalPeriods.length * 0.003;
      basis = 'Без виміру парку: стара формула (137 комбінацій/с, 3 мс на вікно) — сильно занижує.';
    }
    const txt = secs < 1 ? '< 1 сек'
      : secs < 60 ? `~${Math.round(secs)} сек`
      : secs < 3600 ? `~${Math.round(secs / 60)} хв`
      : secs < 72 * 3600 ? `~${(secs / 3600).toFixed(1)} год`
      : `~${(secs / 86400).toFixed(1)} доби`;
    const color = secs < 60 ? '#16A34A' : secs < 3600 ? '#D97706' : '#DC2626';
    return { text: txt, color, basis };
  })();

  // ── Duration ──────────────────────────────────────────────────────────────────
  const durationDays = (() => {
    try { const d = (new Date(form.stopTime) - new Date(form.startTime)) / 86400000; return isNaN(d) ? '—' : `${Math.round(d)} days`; }
    catch (_) { return '—'; }
  })();

  // ── Field helpers ──────────────────────────────────────────────────────────────
  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); }
  function toggleMulti(key, val) {
    setForm(f => { const cur = f[key] || []; return { ...f, [key]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] }; });
  }
  // ── Run backtest ──────────────────────────────────────────────────────────────
  // Тіло запиту з поточної форми. Окремою функцією, бо його ж бере перебір по
  // блоку — і для лічильника (preview), і для запуску: тіло мусить бути одне.
  function buildRunBody() {
    // Max positions is the user's value in BOTH modes. In 'specific' the picked
    // tickers are a SCREENING POOL (the strategy still holds max_pos at a time) —
    // NOT an equal-weight hold-everything basket. (Old behaviour forced
    // num_companies = len(selected) → pos_size 100/N → near-flat identical results.)
    let nCoList;
    if (nCompaniesMode === 'range') {
      const { min, max, step } = nCompaniesRange;
      nCoList = [];
      for (let v = min; v <= max + 1e-9; v += step) nCoList.push(Math.round(v));
      if (nCoList.length === 0) nCoList = [Math.max(1, min)];
    } else {
      nCoList = [Math.max(1, Number(numCompanies) || 20)];
    }
    const nCo    = nCoList[0];
    const pSize  = parseFloat((100 / nCo).toFixed(4));
    const body = {
      name:              searchName || '',
      start_time:        form.startTime,
      stop_time:         form.stopTime,
      data_type:         dataType,
      country:           form.country,
      // sector/index now LAYER on top of a specific list too (not just 'all' mode).
      sector:            form.sector,
      index:             form.index,
      specific_tickers:  tickerMode === 'specific' && selectedTickers.length > 0 ? selectedTickers : undefined,
      market_cap_min:    Number(form.marketCapMin),
      market_cap_max:    Number(form.marketCapMax),
      market_cap_buckets: capBucketsPayload(form),
      beta_min:          form.betaMin !== '' ? Number(form.betaMin) : undefined,
      beta_max:          form.betaMax !== '' ? Number(form.betaMax) : undefined,
      min_avg_volume:    form.minAvgVolume !== '' ? Number(form.minAvgVolume) : undefined,
      strategy:          selectedStrategy,
      engine:            engine,
      initial_capital:   Number(form.initialCapital),
      num_companies:     nCoList,
    };

    // Add fixed or range params (posSize is now auto from numCompanies)
    body['pos_size'] = pSize;
    const rangeKeys = [
      { key: 'xBuy',     bodyFixed: 'x_buy',     bodyFrom: 'x_buy_from',     bodyTo: 'x_buy_to',     bodyStep: 'x_buy_step' },
      { key: 'tHoursBuy',bodyFixed: 't_hours_buy',bodyFrom: 't_hours_buy_from',bodyTo: 't_hours_buy_to',bodyStep: 't_hours_buy_step' },
      { key: 'ySell',    bodyFixed: 'y_sell',     bodyFrom: 'y_sell_from',    bodyTo: 'y_sell_to',    bodyStep: 'y_sell_step' },
    ];
    for (const { key, bodyFixed, bodyFrom, bodyTo, bodyStep } of rangeKeys) {
      if (paramMode[key] === 'range') {
        body[bodyFrom] = rangeP[key].from;
        body[bodyTo]   = rangeP[key].to;
        body[bodyStep] = rangeP[key].step;
      } else {
        body[bodyFixed] = fixed[key];
      }
    }

    // sell_window_days: 'same' = omit (backend uses buy window, GPU-compatible)
    if (sellWindowMode === 'range') {
      body.sell_window_days_from = sellWindowRange.from;
      body.sell_window_days_to   = sellWindowRange.to;
      body.sell_window_days_step = sellWindowRange.step;
    } else if (sellWindowMode === 'fixed') {
      body.sell_window_days = Number(sellWindowDays);
    }

    // Advanced settings
    body.sell_mode          = sellMode;
    body.stagnation_enabled = stagnation.enabled;
    if (stagnation.min_pct.mode === 'single') {
      body.stagnation_min_pct       = stagnation.min_pct.value;
      body.stagnation_min_pct_range = null;
    } else {
      body.stagnation_min_pct       = null;
      body.stagnation_min_pct_range = { min: parseFloat(stagnation.min_pct.min), max: parseFloat(stagnation.min_pct.max), step: parseFloat(stagnation.min_pct.step) || 0.1 };
    }
    if (stagnation.max_pct.mode === 'single') {
      body.stagnation_max_pct       = stagnation.max_pct.value;
      body.stagnation_max_pct_range = null;
    } else {
      body.stagnation_max_pct       = null;
      body.stagnation_max_pct_range = { min: parseFloat(stagnation.max_pct.min), max: parseFloat(stagnation.max_pct.max), step: parseFloat(stagnation.max_pct.step) || 0.1 };
    }
    if (stagnation.bars.mode === 'single') {
      body.stagnation_bars       = stagnation.bars.value;
      body.stagnation_bars_range = null;
    } else {
      body.stagnation_bars       = null;
      body.stagnation_bars_range = { min: stagnation.bars.min, max: stagnation.bars.max, step: stagnation.bars.step };
    }
    body.intraday_enabled   = intraday.enabled;
    body.intraday_pct       = intraday.pct;

    // Risk protection
    body.risk_protection = { ...riskProt };
    body.reinvest = reinvest;
    body.regime_filter = regimeFilter;
    body.hard_stop_pct = hardStopPct;
    body.hard_stop_cooldown_bars = hardStopCooldownBars;
    body.use_peak_window_buy  = usePeakWindowBuy;
    body.use_peak_window_sell = usePeakWindowSell;

    // Meta — filter context for display in results table
    body.meta = {
      regime_filter:    regimeFilter,
      ticker_mode:      tickerMode,
      specific_tickers: tickerMode === 'specific' ? selectedTickers : [],
      countries:        Array.isArray(form.country)   ? form.country   : [form.country],
      sectors:          Array.isArray(form.sector)    ? form.sector    : [form.sector],
      indexes:          Array.isArray(form.index)     ? form.index     : [],
      market_cap_type:  capTypeLabel(form),
      market_cap_buckets: capBucketsPayload(form),
      market_cap_min:   Number(form.marketCapMin),
      market_cap_max:   Number(form.marketCapMax),
      beta_min:         form.betaMin !== '' ? Number(form.betaMin) : null,
      beta_max:         form.betaMax !== '' ? Number(form.betaMax) : null,
      min_avg_volume:   form.minAvgVolume !== '' ? Number(form.minAvgVolume) : null,
    };

    // Add parallel-specific params when parallel strategy selected
    if (selectedStrategy === 'parallel') {
      body.parallel_split           = parallelSplit;
      body.p_momentum_window_h      = momentumParams.window_h;
      body.p_momentum_buy_pct       = momentumParams.buy_pct;
      body.p_momentum_sell_pct      = momentumParams.sell_pct;
      body.p_momentum_num_cos       = momentumParams.num_cos?.length ? momentumParams.num_cos : [12];
      body.p_momentum_pos_size      = momentumParams.pos_size;
      body.p_mr_window_h            = mrParams.window_h;
      body.p_mr_entry_drop_pct      = mrParams.entry_drop_pct;
      body.p_mr_take_profit_pct     = mrParams.take_profit_pct;
      body.p_mr_stop_loss_pct       = mrParams.stop_loss_pct;
      body.p_mr_num_cos             = mrParams.num_cos?.length ? mrParams.num_cos : [8];
      body.p_mr_pos_size            = mrParams.pos_size;
    }

    // Additional periods testing
    // Filter out incomplete entries (both dates required); backend will
    // re-validate format and skip malformed.
    // На сервер їдуть ЛИШЕ дати. Решта полів згенерованого вікна (зерно,
    // довжина, днів) потрібна формі, а не рушію — а `additional_periods`
    // лягає в params_json КОЖНОГО рядка сітки, тож зайвий ключ множиться на
    // десятки тисяч рядків.
    const _ap = additionalPeriods
      .filter(p => p.start_date && p.end_date)
      .map(p => ({ start_date: p.start_date, end_date: p.end_date }));
    if (_ap.length > 0) {
      body.additional_periods = _ap;
    }

    if (strategyTypeRef.current === 'advanced') {
      // Build advanced-specific params on top of shared body
      body.strategy = 'advanced';
      body.data_type = 'hourly';
      // Advanced mechanism flags
      body.buy_cap_enabled = buyCapEnabled;
      body.buy_cap_off_enabled = buyCapOffEnabled;
      if (buyCapEnabled) {
        if (buyCapMode === 'range') { body.buy_cap_pct_from = buyCapRange.from; body.buy_cap_pct_to = buyCapRange.to; body.buy_cap_pct_step = buyCapRange.step; }
        else { body.buy_cap_pct = buyCapFixed; }
      }
      body.overpriced_filter_enabled = overpricedEnabled;
      if (overpricedEnabled) {
        if (ovpPctMode === 'range') { body.overpriced_pct_from = ovpPctRange.from; body.overpriced_pct_to = ovpPctRange.to; body.overpriced_pct_step = ovpPctRange.step; }
        else { body.overpriced_pct = ovpPctFixed; }
        if (ovpPerMode === 'range') { body.overpriced_period_bars_from = ovpPerRange.from; body.overpriced_period_bars_to = ovpPerRange.to; body.overpriced_period_bars_step = ovpPerRange.step; }
        else { body.overpriced_period_bars = ovpPerFixed; }
      }
      body.dip_filter_enabled = dipEnabled;
      if (dipEnabled) {
        if (dipPctMode === 'range') { body.dip_pct_from = dipPctRange.from; body.dip_pct_to = dipPctRange.to; body.dip_pct_step = dipPctRange.step; }
        else { body.dip_pct = dipPctFixed; }
        if (dipPerMode === 'range') { body.dip_period_bars_from = dipPerRange.from; body.dip_period_bars_to = dipPerRange.to; body.dip_period_bars_step = dipPerRange.step; }
        else { body.dip_period_bars = dipPerFixed; }
      }
      body.rotation_enabled = rotEnabled;
      if (rotEnabled) {
        if (rotHoldMode === 'range') { body.rotation_min_hold_bars_from = rotHoldRange.from; body.rotation_min_hold_bars_to = rotHoldRange.to; body.rotation_min_hold_bars_step = rotHoldRange.step; }
        else { body.rotation_min_hold_bars = rotHoldFixed; }
        if (rotFactorMode === 'range') { body.rotation_factor_from = rotFactorRange.from; body.rotation_factor_to = rotFactorRange.to; body.rotation_factor_step = rotFactorRange.step; }
        else { body.rotation_factor = rotFactorFixed; }
      }
      body.opportunity_enabled = oppEnabled;
      if (oppEnabled) {
        if (oppHoldMode === 'range') { body.opportunity_min_hold_bars_from = oppHoldRange.from; body.opportunity_min_hold_bars_to = oppHoldRange.to; body.opportunity_min_hold_bars_step = oppHoldRange.step; }
        else { body.opportunity_min_hold_bars = oppHoldFixed; }
        if (oppGainMode === 'range') { body.opportunity_min_gain_pct_from = oppGainRange.from; body.opportunity_min_gain_pct_to = oppGainRange.to; body.opportunity_min_gain_pct_step = oppGainRange.step; }
        else { body.opportunity_min_gain_pct = oppGainFixed; }
      }
      body.swap_defer_enabled = swapDeferEnabled;
      body.swap_defer_off_enabled = swapDeferOffEnabled;
      body.swap_tp_frac_enabled = swapTpEnabled;
      body.swap_tp_frac_off_enabled = swapTpOffEnabled;
      if (swapTpEnabled) {
        if (swapTpMode === 'range') { body.swap_tp_frac_pct_from = swapTpRange.from; body.swap_tp_frac_pct_to = swapTpRange.to; body.swap_tp_frac_pct_step = swapTpRange.step; }
        else { body.swap_tp_frac_pct = swapTpFixed; }
      }
      body.swap_htc_enabled = swapHtcEnabled && htcEnabled;
      body.swap_htc_off_enabled = swapHtcOffEnabled;
      body.hold_to_cost_enabled = htcEnabled;
      body.hold_to_cost_off_enabled = htcOffEnabled;
      // Поріг беззбитковості діє лише разом із HTC — без нього поле не шлемо,
      // щоб рядки без HTC не несли «увімкнено» ні до чого.
      if (htcEnabled && htcCostEnabled) {
        body.htc_cost_enabled = true;
        const mk = Number(htcCostMarkup);
        body.htc_cost_markup_pct = Number.isFinite(mk) && mk >= 0 ? mk : 0.1;
      }
      // Buy% на виході (правило «не виріс», менеджер 29.10). Хоч один варіант
      // завжди є — форма не дає зняти обидва.
      body.no_growth_enabled = noGrowthOn || !noGrowthOff;
      body.no_growth_off_enabled = noGrowthOff;
      if (htcEnabled) {
        if (htcBarsMode === 'range') { body.hold_to_cost_bars_from = htcBarsRange.from; body.hold_to_cost_bars_to = htcBarsRange.to; body.hold_to_cost_bars_step = htcBarsRange.step; }
        else { body.hold_to_cost_bars = htcBarsFixed; }
      }
      body.take_profit_enabled = tpAnyEnabled;
      if (tpAnyEnabled) {
        const tpModes = [];
        if (tpImmediateEnabled) tpModes.push(1);
        if (tpLetItRunEnabled)  tpModes.push(2);
        body.take_profit_modes = tpModes;
        if (tpPctMode === 'range') { body.take_profit_pct_from = tpPctRange.from; body.take_profit_pct_to = tpPctRange.to; body.take_profit_pct_step = tpPctRange.step; }
        else { body.take_profit_pct = tpPctFixed; }
        if (tpExpiryMode === 'range') { body.take_profit_expiry_bars_from = tpExpiryRange.from; body.take_profit_expiry_bars_to = tpExpiryRange.to; body.take_profit_expiry_bars_step = tpExpiryRange.step; }
        else { body.take_profit_expiry_bars = tpExpiryFixed; }
      }
      body.pit_universe_enabled = pitEnabled;
      if (pitEnabled) body.pit_require_data = pitRequireData;
      body.delisting_enabled = delistEnabled;
      body.reorg_enabled = reorgEnabled;
      body.per_bar_universe = perBarUniverse;
      body.slicing_enabled = sliceEnabled;
      body.dynamic_n_enabled = dynNEnabled;
      body.partial_sells_enabled = partialSells;
      if (dynNEnabled) body.dynamic_n_mode = Number(dynNMode);
      if (sliceEnabled) {
        body.min_position_usd = Number(sliceCfg.minPositionUsd);
        body.min_share_price = Number(sliceCfg.minSharePrice);
      }
      body.liquidity_enabled = liqEnabled;
      if (liqEnabled) {
        body.min_adv_usd          = Number(liqCfg.minAdvUsd);
        body.max_flat_bar_pct     = Number(liqCfg.maxFlatBarPct);
        body.position_adv_cap_pct = Number(liqCfg.positionAdvCapPct);
        body.adv_window_days      = Number(liqCfg.advWindowDays);
        body.impact_enabled       = impactEnabled;
        body.impact_bps_per_pct   = Number(liqCfg.impactBpsPerPct);
        // Вісь порога обігу. Без прапорця поля не надсилаються взагалі —
        // бекенд тоді бачить рівно те саме тіло, що й до цієї фічі.
        if (minAdvRange.enabled) {
          body.min_adv_usd_from = Number(minAdvRange.from);
          body.min_adv_usd_to   = Number(minAdvRange.to);
          body.min_adv_usd_step = Number(minAdvRange.step);
        }
      }
      // Пріоритет черги купівлі: «обіг»/«сплеск» читають дані блоку
      // ліквідності, тож без нього лишається лише глибина падіння. Бекенд
      // таку комбінацію відхиляє — не надсилаємо її й звідси.
      body.buy_priority_options = liqEnabled && buyPriorities.length
        ? buyPriorities : ['dip'];
      // Набори капіталізації: перший — той, що у блоці всесвіту, далі додані.
      // Порожньо → поле не надсилається взагалі, поведінка як була.
      if (capBucketSets.length) {
        body.market_cap_bucket_sets = [capBucketsPayload(form), ...capBucketSets];
      }
      body.margin_enabled = marginEnabled;
      if (marginEnabled) {
        body.margin_cap              = Number(marginCfg.cap);
        body.margin_maintenance_pct  = Number(marginCfg.maintPct);
        body.margin_delev_start_pct  = Number(marginCfg.delevStartPct);
        body.margin_delev_stop_pct   = Number(marginCfg.delevStopPct);
        body.margin_interest_apr_pct = Number(marginCfg.interestAprPct);
        body.margin_min_price        = Number(marginCfg.minPrice);
        if (marginCapRange.enabled) {
          body.margin_cap_from = Number(marginCapRange.from);
          body.margin_cap_to   = Number(marginCapRange.to);
          body.margin_cap_step = Number(marginCapRange.step);
        }
      }
      body.save_top_n = saveTopN === 'all' ? 0 : parseInt(saveTopN, 10);
      body.min_return_pct = minReturnPct !== '' ? parseFloat(minReturnPct) : null;
      body.cull_negative_periods = !!cullNegativePeriods;
      if (cullNegativePeriods) {
        body.cull_floor_pct = cullFloorPct !== '' ? parseFloat(cullFloorPct) : 0;
        body.cull_loss_tolerance_pct = cullLossTolPct !== '' ? parseFloat(cullLossTolPct) : 0;
      }
      body.two_stage_enabled = !!twoStageActive;
      body.smart_enabled = !!smartActive;
      if (smartActive) {
        body.smart_budget      = smartBudgetN;
        body.smart_gen_size    = parseInt(smartGenSize, 10) || 1000;
        body.smart_explore_pct = parseFloat(smartExplorePct) || 0;
        body.smart_metric      = smartMetric;
        body.smart_top_k       = parseInt(smartTopK, 10) || 20;
      }
      if (twoStageActive) {
        body.two_stage_periods = parseInt(twoStagePeriods, 10);
        body.two_stage_top_n   = parseInt(twoStageTopN, 10);
      }
    } else {
      body.save_top_n = saveTopN === 'all' ? 0 : parseInt(saveTopN, 10);
      body.min_return_pct = minReturnPct !== '' ? parseFloat(minReturnPct) : null;
    }
    return body;
  }

  async function openBlockPanel() {
    const next = !blockOpen;
    setBlockOpen(next);
    if (!next) return;
    try {
      const res = await getAnalyzerBlocks();
      setBlocks(res.data.blocks || []);
      setBlockAxesDefs(res.data.axes || []);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  }

  // Лічильник перебору по блоку: те саме тіло, що піде на запуск, сервер нічого
  // не пише. Форма міняється без відома панелі, тож число — на момент натискання.
  async function runBlockPreview() {
    if (!blockBatchId || blockAxes.length === 0) return;
    setBlockPreviewing(true);
    try {
      const body = buildRunBody();
      body.block_batch_id = blockBatchId;
      body.block_axes = blockAxes;
      const res = await previewAnalyzerBlock(body);
      setBlockPreview(res.data);
    } catch (e) {
      setBlockPreview({ error: e.response?.data?.detail || e.message });
    } finally {
      setBlockPreviewing(false);
    }
  }

  function toggleBlockAxis(key) {
    setBlockPreview(null);
    setBlockAxes(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  // block = { batchId, axes } → перебір по готовому блоку замість діапазонів форми.
  async function handleRun(block = null) {
    setLoading(true);
    setRunningBatch(null);
    try {
      const body = buildRunBody();
      let res;
      if (strategyTypeRef.current === 'advanced') {
        if (block) {
          body.block_batch_id = block.batchId;
          body.block_axes = block.axes;
        }
        res = await runAnalyzerAdvanced(body);
      } else {
        res = await runAnalyzer(body);
      }
      const batchId = res.data.batch_id;
      const count   = res.data.count || 1;
      setRunningBatch({ batch_id: batchId, count });
      if (strategyTypeRef.current !== 'advanced') setBatchFilter(batchId);
      setPage(0);
      if (strategyTypeRef.current !== 'advanced') loadBatches();

      // Auto-save batch filter
      try {
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const min = String(now.getMinutes()).padStart(2, '0');
        const fmtDate = d => { if (!d) return '?'; const [y, m, day] = d.split('-'); return `${day}.${m}.${String(y).slice(2)}`; }; // "2025-10-01" → "01.10.25"
        // Перебір по блоку рахується у вікні блоку, а не форми.
        const winStart = block && blockPreview?.start_time ? blockPreview.start_time : body.start_time;
        const winStop  = block && blockPreview?.stop_time  ? blockPreview.stop_time  : body.stop_time;
        const autoName = `Створено ${dd}.${mm} ${hh}:${min} [${count}] ${fmtDate(winStart)}–${fmtDate(winStop)}${block ? ' ← блок' : ''}`;
        await saveAnalyzerSearch({
          name: autoName,
          params: {
            _type: 'batch',
            batch_id: batchId,
            count,
            start: winStart,
            end: winStop,
            // Знімок форми, з якою сітку запустили: кнопка копіювання на
            // плашці повертає саме ці параметри пошуку, а не поточні.
            ui: buildFormSnapshot(),
            ...(runComment.trim() ? { comment: runComment.trim() } : {}),
          },
        });
        setRunComment('');
        loadSavedSearches();
      } catch (_) {}

      startPolling(batchId);
    } catch (e) {
      setLoading(false);
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  }

  function handleReset() {
    setForm(DEFAULT_FORM);
    setDataType('hourly');
    setFixed(DEFAULT_FIXED);
    setParamMode({ xBuy: 'fixed', tHoursBuy: 'fixed', ySell: 'fixed', posSize: 'fixed' });
    setNumCompanies(20);
    setSellWindowDays(3);
    setSellWindowMode('same');
    setSellWindowRange(DEFAULT_SELL_WINDOW_RANGE);
    setSearchName('');
    setSelectedStrategy('mr_deep');
    setParallelSplit(DEFAULT_PARALLEL.split);
    setMomentumParams(DEFAULT_PARALLEL.momentum);
    setMrParams(DEFAULT_PARALLEL.mr);
    setRiskProt({ enabled: false, sell_overnight: true, sell_weekend: true, max_loss_to_sell_pct: 0.5 });
    setSellMode('rolling');
    setStagnation({ enabled: false, min_pct: -0.5, max_pct: 0.5, bars: 24 });
    setIntraday({ enabled: false, pct: 3.0 });
    setAdvancedOpen(false);
    setReinvest(true);
    setTickerMode('all');
    setSelectedTickers([]);
    setTickerSearch('');
  }

  async function handleSaveSearch() {
    if (!searchName.trim()) { alert('Enter a name first'); return; }
    try {
      await saveAnalyzerSearch({ name: searchName.trim(), params: { ...form, dataType, fixed, paramMode, rangeP, numCompanies, sellWindowDays, sellWindowMode, sellWindowRange, selectedStrategy, engine, parallelSplit, momentumParams, mrParams, sellMode, stagnation, intraday, reinvest, tickerMode, selectedTickers, additionalPeriods } });
      loadSavedSearches();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  }

  function handleLoadSearch(s) {
    const p = s.params;

    // Clicking the active tag again → deactivate. Тег сітки знімає лише сітку:
    // фільтри користувача (Periods, Avg. Yr% …) лишаються — їх виставляють,
    // щоб переглядати сітки одну за одною. Тег збережених фільтрів знімає їх.
    if (activeSearchId === s.id) {
      setActiveSearchId(null);
      setPage(0);
      setTotal(0);
      batchFilterRef.current = '';
      setBatchFilter('');
      if (p._type !== 'batch') setFiltersDirect([]);
      setQuickIdSearch('');
      quickIdRef.current = '';
      return;
    }

    // Batch filter tag → filter results table by that batch; clear the quick id.
    // Фільтри НЕ чіпаються: користувач перемикає сітки з тими самими умовами
    // (напр. Periods ≥ 1000). Незмінні appliedFilters ще й не запускають
    // useEffect([appliedFilters]) — лишається єдиний loadResults від
    // useEffect([batchFilter]), refs оновлюються синхронно нижче.
    if (p._type === 'batch') {
      setPage(0);
      setTotal(0);
      setActiveSearchId(s.id);
      setShowFavoritesOnly(false);
      showFavoritesRef.current = false;
      setQuickIdSearch('');
      quickIdRef.current = '';
      batchFilterRef.current = p.batch_id || '';
      setBatchFilter(p.batch_id || '');
      return;
    }

    // Applied-filters tag → restore filter chips; clear everything else first.
    // batchFilterRef updated synchronously so buildFilterParams sees '' immediately.
    if (p._type === 'filter') {
      const restored = p.filters || [];
      setPage(0);
      setTotal(0);
      setActiveSearchId(s.id);
      setShowFavoritesOnly(false);
      showFavoritesRef.current = false;
      batchFilterRef.current = '';
      setBatchFilter('');
      setQuickIdSearch('');
      quickIdRef.current = '';
      setFiltersDirect(restored);
      // useEffect([appliedFilters]) will fire loadResults(0) after render,
      // at which point filtersRef.current is already updated by the hook (line 43)
      return;
    }

    // Form-type search — clear all filters/batch so only the form params apply
    setActiveSearchId(null);
    setPage(0);
    setTotal(0);
    batchFilterRef.current = '';
    resetFilters();
    setBatchFilter('');
    setQuickIdSearch('');
    quickIdRef.current = '';
    setForm({ ...DEFAULT_FORM, ...(p.form || p) });
    if (p.dataType) setDataType(p.dataType);
    if (p.fixed) setFixed(p.fixed);
    if (p.paramMode) setParamMode(p.paramMode);
    if (p.rangeP) setRangeP(p.rangeP);
    if (p.numCompanies != null) setNumCompanies(typeof p.numCompanies === 'number' ? p.numCompanies : (Array.isArray(p.numCompanies) ? (p.numCompanies[0] || 20) : 20));
    if (p.sellWindowDays != null) setSellWindowDays(p.sellWindowDays);
    if (p.sellWindowMode) setSellWindowMode(p.sellWindowMode);
    if (p.sellWindowRange) setSellWindowRange(p.sellWindowRange);
    if (p.selectedStrategy) setSelectedStrategy(p.selectedStrategy);
    if (p.engine) setEngine(p.engine);
    if (p.parallelSplit != null) setParallelSplit(p.parallelSplit);
    if (p.momentumParams) setMomentumParams(p.momentumParams);
    if (p.mrParams) setMrParams(p.mrParams);
    if (p.sellMode) setSellMode(p.sellMode);
    if (p.stagnation) setStagnation(p.stagnation);
    if (p.intraday)   setIntraday(p.intraday);
    if (p.reinvest != null) setReinvest(p.reinvest);
    if (p.tickerMode) setTickerMode(p.tickerMode);
    if (p.selectedTickers?.length > 0) setSelectedTickers(p.selectedTickers);
    else if (p.specific_tickers?.length > 0) { setTickerMode('specific'); setSelectedTickers(p.specific_tickers); }
    // Restore additional periods saved with the named search (they were previously
    // dropped from the payload → periods silently lost on apply/refresh).
    if (Array.isArray(p.additionalPeriods)) setAdditionalPeriods(p.additionalPeriods);
    setSearchName(s.name);
  }

  async function handleDeleteSearch(id, e) {
    e.stopPropagation();
    if (!window.confirm('Видалити збережений фільтр?')) return;
    try { await deleteAnalyzerSearch(id); loadSavedSearches(); } catch (_) {}
  }

  async function handleSaveFilterTag() {
    if (appliedFilters.length === 0) { alert('Немає активних фільтрів для збереження'); return; }
    const name = window.prompt('Назва фільтру:', '');
    if (!name || !name.trim()) return;
    try {
      await saveAnalyzerSearch({
        name: name.trim(),
        params: { _type: 'filter', filters: appliedFilters },
      });
      loadSavedSearches();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  }

  async function handleDelete(id) {
    try { await deleteAnalyzerResult(id); loadResults(page); }
    catch (e) { if (e.response?.status === 409) alert(e.response.data?.detail || 'Рядок у favorites — спершу зніміть зірочку'); }
  }

  // ── Перевірка відзначених рядків на новому наборі вікон ──────────────────
  // Рахується звичайною сіткою: скільки рядків відзначено — стільки комбінацій,
  // тобто стільки лізів, і роботу бере весь парк. Вікна після завершення
  // дописуються в самі рядки, а тимчасова пачка прибирається — тож у таблиці не
  // зʼявляється копій лідерів.
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckMode, setRecheckMode] = useState('random');
  const [recheckBusy, setRecheckBusy] = useState(false);
  const [recheckMsg,  setRecheckMsg]  = useState('');
  const [recheckErr,  setRecheckErr]  = useState('');

  const handleRecheckSelected = useCallback(async () => {
    if (checkedIds.size === 0) return;
    setRecheckBusy(true); setRecheckErr(''); setRecheckMsg('');
    try {
      const res = await recheckSelectedRows({
        row_ids:            [...checkedIds],
        period_months:      Number(genCfg.months),
        n_periods:          Number(genCfg.count),
        extra_days_enabled: !!genCfg.extraEnabled,
        extra_days_from:    Number(genCfg.extraFrom) || 0,
        extra_days_to:      Number(genCfg.extraTo) || 0,
        // Перебір усіх точок входу — те, чим кандидат зі 100 вікон
        // перетворюється на перевіреного. Сервер сам виключить ті точки, які
        // в рядках уже пораховані, тож дублів не буде.
        mode:               recheckMode === 'sweep' ? 'sweep' : 'random',
      });
      const d = res.data || {};
      const bits = [`${d.rows} рядків × ${d.windows} вікон по ${d.period_months} міс.`];
      if (d.clamped_end) bits.push(`праву межу обрізано даними до ${d.clamped_end}`);
      bits.push(`зерно ${d.seed}`);
      setRecheckMsg(`Запущено: ${bits.join(' · ')}. Вікна допишуться в рядки, `
                  + 'коли пачка добіжить — онови таблицю.');
      setRecheckOpen(false);
    } catch (e) {
      setRecheckErr(e?.response?.data?.detail || e.message || 'Не вдалось запустити');
    } finally {
      setRecheckBusy(false);
    }
  }, [checkedIds, genCfg]);

  async function handleDeleteSelected() {
    if (checkedIds.size === 0) return;
    if (!window.confirm(`Видалити ${checkedIds.size} обраних стратегій?`)) return;
    const ids = [...checkedIds];
    try {
      await deleteAnalyzerBulk(ids);
      setCheckedIds(new Set());
      loadResults(page);
    } catch (_) {}
  }

  async function handleDeleteFiltered() {
    if (!window.confirm(`Видалити всі ${total.toLocaleString()} записів що відповідають поточному фільтру?`)) return;
    const params = buildFilterParams();
    setDeletingFiltered(true);
    try {
      const res = await deleteAnalyzerFiltered(params);
      setCheckedIds(new Set());
      loadResults(0);
      if (res.data?.deleted != null) alert(`Видалено: ${res.data.deleted.toLocaleString()} записів`);
    } catch (_) {
    } finally {
      setDeletingFiltered(false);
    }
  }

  async function handleDeleteBatch(batchId, e) {
    e.stopPropagation();
    if (!window.confirm('Видалити всю групу комбінацій?')) return;
    try {
      const r = await deleteAnalyzerBatch(batchId);
      if (r.data?.kept_saved) alert(`Залишено ${r.data.kept_saved} рядків із ★ favorites — зірочка захищає від видалення.`);
      loadResults(page); loadBatches();
    } catch (_) {}
  }

  // Poll running bot names for "▶ Mock" button/column state (light endpoint)
  useEffect(() => {
    let active = true;
    async function pollRunning() {
      try {
        const res = await getRunningNames();
        if (!active) return;
        setRunningBotNames(res.data?.names || []);
      } catch (_) {}
    }
    pollRunning();
    const id = setInterval(pollRunning, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  async function handleRunMock(r, alreadyRunning) {
    if (alreadyRunning) {
      // Navigate to /running page
      window.dispatchEvent(new CustomEvent('goto:running'));
      return;
    }
    setRunMockStatus('starting');
    const label = r.name || `Strategy #${r.id}`;
    const capital = r.params?.initial_capital || 100000;
    try {
      await addStrategyToBot({
        strategy_name:  r.params?.strategy || 'meanreversion',
        strategy_label: label,
        capital:        parseFloat(capital),
        capital_pct:    100.0,
        params:         r.params || {},
        metrics:        r.metrics || {},
        score:          r.score || 0,
      });
      setRunningBotNames(prev => [...prev.filter(n => n !== label), label]);
      setRunMockStatus('done');
      setTimeout(() => setRunMockStatus(null), 3000);
      window.dispatchEvent(new CustomEvent('goto:running'));
    } catch (err) {
      console.error('handleRunMock error:', err);
      setRunMockStatus('error');
      setTimeout(() => setRunMockStatus(null), 4000);
    }
  }

  // Плашка сітки → форма пошуку: ті самі діапазони, прапорці й вікна, з якими
  // сітку запускали. Рядок таблиці («копіювати у форму») дає ОДНУ комбінацію;
  // тут — увесь пошук.
  function openCommentEdit(s, e) {
    e.stopPropagation();
    setCommentEdit({ id: s.id, name: s.name, text: s.params?.comment || '', saving: false });
  }

  async function saveCommentEdit() {
    if (!commentEdit) return;
    setCommentEdit(c => ({ ...c, saving: true }));
    try {
      await updateAnalyzerSearchComment(commentEdit.id, commentEdit.text);
      setCommentEdit(null);
      loadSavedSearches();
    } catch (e) {
      setCommentEdit(c => c && { ...c, saving: false });
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  }

  function handleCopySearchToForm(s, e) {
    e.stopPropagation();
    const ui = s.params?.ui;
    if (!ui) {
      alert('Для цієї сітки параметри пошуку не збережено: знімок форми '
            + 'зберігається лише для сіток, запущених після 25.09.');
      return;
    }
    applyFormSnapshot(ui, { withPresets: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCopyToForm(r) {
    const p = r.params || {};
    // Scroll to top of page to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // ── Basic form fields ──────────────────────────────────────────────────────
    setForm(f => ({
      ...f,
      startTime:      p.start_time     || f.startTime,
      stopTime:       p.stop_time      || f.stopTime,
      country:        p.country        || f.country,
      sector:         p.sector         || f.sector,
      // index/cap buckets restored too, so "копіювати у форму" reproduces the
      // universe the row was actually run on. Absent on rows saved before the
      // feature → keep the current form value (buckets fall back to min/max).
      index:          Array.isArray(p.index) ? p.index : f.index,
      marketCapMin:   p.market_cap_min  != null ? p.market_cap_min  : f.marketCapMin,
      marketCapMax:   p.market_cap_max  != null ? p.market_cap_max  : f.marketCapMax,
      marketCapBuckets: Array.isArray(p.market_cap_buckets)
        ? canonCapBuckets(p.market_cap_buckets)
        : f.marketCapBuckets,
      capCustom:      Array.isArray(p.market_cap_buckets)
        ? p.market_cap_buckets.some(x => String(x).toLowerCase() === 'custom')
        : f.capCustom,
      betaMin:        p.beta_min        != null ? p.beta_min        : f.betaMin,
      betaMax:        p.beta_max        != null ? p.beta_max        : f.betaMax,
      minAvgVolume:   p.min_avg_volume  != null ? p.min_avg_volume  : f.minAvgVolume,
      initialCapital: p.initial_capital != null ? p.initial_capital : f.initialCapital,
    }));

    // ── Data type ──────────────────────────────────────────────────────────────
    if (p.data_type) {
      setDataType(p.data_type);
      if (p.data_type === 'daily') {
        setRangeP(prev => ({ ...prev, tHoursBuy: { from: 1, to: 14, step: 7 } }));
      } else {
        setRangeP(prev => ({ ...prev, tHoursBuy: { from: 1, to: 72, step: 6 } }));
      }
    }

    // ── Strategy ───────────────────────────────────────────────────────────────
    if (p.strategy) setSelectedStrategy(p.strategy);

    // ── Core params — always set to fixed mode with copied values ─────────────
    setParamMode({ xBuy: 'fixed', tHoursBuy: 'fixed', ySell: 'fixed', posSize: 'fixed' });
    setFixed(prev => ({
      ...prev,
      xBuy:      p.x_buy       != null ? p.x_buy       : prev.xBuy,
      tHoursBuy: p.t_hours_buy != null ? p.t_hours_buy  : prev.tHoursBuy,
      ySell:     p.y_sell      != null ? p.y_sell       : prev.ySell,
      posSize:   (p.pos_size ?? p.position_size_pct) != null ? (p.pos_size ?? p.position_size_pct) : prev.posSize,
    }));

    // ── Sell window ────────────────────────────────────────────────────────────
    if (p.sell_window_days != null) {
      setSellWindowMode('fixed');
      setSellWindowDays(p.sell_window_days);
    } else {
      setSellWindowMode('same');
    }

    // ── Num companies ──────────────────────────────────────────────────────────
    if (p.num_companies != null) setNumCompanies(p.num_companies);

    // ── Sell mode ──────────────────────────────────────────────────────────────
    setSellMode(p.sell_mode || 'rolling');

    // ── Buy% на виході — відсутнє поле = правило діє (рядки до 29.10) ─────────
    {
      const ng = p.no_growth_enabled == null ? true
        : !(p.no_growth_enabled === false || p.no_growth_enabled === 'false');
      setNoGrowthOn(ng);
      setNoGrowthOff(!ng);
    }

    // ── Stagnation — always reset, enable only if present in params ───────────
    setStagnation(prev => ({
      enabled: p.stagnation_enabled ?? false,
      min_pct: p.stagnation_min_pct_range
        ? { mode: 'range', value: prev.min_pct?.value ?? -0.5, ...p.stagnation_min_pct_range }
        : { mode: 'single', value: p.stagnation_min_pct ?? -0.5, min: -2, max: -0.5, step: 0.5 },
      max_pct: p.stagnation_max_pct_range
        ? { mode: 'range', value: prev.max_pct?.value ?? 0.5, ...p.stagnation_max_pct_range }
        : { mode: 'single', value: p.stagnation_max_pct ?? 0.5, min: 0.5, max: 2, step: 0.5 },
      bars: p.stagnation_bars_range
        ? { mode: 'range', value: prev.bars?.value ?? 30, ...p.stagnation_bars_range }
        : { mode: 'single', value: p.stagnation_bars ?? 30, min: 20, max: 60, step: 10 },
    }));

    // ── Intraday signal — always reset ────────────────────────────────────────
    setIntraday({ enabled: p.intraday_enabled ?? false, pct: p.intraday_pct ?? 3.0 });

    // ── Risk Protection — always reset ────────────────────────────────────────
    setRiskProt(p.risk_protection ?? {
      enabled: false,
      sell_overnight: true,
      sell_weekend: true,
      max_loss_to_sell_pct: 0.5,
    });

    // ── Reinvest — always reset ───────────────────────────────────────────────
    setReinvest(p.reinvest ?? true);

    // ── Peak window — always reset ────────────────────────────────────────────
    setUsePeakWindowBuy(p.use_peak_window_buy   ? !!p.use_peak_window_buy  : false);
    setUsePeakWindowSell(p.use_peak_window_sell ? !!p.use_peak_window_sell : false);

    // ── Hard stop — always reset ──────────────────────────────────────────────
    setHardStopPct(p.hard_stop_pct ?? 0);
    setHardStopCooldownBars(p.hard_stop_cooldown_bars ?? 0);

    // ── Regime filter — always reset ──────────────────────────────────────────
    setRegimeFilter(Array.isArray(p.regime_filter) ? p.regime_filter : []);

    // ── Buy Cap — always reset ────────────────────────────────────────────────
    setBuyCapEnabled(p.buy_cap_enabled ?? false);
    setBuyCapMode('fixed');
    setBuyCapFixed(p.buy_cap_pct ?? 10.0);

    // ── Overpriced Filter — always reset ──────────────────────────────────────
    setOverpricedEnabled(p.overpriced_filter_enabled ?? false);
    setOvpPctMode('fixed');
    setOvpPctFixed(p.overpriced_pct ?? 5.0);
    setOvpPerMode('fixed');
    setOvpPerFixed(p.overpriced_period_bars ?? 48);

    // ── Dip Filter — always reset ─────────────────────────────────────────────
    setDipEnabled(p.dip_filter_enabled ?? false);
    setDipPctMode('fixed');
    setDipPctFixed(p.dip_pct ?? 5.0);
    setDipPerMode('fixed');
    setDipPerFixed(p.dip_period_bars ?? 24);

    // ── Rotation — always reset ───────────────────────────────────────────────
    setRotEnabled(p.rotation_enabled ?? false);
    setRotHoldMode('fixed');
    setRotHoldFixed(p.rotation_min_hold_bars ?? 3);
    setRotFactorMode('fixed');
    setRotFactorFixed(p.rotation_factor ?? 2.0);

    // ── Opportunity — always reset ────────────────────────────────────────────
    setOppEnabled(p.opportunity_enabled ?? false);
    setOppHoldMode('fixed');
    setOppHoldFixed(p.opportunity_min_hold_bars ?? 1);
    setOppGainMode('fixed');
    setOppGainFixed(p.opportunity_min_gain_pct ?? 2.0);

    // ── Продаж під покупку — always reset ────────────────────────────────────
    setSwapDeferEnabled(!!p.swap_defer_enabled);
    setSwapDeferOffEnabled(!p.swap_defer_enabled);
    setSwapTpEnabled(Number(p.swap_tp_frac_pct || 0) > 0);
    setSwapTpOffEnabled(!(Number(p.swap_tp_frac_pct || 0) > 0));
    setSwapTpMode('fixed');
    setSwapTpFixed(Number(p.swap_tp_frac_pct || 0) > 0 ? Number(p.swap_tp_frac_pct) : 80);

    setSwapHtcEnabled(!!p.swap_htc_enabled);
    setSwapHtcOffEnabled(!p.swap_htc_enabled);

    // ── Hold to Cost — always reset ───────────────────────────────────────────
    setHtcEnabled(p.hold_to_cost_enabled ?? false);
    // Відсутнє поле = поріг від ціни купівлі (рядки до 02.11).
    {
      const on = p.htc_cost_enabled === true || p.htc_cost_enabled === 'true';
      setHtcCostEnabled(on);
      const mk = Number(p.htc_cost_markup_pct);
      setHtcCostMarkup(on && Number.isFinite(mk) ? mk : 0.1);
    }
    setHtcBarsMode('fixed');
    setHtcBarsFixed(p.hold_to_cost_bars ?? 48);

    // ── Take Profit — always reset ────────────────────────────────────────────
    const _tpModeN = p.take_profit_mode != null ? Number(p.take_profit_mode) : (p.take_profit_enabled ? 1 : 0);
    setTpImmediateEnabled(_tpModeN === 1);
    setTpLetItRunEnabled(_tpModeN === 2);
    setTpPctMode('fixed');
    setTpPctFixed(p.take_profit_pct ?? 10.0);
    setTpExpiryMode('fixed');
    setTpExpiryFixed(p.take_profit_expiry_bars ?? 0);

    // ── Margin 2:1 — always reset ─────────────────────────────────────────────
    setMarginEnabled(!!p.margin_enabled);
    setPitEnabled(!!p.pit_universe_enabled);
    setDelistEnabled(!!p.delisting_enabled);
    setReorgEnabled(!!p.reorg_enabled);
    if (p.pit_require_data != null) setPitRequireData(!!p.pit_require_data);
    setLiqEnabled(!!p.liquidity_enabled);
    setImpactEnabled(!!p.impact_enabled);
    // Рядок результату — це ОДНА комбінація, тож у ньому один пріоритет і один
    // набір капіталізації. Вісь наборів згортається: копія у форму дає рівно ту
    // комбінацію, що дала цей рядок, а не всю сітку, з якої він походить.
    setBuyPriorities([p.buy_priority || 'dip']);
    setCapBucketSets([]);
    setLiqCfg(prev => ({
      ...prev,
      ...(p.min_adv_usd          != null ? { minAdvUsd:         Number(p.min_adv_usd) }          : {}),
      ...(p.max_flat_bar_pct     != null ? { maxFlatBarPct:     Number(p.max_flat_bar_pct) }     : {}),
      ...(p.position_adv_cap_pct != null ? { positionAdvCapPct: Number(p.position_adv_cap_pct) } : {}),
      ...(p.adv_window_days      != null ? { advWindowDays:     Number(p.adv_window_days) }      : {}),
      ...(p.impact_bps_per_pct   != null ? { impactBpsPerPct:   Number(p.impact_bps_per_pct) }   : {}),
    }));
    setMarginCfg(prev => ({
      ...prev,
      cap:            p.margin_cap ?? 2.0,
      maintPct:       p.margin_maintenance_pct ?? 25,
      delevStartPct:  p.margin_delev_start_pct ?? 30,
      delevStopPct:   p.margin_delev_stop_pct ?? 35,
      interestAprPct: p.margin_interest_apr_pct ?? 6,
      minPrice:       p.margin_min_price ?? 5,
    }));

    // ── Tickers — always reset ────────────────────────────────────────────────
    if (p.specific_tickers?.length > 0) {
      setTickerMode('specific');
      setSelectedTickers(p.specific_tickers);
    } else {
      setTickerMode('all');
      setSelectedTickers([]);
    }

    // Open Advanced Settings so user can see copied values
    setAdvancedOpen(true);
  }

  async function handleRecalcOne(id) {
    setRecalcingId(id);
    try {
      const res = await recalculateAnalyzerResult(id);
      // If ARQ worker is available the job runs async — poll batch progress endpoint
      if (res.data?.mode === 'arq') {
        let seenActive = false;
        await new Promise(resolve => {
          const poll = setInterval(async () => {
            try {
              const prog = await getRecalculateBatchProgress();
              const info = prog.data;
              if (info.status === 'running' || info.status === 'loading') seenActive = true;
              const isDone = info.status === 'completed' || info.status === 'failed'
                || (info.status === 'idle' && seenActive);
              if (isDone) { clearInterval(poll); resolve(); }
            } catch (_) { clearInterval(poll); resolve(); }
          }, 800);
        });
      }
      await loadResults(page);
    } catch (_) {}
    setRecalcingId(null);
  }

  async function handleRecalcAll() {
    setRecalcConfirm(false);
    try {
      await recalculateAnalyzerAll();
    } catch (_) {}
    // Start polling progress
    recalcPollRef.current = setInterval(async () => {
      try {
        const res = await getRecalculateProgress();
        const info = res.data;
        setRecalcProgress(info);
        if (info.status === 'completed' || info.status === 'failed' || info.status === 'cancelled') {
          clearInterval(recalcPollRef.current);
          recalcPollRef.current = null;
          await loadResults(page);
          setTimeout(() => setRecalcProgress(null), 3000);
        }
      } catch (_) {}
    }, 800);
  }

  async function handleViewDetail(row) {
    try {
      const res = await getAnalyzerResult(row.id);
      setSelectedData(res.data);
      setSelectedId(row.id);
      if (navTo) navTo(`/analyzer/${row.id}`);
    } catch (_) {}
  }

  function handlePageChange(dir) {
    if (pageSize === 'all') return;
    const ps = Number(pageSize);
    const newPage = page + dir;
    if (newPage < 0 || newPage * ps >= total) return;
    setPage(newPage);
    loadResults(newPage);
  }

  function handlePageSizeChange(val) {
    setPageSize(val);
    pageSizeRef.current = val;
    setPage(0);
    setResults([]);   // clear so skeleton shows while loading
    loadResults(0, undefined, undefined, val);
  }

  function handleGoToPage(pg) {
    if (pageSize === 'all') return;
    const ps = Number(pageSize);
    if (pg < 0 || pg * ps >= total) return;
    setPage(pg);
    loadResults(pg);
  }

  // ── Sorting ───────────────────────────────────────────────────────────────────
  function handleSort(col) {
    const newDir = sortCol === col ? -sortDir : -1;
    setSortCol(col);
    setSortDir(newDir);
    sortRef.current = { col, dir: newDir };
    setPage(0);
    loadResults(0, col, newDir);
  }

  // ── Checkboxes ────────────────────────────────────────────────────────────────
  function handleToggleCheck(id) {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    const visible = displayResults;
    const allChecked = visible.length > 0 && visible.every(r => checkedIds.has(r.id));
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(visible.map(r => r.id)));
    }
  }

  async function handleRecalcSelected() {
    if (checkedIds.size === 0) return;
    const ids = [...checkedIds];
    setRecalcSelectedProgress({ current: 0, total: ids.length, status: 'loading' });
    try {
      await recalculateAnalyzerBatch(ids);
    } catch (_) {}
    // Poll progress from ARQ worker
    // seenActive: wait for job to actually start before treating 'idle' as terminal
    if (recalcSelectedPollRef.current) clearInterval(recalcSelectedPollRef.current);
    let seenActive = false;
    recalcSelectedPollRef.current = setInterval(async () => {
      try {
        const res = await getRecalculateBatchProgress();
        const info = res.data;
        if (info.status === 'running' || info.status === 'loading') seenActive = true;
        if (info.total > 0) setRecalcSelectedProgress(info);
        const isDone = info.status === 'completed' || info.status === 'failed'
          || (info.status === 'idle' && seenActive);
        if (isDone) {
          clearInterval(recalcSelectedPollRef.current);
          recalcSelectedPollRef.current = null;
          setCheckedIds(new Set());
          await loadResults(page);
          setTimeout(() => setRecalcSelectedProgress(null), 2000);
        }
      } catch (_) {}
    }, 800);
  }

  // ── Stars / Favorites ─────────────────────────────────────────────────────────
  // Persisted server-side via analyzer_results.is_saved so backend cleanups
  // (_apply_top_n_postprocess, cleanup_old_results) skip starred rows.
  async function handleToggleStar(id) {
    const wasStarred = starredIdsRef.current.has(id);
    // Optimistic UI update
    setStarredIds(prev => {
      const next = new Set(prev);
      wasStarred ? next.delete(id) : next.add(id);
      return next;
    });
    setResults(prev => prev.map(r => r.id === id ? { ...r, is_saved: !wasStarred } : r));
    try {
      if (wasStarred) await unsaveAnalyzerResult(id);
      else            await saveAnalyzerResult(id);
    } catch (err) {
      console.error('[handleToggleStar] failed, rolling back:', err);
      setStarredIds(prev => {
        const next = new Set(prev);
        wasStarred ? next.add(id) : next.delete(id);
        return next;
      });
      setResults(prev => prev.map(r => r.id === id ? { ...r, is_saved: wasStarred } : r));
    }
  }

  async function handlePushToTrading(resultId) {
    setPushStatus(s => ({ ...s, [resultId]: 'loading' }));
    try {
      const res = await pushResultToTrading(resultId, pushTarget);
      const already = res.data?.already_exists;
      const where = res.data?.target ? ` (${res.data.target})` : '';
      setPushStatus(s => ({ ...s, [resultId]: 'ok' }));
      alert(already ? `ℹ️ Стратегію не додано — вона вже є в роботі${where}` : `✓ Стратегію додано в торгового робота${where}`);
    } catch (e) {
      setPushStatus(s => ({ ...s, [resultId]: 'error' }));
      alert('Помилка відправки: ' + (e?.response?.data?.detail || e.message));
    }
  }

  // ── Filter management — provided by useAnalyzerFilters hook ──────────────────
  // addFilter, removeFilter, updateFilter, toggleOperatorFn, resetFilters are from hook

  // ── Column visibility ─────────────────────────────────────────────────────────
  function toggleColumn(col) {
    setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
  }
  function selectAllColumns() {
    setVisibleColumns(Object.fromEntries(Object.keys(DEFAULT_COLUMNS).map(k => [k, true])));
  }
  function clearAllColumns() {
    setVisibleColumns(Object.fromEntries(Object.keys(DEFAULT_COLUMNS).map(k => [k, false])));
  }
  // colMenuOpen is now internal to ColumnToggle — kept here for toggling:
  function handleToggleColMenu() { setColMenuOpen(o => !o); }

  // ── Group results by batch_id (server already sorted globally) ───────────────
  const groupedResults = (() => {
    const groups = [];
    const seen = new Map();
    for (const row of results) {
      const key = row.batch_id || `single_${row.id}`;
      if (!seen.has(key)) {
        seen.set(key, []);
        groups.push({ key, rows: seen.get(key) });
      }
      seen.get(key).push(row);
    }
    return groups;
  })();

  // ── Display results (backend already filters; no client-side filtering needed) ─
  const displayResults = results;

  // ── Detail view ───────────────────────────────────────────────────────────────
  if (selectedId && selectedData) {
    if (tickerView) {
      // 🚨 Ці прапорці вирішують, ЯКИЙ графік запитає сторінка тікера, і раніше
      // вони брехали: `daily: has_data true` при тому, що денне сховище давно
      // не поповнюється — у ньому 1401 файл проти 4632 годинних. Тікер без
      // денних барів відповідав 404, і сторінка писала «Не вдалося завантажити
      // дані» (менеджер 27.08: AREB — годинні бари є, денних немає).
      // Аналізатор працює на годинних барах, тож просимо саме їх.
      const fakeTicker = {
        ticker: tickerView.ticker,
        name:   tickerView.ticker,
        hourly: { has_data: true },
        daily:  { has_data: false },
      };
      return (
        <TickerDetail
          ticker={fakeTicker}
          tradeContext={tickerView}
          onBack={() => {
            setTickerView(null);
            if (navTo) navTo(`/analyzer/${selectedId}`);
          }}
        />
      );
    }
    return (
      <BacktestDetail
        data={selectedData}
        onBack={() => {
          setSelectedId(null);
          setSelectedData(null);
          if (navTo) navTo('/analyzer');
        }}
        onTickerClick={(ctx) => {
          setTickerView(ctx);
          if (navTo) navTo(`/analyzer/${selectedId}/${ctx.ticker}`);
        }}
        onRecalc={async () => {
          await handleRecalcOne(selectedId);
          try {
            const res = await getAnalyzerResult(selectedId);
            setSelectedData(res.data);
          } catch (_) {}
        }}
        onToggleStar={async () => {
          await handleToggleStar(selectedId);
          setSelectedData(d => d ? { ...d, is_saved: !d.is_saved } : d);
        }}
        onCopyToForm={() => {
          handleCopyToForm(selectedData);
          setSelectedId(null);
          setSelectedData(null);
          if (navTo) navTo('/analyzer');
        }}
        onPushToTrading={localStorage.getItem('role') === 'admin' ? () => handlePushToTrading(selectedId) : undefined}
        pushTargets={tradingTargets}
        pushTarget={pushTarget}
        onPushTargetChange={setPushTarget}
      />
    );
  }

  const pageOffset = pageSize === 'all' ? 0 : page * Number(pageSize);

  return (
    <div style={S.page} className="sa-root">
      <div style={S.wrap} className="sa-wrap">

        {/* ── Run Mock status toast ── */}
        {runMockStatus && (
          <div style={{
            position: 'fixed', top: 70, right: 20, zIndex: 9999,
            padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            background: runMockStatus === 'starting' ? '#DBEAFE'
                      : runMockStatus === 'done'     ? '#DCFCE7'
                      : '#FEE2E2',
            color: runMockStatus === 'starting' ? '#1D4ED8'
                 : runMockStatus === 'done'     ? '#15803D'
                 : '#DC2626',
            border: `1px solid ${runMockStatus === 'starting' ? '#BFDBFE' : runMockStatus === 'done' ? '#BBF7D0' : '#FECACA'}`,
          }}>
            {runMockStatus === 'starting' && '⏳ Запускаємо стратегію...'}
            {runMockStatus === 'done'     && '✅ Стратегія запущена! Перехід до Running...'}
            {runMockStatus === 'error'    && '❌ Помилка запуску стратегії'}
          </div>
        )}

        {/* ── Form Card ── */}
        <div style={S.formCard}>
          {/* Header: title + data type switcher */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={S.formTitle}>Параметри розрахунку</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11, color: C.textSm,
                padding: '3px 10px', background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 6,
              }}>
                {dataCatalog
                  ? (() => {
                      const c = dataCatalog[dataType];
                      if (!c || c.count === 0) return 'Немає даних';
                      const from = c.date_from || '?';
                      const to   = c.date_to   || '?';
                      return `Доступно: ${c.count} тікерів, ${from} → ${to}`;
                    })()
                  : 'Завантаження…'}
              </span>
            </div>
          </div>

          {/* ── Engine switcher ── */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: C.textMd, fontWeight: 500 }}>Engine:</span>
            {[
              { val: 'python', label: 'Python' },
              { val: 'numba',  label: 'Numba'  },
              { val: 'cuda',   label: 'CUDA',   disabled: true },
            ].map(({ val, label, disabled }) => (
              <button
                key={val}
                disabled={disabled}
                onClick={() => !disabled && setEngine(val)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: `1px solid ${engine === val ? C.primary : C.border}`,
                  background: disabled ? '#F1F5F9' : engine === val ? C.primary : 'transparent',
                  color: disabled ? C.textSm : engine === val ? '#fff' : C.text,
                  opacity: disabled ? 0.5 : 1,
                }}
              >{label}</button>
            ))}
          </div>

          {/* ── 2-column layout: Trading Params | Universe & Filters ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', alignItems: 'start' }}>

            {/* ── LEFT: Trading Parameters ── */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
                Trading Parameters
              </div>

              {/* Time range */}
              <div style={{ ...S.grid3, marginBottom: 8 }}>
                <div>
                  <label style={S.label}>Start time</label>
                  <input type="date" style={S.input} value={form.startTime} onChange={e => setField('startTime', e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>Stop time</label>
                  {/* max=сьогодні: дата в майбутньому відкидає ВСІ живі тікери
                      (їхні бари не сягають уперед) і лишає у всесвіті самі
                      делістингові — числа виглядають правдоподібно, а описують
                      неіснуючий ринок. Спостережено на несті 2026-08-06. */}
                  <input type="date" style={S.input} max={new Date().toISOString().slice(0, 10)}
                         value={form.stopTime} onChange={e => setField('stopTime', e.target.value)} />
                  {form.stopTime > new Date().toISOString().slice(0, 10) && (
                    <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>
                      ⚠️ Дата в майбутньому — барів за неї не існує. Поставте сьогоднішню або раніше.
                    </div>
                  )}
                </div>
                <div>
                  <label style={S.label}>Duration (auto)</label>
                  <input style={{ ...S.input, background: '#F8FAFC', color: C.textMd }} value={durationDays} readOnly />
                </div>
              </div>

              {/* Additional Periods */}
              <div style={{ marginBottom: 12, padding: '8px 10px', background: '#F8FAFC', borderRadius: 6, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <label style={{ ...S.label, margin: 0, fontWeight: 600 }}>
                    📅 Additional Periods
                    {additionalPeriods.length > 0 && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: C.textSm, fontWeight: 400 }}>
                        {additionalPeriods.length} period{additionalPeriods.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </label>
                  <button
                    onClick={() => setAdditionalPeriods(p => [...p, { start_date: '', end_date: '' }])}
                    style={{ padding: '3px 10px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >+ Add period</button>
                </div>

                {periodsOutside.count > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, fontSize: 12, color: '#78350F' }}>
                    <b>{periodsOutside.count} з {periodsOutside.total} періодів лежать поза головним вікном</b>{' '}
                    ({(form.startTime || '').slice(0, 10)}…{(form.stopTime || '').slice(0, 10)}).
                    Парк таку сітку не рахує — її рахував би один сервер, днями. Числа
                    періодів від розширення вікна не зміняться; зміниться лише рядок
                    головного вікна.
                    <div style={{ marginTop: 6 }}>
                      <button
                        onClick={() => setForm(f => ({ ...f, startTime: periodsOutside.lo, stopTime: periodsOutside.hi }))}
                        style={{ padding: '3px 10px', background: '#B45309', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >Розширити головне вікно до {periodsOutside.lo}…{periodsOutside.hi}</button>
                    </div>
                  </div>
                )}

                {additionalPeriods.length === 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.textMd, fontStyle: 'italic' }}>
                    Кожну комбінацію можна судити не по одному вікну, а по сотні: згенеруй набір нижче
                    або додай окремі вікна вручну кнопкою "+ Add period". Метрики всіх вікон зводяться
                    в підсумок рядка — середнє, найгірше і частку збиткових вікон.
                  </div>
                )}

                {/* Генератор набору вікон. Довші набори збираються кількома
                    натисканнями: партія дописується, а не заміщає попередню. */}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textMd, marginBottom: 5 }}>
                    🎲 Згенерувати набір вікон усередині основного періоду
                  </div>
                  {/* Два режими. Випадковий лишається для невеликих наборів;
                      перебір — для повної перевірки: точок входу всього ~1200,
                      тож тисяча випадкових покриває 83% простору з дірками. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    {[['random', 'випадкові вікна'], ['sweep', 'усі точки входу']].map(([v, label]) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4,
                                              fontSize: 11, color: C.textMd, cursor: 'pointer' }}>
                        <input type="radio" name="genmode" checked={(genCfg.mode || 'random') === v}
                          onChange={() => setGenCfg({ ...genCfg, mode: v })} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {/* Пояснення режиму. Коротке, але з наслідком для ВІДБОРУ, а
                      не лише з описом механіки: перебір робить критерій
                      суворішим, і це має бути видно до натискання, а не після
                      порожньої таблиці. */}
                  <div style={{ fontSize: 10, color: C.textSm, lineHeight: 1.55,
                                marginBottom: 6, paddingLeft: 2 }}>
                    {(genCfg.mode || 'random') === 'sweep' ? (
                      <>
                        По одному вікну з <b>кожного торгового дня</b>, де вікно ще
                        вміщається до кінця періоду. Повна перевірка: без повторів і
                        без пропусків.<br />
                        ⚠️ Вона й <b>суворіша</b> — що більше вікон, то більший шанс
                        спіймати поганий відрізок. Стратегія від цього не починає
                        проходити, може тільки перестати.
                      </>
                    ) : (
                      <>
                        Задана кількість вікон у <b>випадкових місцях</b> періоду.
                        Швидше й м'якше: перевіряється частина можливих точок входу
                        (наприклад, 1000 вікон на п'ятирічному періоді — це близько
                        половини з ~1200 можливих).
                      </>
                    )}
                    <br />
                    Набір один і той самий для <b>кожної</b> комбінації сітки. Точка
                    входу, уже зайнята набором, удруге не береться — навіть якщо
                    додавати вікна кількома натисканнями.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: C.textSm }}>Довжина</span>
                    <input type="number" min={1} value={genCfg.months}
                      onChange={e => setGenCfg({ ...genCfg, months: e.target.value })}
                      style={{ ...S.input, width: 62, padding: '4px 6px', fontSize: 12 }} />
                    <span style={{ fontSize: 11, color: C.textSm }}>міс. ×</span>
                    <input type="number" min={1} max={100} value={genCfg.count}
                      disabled={genCfg.mode === 'sweep'}
                      title={genCfg.mode === 'sweep'
                        ? 'У режимі перебору кількість задають самі дані: скільки торгових днів, стільки й вікон'
                        : ''}
                      onChange={e => setGenCfg({ ...genCfg, count: e.target.value })}
                      style={{ ...S.input, width: 62, padding: '4px 6px', fontSize: 12,
                               background: genCfg.mode === 'sweep' ? '#F1F5F9' : '#fff' }} />
                    <span style={{ fontSize: 11, color: C.textSm }}>шт.</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.textMd, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!genCfg.extraEnabled}
                        onChange={e => setGenCfg({ ...genCfg, extraEnabled: e.target.checked })} />
                      + випадково
                    </label>
                    <input type="number" min={0} disabled={!genCfg.extraEnabled} value={genCfg.extraFrom}
                      onChange={e => setGenCfg({ ...genCfg, extraFrom: e.target.value })}
                      style={{ ...S.input, width: 56, padding: '4px 6px', fontSize: 12,
                               background: genCfg.extraEnabled ? '#fff' : '#F1F5F9' }} />
                    <span style={{ fontSize: 11, color: C.textSm }}>…</span>
                    <input type="number" min={0} disabled={!genCfg.extraEnabled} value={genCfg.extraTo}
                      onChange={e => setGenCfg({ ...genCfg, extraTo: e.target.value })}
                      style={{ ...S.input, width: 56, padding: '4px 6px', fontSize: 12,
                               background: genCfg.extraEnabled ? '#fff' : '#F1F5F9' }} />
                    <span style={{ fontSize: 11, color: C.textSm }}>дн.</span>
                    <button
                      onClick={generatePeriods}
                      disabled={genBusy}
                      title="Вікна заданої довжини всередині основного періоду. Одні й ті самі вікна дістануться КОЖНІЙ комбінації сітки — інакше рядки непорівнянні між собою. Точка входу, уже зайнята набором, удруге не береться."
                      style={{ padding: '4px 10px', background: genBusy ? '#94A3B8' : '#7C3AED',
                               color: '#fff', border: 'none', borderRadius: 5, fontSize: 12,
                               fontWeight: 600, cursor: genBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                    >{genBusy ? 'Рахую…' : '🎲 Згенерувати'}</button>
                  </div>
                  {genNote && (
                    <div style={{ fontSize: 11, color: C.success, marginTop: 5 }}>✓ {genNote}</div>
                  )}
                  {genError && (
                    <div style={{ fontSize: 11, color: C.danger, marginTop: 5 }}>⚠️ {genError}</div>
                  )}
                </div>

                {/* Period presets — apply a saved set / save the current set (#3) */}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textMd, marginBottom: 5 }}>
                    💾 Збережені набори періодів
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {/* Apply a saved set — always visible (disabled hint when none yet) */}
                    <select
                      value=""
                      disabled={periodPresets.length === 0}
                      onChange={e => { if (e.target.value) { importPeriodPreset(e.target.value); e.target.value = ''; } }}
                      title={periodPresets.length ? 'Застосувати збережений набір періодів до форми' : 'Немає збережених наборів — спершу збережи поточні періоди'}
                      style={{ ...S.input, width: 'auto', minWidth: 200, padding: '4px 8px', fontSize: 12,
                        cursor: periodPresets.length ? 'pointer' : 'not-allowed',
                        background: periodPresets.length ? '#fff' : '#F1F5F9',
                        color: periodPresets.length ? C.text : C.textSm }}
                    >
                      <option value="">
                        {periodPresets.length ? '📥 Застосувати збережений набір…' : 'Немає збережених наборів'}
                      </option>
                      {periodPresets.map(pr => {
                        const mainStr = pr.main
                          ? `${pr.main.start_date}→${pr.main.end_date}`
                          : null;
                        const label = mainStr
                          ? `${pr.name} (${mainStr}, +${pr.periods.length})`
                          : `${pr.name} (${pr.periods.length})`;
                        return <option key={pr.name} value={pr.name}>{label}</option>;
                      })}
                    </select>
                    <span style={{ fontSize: 11, color: C.textSm }}>або</span>
                    {/* Save the current set */}
                    <input
                      style={{ ...S.input, width: 150, padding: '4px 8px', fontSize: 12 }}
                      placeholder="Назва набору…"
                      value={periodPresetName}
                      onChange={e => setPeriodPresetName(e.target.value)}
                    />
                    <button
                      onClick={savePeriodPreset}
                      style={{ padding: '4px 10px', background: '#0EA5E9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >💾 Зберегти поточні</button>
                  </div>
                  {periodPresets.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: C.textSm }}>Видалити:</span>
                      {periodPresets.map(pr => (
                        <span key={pr.name} title={`Видалити набір "${pr.name}"`}
                          onClick={() => deletePeriodPreset(pr.name)}
                          style={{ fontSize: 10, color: C.textSm, cursor: 'pointer', border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>
                          {pr.name} ✕
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Згенеровані вікна — партіями, згорнуто. Сто рядків із полями
                    дат нечитабельні, та й редагувати їх поштучно немає сенсу:
                    партія приймається або прибирається цілком. Вручну додані
                    вікна лишаються звичайними рядками нижче. */}
                {(() => {
                  const batches = [];
                  const seen = new Map();
                  additionalPeriods.forEach((p, i) => {
                    if (!p || !p.generated) return;
                    const key = `${p.seed}|${p.months}`;
                    if (!seen.has(key)) {
                      seen.set(key, batches.length);
                      batches.push({ key, seed: p.seed, months: p.months,
                                     sweep: !!p.sweep, items: [] });
                    }
                    batches[seen.get(key)].items.push({ p, i });
                  });
                  return batches.map(b => (
                    <div key={b.key} style={{ marginTop: 8, padding: '6px 8px', background: '#fff',
                                              border: `1px solid ${C.border}`, borderRadius: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span
                          onClick={() => setGenOpen(o => ({ ...o, [b.key]: !o[b.key] }))}
                          style={{ fontSize: 12, fontWeight: 600, color: C.text, cursor: 'pointer' }}
                        >
                          {genOpen[b.key] ? '▾' : '▸'} {b.items.length} вікон × {b.months} міс.
                          {b.sweep ? ' (усі точки входу)' : ''}
                        </span>
                        <span style={{ fontSize: 10, color: C.textSm }}>зерно {b.seed}</span>
                        <button
                          onClick={() => setAdditionalPeriods(prev =>
                            prev.filter(x => !(x.generated && String(x.seed) === String(b.seed)
                                               && String(x.months) === String(b.months))))}
                          title="Прибрати всю партію"
                          style={{ marginLeft: 'auto', padding: '2px 8px', background: 'transparent',
                                   color: C.danger, border: `1px solid ${C.border}`, borderRadius: 5,
                                   fontSize: 12, fontWeight: 700, cursor: 'pointer', lineHeight: 1.4 }}
                        >× партію</button>
                      </div>
                      {genOpen[b.key] && (
                        <div style={{ marginTop: 6, maxHeight: 190, overflowY: 'auto',
                                      fontSize: 11, color: C.textMd, lineHeight: 1.7 }}>
                          {b.items.map(({ p, i }, n) => (
                            <div key={i} style={{ display: 'flex', gap: 8 }}>
                              <span style={{ color: C.textSm, minWidth: 30 }}>{n + 1}.</span>
                              <span>{p.start_date} → {p.end_date}</span>
                              {p.days != null && <span style={{ color: C.textSm }}>({p.days} дн.)</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ));
                })()}

                {additionalPeriods.map((p, i) => {
                  if (p && p.generated) return null;
                  // Нумерація рахує лише ручні вікна: інакше перше ж поле після
                  // згенерованої сотні звалось би «Period 101».
                  const manualNo = additionalPeriods
                    .slice(0, i).filter(x => x && !x.generated).length + 1;
                  const outOfRange = (p.start_date && p.start_date < form.startTime)
                                  || (p.end_date   && p.end_date   > form.stopTime);
                  return (
                    <div key={i}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginTop: 8, alignItems: 'end' }}>
                        <div>
                          <label style={S.label}>Period {manualNo} Start</label>
                          <input
                            type="date" style={{ ...S.input, borderColor: outOfRange ? '#F59E0B' : undefined }}
                            value={p.start_date}
                            onChange={e => setAdditionalPeriods(prev => prev.map((x, idx) => idx === i ? { ...x, start_date: e.target.value } : x))}
                          />
                        </div>
                        <div>
                          <label style={S.label}>Period {manualNo} End</label>
                          <input
                            type="date" style={{ ...S.input, borderColor: outOfRange ? '#F59E0B' : undefined }}
                            value={p.end_date}
                            onChange={e => setAdditionalPeriods(prev => prev.map((x, idx) => idx === i ? { ...x, end_date: e.target.value } : x))}
                          />
                        </div>
                        <button
                          onClick={() => setAdditionalPeriods(prev => prev.filter((_, idx) => idx !== i))}
                          title="Remove this period"
                          style={{ padding: '6px 10px', background: 'transparent', color: C.danger, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 14, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}
                        >×</button>
                      </div>
                      {outOfRange && (
                        <div style={{ fontSize: 11, color: '#D97706', marginTop: 3, paddingLeft: 2 }}>
                          ⚠️ Поза основним діапазоном ({form.startTime} — {form.stopTime}) — завантажує окремі дані, повільніше
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedStrategy !== 'parallel' ? (
                <>
                  {/* Strategy params: 4 fields in 2×2 grid */}
                  <div style={{ ...S.grid2, marginBottom: 12 }}>
                    <RangeField
                      label="Поріг зростання для входу (Buy%)"
                      tooltip="Мінімальне зростання ціни акції (%) за вікно T-Buy, щоб спрацював сигнал купівлі. Наприклад, 1.0% = купуємо лише якщо акція зросла щонайменше на 1% за вказаний час. Менше значення — більше угод, більше — тільки сильні сигнали."
                      paramKey="xBuy"
                      mode={paramMode.xBuy} fixed={fixed.xBuy} range={rangeP.xBuy}
                      onModeToggle={() => toggleMode('xBuy')}
                      onFixed={v => setFixed(p => ({ ...p, xBuy: v }))}
                      onRange={r => setRangeP(p => ({ ...p, xBuy: r }))} />
                    <PeriodField
                      dataType={dataType}
                      mode={paramMode.tHoursBuy} fixed={fixed.tHoursBuy} range={rangeP.tHoursBuy}
                      onModeToggle={() => toggleMode('tHoursBuy')}
                      onFixed={v => setFixed(p => ({ ...p, tHoursBuy: v }))}
                      onRange={r => setRangeP(p => ({ ...p, tHoursBuy: r }))} />
                    <RangeField
                      label="Поріг падіння для виходу (Drop%)"
                      tooltip="Максимально допустиме падіння ціни (%) від ціни купівлі або за вікно T-Sell. При перевищенні цього порогу позиція закривається з метою обмеження збитків (стоп-лос). Менше значення — жорсткіший стоп, більше — більше терпимості до просадки."
                      paramKey="ySell"
                      mode={paramMode.ySell} fixed={fixed.ySell} range={rangeP.ySell}
                      onModeToggle={() => toggleMode('ySell')}
                      onFixed={v => setFixed(p => ({ ...p, ySell: v }))}
                      onRange={r => setRangeP(p => ({ ...p, ySell: r }))} />
                    <SellWindowField
                      dataType={dataType}
                      mode={sellWindowMode} fixed={sellWindowDays} range={sellWindowRange}
                      onModeToggle={() => setSellWindowMode(m => m === 'same' ? 'fixed' : m === 'fixed' ? 'range' : 'same')}
                      onFixed={v => setSellWindowDays(v)}
                      onRange={r => setSellWindowRange(r)} />
                  </div>

                  {/* Buy% на виході — правило «не виріс» (менеджер 29.10) */}
                  {isAdvancedForm && (
                    <div style={{ marginBottom: 12, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <div style={{ ...S.label, display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                        Buy% на виході
                        <Tooltip text="«Лише вхід»: Buy% тільки ініціює купівлю. «Вхід + утримання»: ще й продаж, якщо після вікна продажу (Sell Window) ціна тримається нижче ціни купівлі + Buy% (правило no_growth, як до 29.10). Обидві галочки — пошук порахує кожну комбінацію в обох варіантах." />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' }}>
                          <input type="checkbox" checked={noGrowthOff}
                            onChange={e => { const v = e.target.checked; if (!v && !noGrowthOn) return; setNoGrowthOff(v); }}
                            style={{ accentColor: C.primary }} />
                          Лише вхід
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' }}>
                          <input type="checkbox" checked={noGrowthOn}
                            onChange={e => { const v = e.target.checked; if (!v && !noGrowthOff) return; setNoGrowthOn(v); }}
                            style={{ accentColor: C.primary }} />
                          Вхід + утримання (продаж, якщо ціна &lt; купівлі + Buy%)
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Initial capital + Number of companies */}
                  <div style={{ ...S.grid2, marginBottom: 12 }}>
                    <div>
                      <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
                        Початковий капітал (Capital, $)
                        <Tooltip text="Стартовий обсяг коштів у доларах для симуляції торгівлі. Впливає на розмір кожної позиції та абсолютний прибуток/збиток. Чим більший капітал, тим суттєвіші абсолютні результати." />
                      </label>
                      <input type="number" style={S.input} value={form.initialCapital} onChange={e => setField('initialCapital', e.target.value)} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                        <label style={{ ...S.label, display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                          Кількість компаній (N cos.)
                          <Tooltip text="Максимальна кількість одночасно відкритих позицій. Капітал рівномірно розподіляється між ними. Більше компаній = диверсифікація, але менший розмір кожної позиції. Рекомендовано: 10–20 для балансу ризику." />
                        </label>
                        <button
                          onClick={() => setNCompaniesMode(m => m === 'fixed' ? 'range' : 'fixed')}
                          style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none',
                            background: nCompaniesMode === 'range' ? '#DBEAFE' : '#F1F5F9',
                            color: nCompaniesMode === 'range' ? C.primary : C.textMd, fontWeight: 600,
                          }}>
                          {nCompaniesMode === 'range' ? 'Діапазон' : 'Фіксоване'}
                        </button>
                      </div>
                      {nCompaniesMode === 'fixed' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 10px' }}>
                          <div>
                            <input
                              type="number" min={1} max={100}
                              value={numCompanies}
                              onChange={e => setNumCompanies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                              style={S.input}
                            />
                            {tickerMode === 'specific' && (
                              <div style={{ fontSize: 11, color: C.textSm, marginTop: 4 }}>
                                🎯 Пул: {selectedTickers.length} акцій — стратегія тримає до {numCompanies} одночасно
                              </div>
                            )}
                          </div>
                          <div>
                            <input
                              style={{ ...S.input, background: '#F8FAFC', color: C.textMd }}
                              value={(100 / Math.max(1, numCompanies)).toFixed(2) + '% pos'}
                              readOnly
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                            <div>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
                              <input type="number" min={1} step={1} style={S.input}
                                value={nCompaniesRange.min}
                                onChange={e => setNCompaniesRange(r => ({ ...r, min: Math.max(1, parseInt(e.target.value) || 1) }))} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
                              <input type="number" min={1} step={1} style={S.input}
                                value={nCompaniesRange.max}
                                onChange={e => setNCompaniesRange(r => ({ ...r, max: Math.max(1, parseInt(e.target.value) || 1) }))} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
                              <input type="number" min={1} step={1} style={S.input}
                                value={nCompaniesRange.step}
                                onChange={e => setNCompaniesRange(r => ({ ...r, step: Math.max(1, parseInt(e.target.value) || 1) }))} />
                            </div>
                          </div>
                          {(() => {
                            const vals = [];
                            for (let v = nCompaniesRange.min; v <= nCompaniesRange.max + 1e-9; v += nCompaniesRange.step)
                              vals.push(Math.round(v));
                            const preview = vals.length <= 6
                              ? vals.join(', ')
                              : vals.slice(0, 3).join(', ') + ', …, ' + vals[vals.length - 1];
                            return (
                              <div style={{ fontSize: 11, color: C.textSm, marginTop: 4 }}>
                                → значення: {preview} ({vals.length} варіантів)
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Position size */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center' }}>
                      Розмір позиції (Pos%)
                      <Tooltip text="Режим розрахунку розміру кожної позиції. Cash — ділить весь наявний кеш на кількість вільних слотів (весь капітал в обороті). Fixed — фіксована сума на позицію від початкового капіталу. Protected — торгуємо тільки початковим капіталом, прибуток не реінвестується." />
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        { val: true,        label: 'Cash',      desc: 'cash ÷ вільні слоти — весь кеш в обороті' },
                        { val: false,       label: 'Fixed',     desc: `фіксовано $${Math.round(Number(form.initialCapital) / Math.max(1, numCompanies)).toLocaleString()} — незмінний слот` },
                        { val: 'protected', label: 'Protected', desc: 'прибуток захищено — торгуємо тільки початковим капіталом' },
                      ].map(({ val, label, desc }) => (
                        <label key={String(val)} title={desc} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                          padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: reinvest === val ? C.primary : C.inputBg,
                          color: reinvest === val ? '#fff' : C.textSm,
                          border: `1px solid ${reinvest === val ? C.primary : C.border}` }}>
                          <input type="radio" name="reinvest" checked={reinvest === val}
                            onChange={() => setReinvest(val)}
                            style={{ display: 'none' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <span style={{ fontSize: 11, color: C.textSm }}>
                      {reinvest === 'protected' ? 'прибуток захищено — початковий $' + Number(form.initialCapital).toLocaleString()
                        : reinvest ? 'cash ÷ вільні слоти'
                        : `фіксовано $${Math.round(Number(form.initialCapital) / Math.max(1, numCompanies)).toLocaleString()}`}
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: C.textSm, padding: '10px 14px', background: '#FFF7ED', border: `1px solid #FED7AA`, borderRadius: 8, marginTop: 4 }}>
                  ⚡ Паралельна стратегія — налаштуйте параметри нижче
                </div>
              )}
            </div>

            {/* ── RIGHT: Universe & Filters ── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Universe & Filters
                </span>
                <span
                  title={universeCountTitle(universeInfo)}
                  style={{
                    fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '2px 10px',
                    background: universeCount === 0 ? '#FEE2E2' : C.primary + '15',
                    color: universeCount === 0 ? '#DC2626' : C.primary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {universeCountLoading ? '… тікерів'
                    : `${universeCount ?? '—'} тікерів`}
                </span>
              </div>

              {/* Ticker mode toggle */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setTickerMode('all')}
                  style={{
                    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    border: `1px solid ${tickerMode === 'all' ? C.primary : C.border}`,
                    background: tickerMode === 'all' ? C.primary : 'transparent',
                    color: tickerMode === 'all' ? '#fff' : C.text,
                  }}
                >
                  🌐 Всі по фільтрах
                </button>
                <button
                  type="button"
                  onClick={() => setTickerMode('specific')}
                  style={{
                    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    border: `1px solid ${tickerMode === 'specific' ? C.primary : C.border}`,
                    background: tickerMode === 'specific' ? C.primary : 'transparent',
                    color: tickerMode === 'specific' ? '#fff' : C.text,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  🎯 Обрані акції
                  {selectedTickers.length > 0 && (
                    <span style={{
                      background: '#ef4444', color: '#fff', borderRadius: 10,
                      padding: '1px 7px', fontSize: 11,
                    }}>
                      {selectedTickers.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Sector filter — visible only in 'all' mode. Country selector removed:
                  the universe is US-only (ibkr_verified_tickers.csv), so form.country
                  stays ['USA'] and the zone checkboxes were dead controls. */}
              {tickerMode === 'all' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
                    Індекс
                    <Tooltip text="Обмежити всесвіт компаніями індексу: S&P 500 (великі) / 400 (середні) / 600 (малі), NASDAQ 100, NASDAQ Composite (усі акції з лістингом на NASDAQ). Кілька індексів = їх обʼєднання (S&P 500+400+600 = S&P 1500). Порожньо = без обмеження за індексом. УВАГА: індекс НЕ замінює решту фільтрів — він накладається ПОВЕРХ сектора/капіталізації/бети/обсягу (перетин). Якщо хочеш чистий склад індексу — постав Market Cap = All і зніми сектор. Лічильник тікерів угорі завжди показує підсумок." />
                  </label>
                  <div style={S.checkRow}>
                    {SP_INDICES.map(({ key, label }) => (
                      <label key={key} style={S.checkItem}>
                        <input type="checkbox" checked={(form.index || []).includes(key)} onChange={() => toggleMulti('index', key)} />
                        {label}
                      </label>
                    ))}
                    {SP1500_KEYS.every(k => (form.index || []).includes(k)) && (
                      <span style={{ fontSize: 11, color: C.primary, fontWeight: 600, alignSelf: 'center' }}>= S&P 1500</span>
                    )}
                  </div>
                </div>
              )}
              {tickerMode === 'all' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={S.label}>Sector</label>
                  <div style={S.checkRow}>
                    {availableSectors.map(s => (
                      <label key={s} style={S.checkItem}>
                        <input type="checkbox" checked={form.sector.includes(s)} onChange={() => toggleMulti('sector', s)} />
                        {s}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Specific tickers dropdown — visible only in 'specific' mode */}
              {tickerMode === 'specific' && (
                <div style={{ marginBottom: 8 }}>
                  {savedLists.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: C.textSm }}>📁 Збережені:</span>
                      {savedLists.map(list => (
                        <button
                          key={list.key}
                          type="button"
                          onClick={() => loadSavedList(list)}
                          title="Завантажити цей список у вибір"
                          style={{
                            background: C.primary + '12', border: `1px solid ${C.primary}55`, borderRadius: 6,
                            padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: C.primary, fontWeight: 600,
                          }}
                        >
                          {list.label} ({list.count})
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setPasteOpen(o => !o)}
                    style={{
                      background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                      padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: C.text,
                    }}
                  >
                    📋 {pasteOpen ? 'Сховати вставку списку' : 'Вставити список тікерів'}
                  </button>
                  {pasteOpen && (
                    <div style={{ marginTop: 6 }}>
                      <textarea
                        value={pasteText}
                        onChange={e => setPasteText(e.target.value)}
                        placeholder="Встав тікери через кому/пробіл/новий рядок (напр. з legacy_backtest_universe_1313.csv)"
                        rows={4}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                          border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12,
                          fontFamily: 'monospace', resize: 'vertical',
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        <button
                          type="button"
                          onClick={applyPastedTickers}
                          style={{
                            background: C.primary, color: '#fff', border: 'none', borderRadius: 6,
                            padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >Завантажити у вибір</button>
                        {pasteMsg && <span style={{ fontSize: 12, color: C.textSm }}>{pasteMsg}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tickerMode === 'specific' && (
                <div style={{ position: 'relative', marginBottom: 10 }} ref={dropdownRef}>
                  <div
                    onClick={() => setDropdownOpen(o => !o)}
                    style={{
                      display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 10px',
                      border: `1px solid ${dropdownOpen ? C.primary : C.border}`,
                      borderRadius: 8, cursor: 'pointer', minHeight: 38,
                      background: C.cardBg, alignItems: 'center',
                    }}
                  >
                    {selectedTickers.length === 0 ? (
                      <span style={{ color: C.textSm, fontSize: 13 }}>Оберіть акції...</span>
                    ) : (
                      selectedTickers.map(t => (
                        <span key={t} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: C.primary + '20', color: C.primary,
                          borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600,
                        }}>
                          {t}
                          <span
                            onClick={e => { e.stopPropagation(); setSelectedTickers(s => s.filter(x => x !== t)); }}
                            style={{ cursor: 'pointer', opacity: 0.7 }}
                          >×</span>
                        </span>
                      ))
                    )}
                    <span style={{ marginLeft: 'auto', color: C.textSm, fontSize: 12 }}>
                      {dropdownOpen ? '▲' : '▼'}
                    </span>
                  </div>

                  {dropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                      background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: 380,
                      display: 'flex', flexDirection: 'column', marginTop: 4,
                    }}>
                      <div style={{
                        padding: '8px 10px', borderBottom: `1px solid ${C.border}`,
                        position: 'sticky', top: 0, background: C.cardBg, zIndex: 1,
                      }}>
                        <input
                          autoFocus
                          placeholder="🔍 Пошук по тікеру, назві або сектору..."
                          value={tickerSearch}
                          onChange={e => setTickerSearch(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          style={{
                            width: '100%', padding: '6px 10px', border: `1px solid ${C.border}`,
                            borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        {selectedTickers.length > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 12, color: C.textSm }}>
                            <span>Обрано: {selectedTickers.length}</span>
                            <button
                              onClick={e => { e.stopPropagation(); setSelectedTickers([]); }}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}
                            >Очистити все</button>
                          </div>
                        )}
                      </div>

                      <div style={{ overflowY: 'auto', flex: 1 }}>
                        {filteredTickers.length === 0 ? (
                          <div style={{ padding: 20, textAlign: 'center', color: C.textSm, fontSize: 13 }}>
                            {allTickersList.length === 0 ? 'Завантаження...' : 'Нічого не знайдено'}
                          </div>
                        ) : filteredTickers.map(t => {
                          const isSel = selectedTickers.includes(t.ticker);
                          return (
                            <div
                              key={t.ticker}
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedTickers(s => isSel ? s.filter(x => x !== t.ticker) : [...s, t.ticker]);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                cursor: 'pointer', background: isSel ? C.primary + '10' : 'transparent',
                                borderBottom: `1px solid ${C.border}20`,
                              }}
                              onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{
                                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                border: `2px solid ${isSel ? C.primary : C.border}`,
                                background: isSel ? C.primary : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {isSel && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontWeight: 700, fontSize: 13, color: C.text, fontFamily: 'monospace' }}>{t.ticker}</span>
                                  {isSel && <span style={{ fontSize: 10, color: C.primary, fontWeight: 600 }}>✓ обрано</span>}
                                </div>
                                <div style={{ fontSize: 11, color: C.textSm, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {t.name}
                                  {t.sector && <span style={{ marginLeft: 6, opacity: 0.6 }}>· {t.sector}</span>}
                                </div>
                              </div>
                              {t.market_cap && (
                                <div style={{ fontSize: 11, color: C.textSm, flexShrink: 0 }}>
                                  ${(t.market_cap / 1e9).toFixed(1)}B
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div style={{
                        padding: '8px 12px', borderTop: `1px solid ${C.border}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <span style={{ fontSize: 12, color: C.textSm }}>{filteredTickers.length} тікерів</span>
                        <button
                          onClick={e => { e.stopPropagation(); setDropdownOpen(false); }}
                          style={{
                            padding: '6px 16px', background: C.primary, color: '#fff',
                            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          }}
                        >
                          Застосувати ({selectedTickers.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hint when specific mode is active */}
              {tickerMode === 'specific' && selectedTickers.length > 0 && (
                <div style={{
                  fontSize: 12, color: C.textSm, padding: '6px 10px',
                  background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd',
                  marginBottom: 10,
                }}>
                  🎯 Торгуємо тільки по {selectedTickers.length} обраних акціях:{' '}
                  {selectedTickers.join(', ')}
                  {' '}— фільтри Market Cap / Beta не застосовуються
                </div>
              )}

              {/* Market cap + Beta + Volume — animated, hidden in specific mode */}
              <div style={{
                overflow: 'hidden',
                maxHeight: tickerMode === 'all' ? '500px' : '0px',
                opacity:   tickerMode === 'all' ? 1 : 0,
                transition: 'max-height 0.3s ease, opacity 0.2s ease',
              }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
                    Market Cap
                    <Tooltip text="Можна обрати КІЛЬКА розмірів одразу (напр. Mid + Small) — у всесвіт потрапить акція, що підходить під будь-який із обраних. 'All' знімає фільтр і не поєднується з рештою. 'Custom' — власний діапазон From/To, теж додається до вибраних." />
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {(() => {
                      const picked = canonCapBuckets(form.marketCapBuckets);
                      const btn = (active) => ({
                        padding: '4px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                        border: `1px solid ${active ? C.primary : C.border}`,
                        background: active ? C.primaryLt : C.cardBg,
                        color: active ? C.primary : C.textMd,
                        fontWeight: active ? 600 : 400,
                      });
                      // "All" lights up only when the universe really is unfiltered by cap.
                      // The legacy default (10M..10T) leaves every button dark, as before.
                      const allActive = picked.length === 0 && !form.capCustom
                        && Number(form.marketCapMin) <= 0 && Number(form.marketCapMax) >= 1e15;
                      return (
                        <>
                          {/* All is exclusive: picking it clears the rest and drops the cap filter. */}
                          <button
                            type="button"
                            onClick={() => setForm(f => ({
                              ...f, marketCapBuckets: [], capCustom: false,
                              marketCapMin: 0, marketCapMax: 1e15,
                            }))}
                            style={btn(allActive)}
                          >All</button>
                          {MC_PRESETS.map(p => {
                            const active = picked.includes(p.label);
                            return (
                              <button
                                key={p.label}
                                type="button"
                                onClick={() => setForm(f => {
                                  const cur = canonCapBuckets(f.marketCapBuckets);
                                  const next = cur.includes(p.label)
                                    ? cur.filter(x => x !== p.label)
                                    : [...cur, p.label];
                                  // Back to "All" (nothing picked) → reset the range so the
                                  // legacy min/max fallback filters nothing out.
                                  const empty = next.length === 0 && !f.capCustom;
                                  return {
                                    ...f, marketCapBuckets: next,
                                    ...(empty ? { marketCapMin: 0, marketCapMax: 1e15 } : {}),
                                  };
                                })}
                                style={btn(active)}
                              >{p.label}</button>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => setForm(f => {
                              const next = !f.capCustom;
                              const cur = canonCapBuckets(f.marketCapBuckets);
                              const empty = !next && cur.length === 0;
                              return {
                                ...f, capCustom: next,
                                // Turning custom ON from the "All" state: seed a sane range
                                // instead of 0..1e15, which would mean "everything".
                                ...(next && Number(f.marketCapMax) >= 1e15 && Number(f.marketCapMin) === 0
                                  ? { marketCapMin: 10000000, marketCapMax: 10000000000000 } : {}),
                                ...(empty ? { marketCapMin: 0, marketCapMax: 1e15 } : {}),
                              };
                            })}
                            style={btn(!!form.capCustom)}
                          >Custom</button>
                        </>
                      );
                    })()}
                  </div>
                  {form.capCustom && (
                    <div style={{ ...S.grid2 }}>
                      <div>
                        <label style={S.label}>From ($)</label>
                        <input type="number" style={S.input} value={form.marketCapMin} onChange={e => setField('marketCapMin', e.target.value)} />
                      </div>
                      <div>
                        <label style={S.label}>To ($)</label>
                        <input type="number" style={S.input} value={form.marketCapMax} onChange={e => setField('marketCapMax', e.target.value)} />
                      </div>
                    </div>
                  )}

                  {/* Додаткові набори капіталізації (менеджер 2026-08-19).
                      Кожен набір — окремий прогін: обраний вище набір + усі
                      додані нижче. Розрізнити їх може лише архівна
                      капіталізація [7]: список компаній вантажиться один раз на
                      всю сітку, тож без неї всі набори дали б однаковий
                      результат. */}
                  <div style={{ marginTop: 10 }}>
                    {capBucketSets.map((setNames, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: C.textSm, minWidth: 62 }}>Набір {idx + 2}</span>
                        {MC_PRESETS.map(preset => {
                          // Регістр-незалежно: збережені набори бувають і 'MEGA', і 'Mega'.
                          const active = canonCapBuckets(setNames).includes(preset.label);
                          return (
                            <button
                              key={preset.label}
                              type="button"
                              onClick={() => setCapBucketSets(prev => prev.map((row, i) => {
                                if (i !== idx) return row;
                                const cur = canonCapBuckets(row);
                                return cur.includes(preset.label)
                                  ? cur.filter(x => x !== preset.label)
                                  : [...cur, preset.label];
                              }))}
                              style={{
                                padding: '4px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                                border: `1px solid ${active ? C.primary : C.border}`,
                                background: active ? C.primaryLt : C.cardBg,
                                color: active ? C.primary : C.textMd,
                                fontWeight: active ? 600 : 400,
                              }}
                            >{preset.label}</button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => setCapBucketSets(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                            border: `1px solid ${C.border}`, background: C.cardBg, color: C.textSm,
                          }}
                          title="Прибрати набір"
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCapBucketSets(prev => [...prev, ['Mega', 'Large']])}
                      style={{
                        padding: '4px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                        border: `1px dashed ${C.border}`, background: C.cardBg, color: C.textMd,
                      }}
                    >+ Додати набір капіталізації</button>
                    {capBucketSets.length > 0 && (
                      <div style={{
                        fontSize: 11, marginTop: 6, borderRadius: 6, padding: '6px 10px',
                        color: C.textMd,
                        background: pitEnabled ? '#DBEAFE' : '#FEE2E2',
                        borderLeft: `3px solid ${pitEnabled ? '#3B82F6' : '#DC2626'}`,
                      }}>
                        {pitEnabled
                          ? `Наборів ${capBucketSets.length + 1} — кількість комбінацій множиться на ${capBucketSets.length + 1}.`
                          : 'Увімкніть [7] «Капіталізація на момент угоди» — інакше пошук не запуститься: без неї всі набори дали б однаковий результат.'}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ ...S.grid3, marginBottom: 10 }}>
                  <div>
                    <label style={S.label}>Beta min <span style={{ color: C.textSm, fontWeight: 400 }}>(volatility)</span></label>
                    <input
                      type="number" step="0.1" min="0" max="10" style={S.input}
                      placeholder="e.g. 0.5"
                      value={form.betaMin}
                      onChange={e => setField('betaMin', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={S.label}>Beta max</label>
                    <input
                      type="number" step="0.1" min="0" max="10" style={S.input}
                      placeholder="e.g. 3.0"
                      value={form.betaMax}
                      onChange={e => setField('betaMax', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={S.label}>Min avg volume <span style={{ color: C.textSm, fontWeight: 400 }}>(10d)</span></label>
                    <input
                      type="number" step="10000" min="0" style={S.input}
                      placeholder="e.g. 100000"
                      value={form.minAvgVolume}
                      onChange={e => setField('minAvgVolume', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Regime filter */}
              <div style={{
                padding: '10px 14px',
                background: '#F8FAFC', borderRadius: 8,
                border: `1px solid ${C.border}`,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: C.textMd,
                  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
                }}>
                  Торгувати тільки в режимах ринку
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={regimeFilter.length === 0}
                      onChange={() => setRegimeFilter([])}
                      style={{ accentColor: C.primary }}
                    />
                    <span style={{ fontSize: 13 }}>🌐 Всі режими</span>
                  </label>
                  {[
                    { key: 'bull',     label: '🟢 Bull Market',  color: '#22c55e' },
                    { key: 'bear',     label: '🔴 Bear Market',  color: '#ef4444' },
                    { key: 'sideways', label: '🟡 Sideways',     color: '#eab308' },
                  ].map(({ key, label, color }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={regimeFilter.includes(key)}
                        onChange={e => setRegimeFilter(f =>
                          e.target.checked ? [...f, key] : f.filter(x => x !== key)
                        )}
                        style={{ accentColor: color }}
                      />
                      <span style={{ fontSize: 13 }}>{label}</span>
                    </label>
                  ))}
                </div>
                {regimeFilter.length > 0 && (() => {
                  const filtered = (regimes?.intervals || []).filter(i => regimeFilter.includes(i.type));
                  const totalDays = filtered.reduce((sum, i) => {
                    const d1 = new Date(i.start), d2 = new Date(i.end);
                    return sum + Math.round((d2 - d1) / 86400000) + 1;
                  }, 0);
                  return (
                    <div style={{ marginTop: 8, fontSize: 11, color: C.textSm }}>
                      📅 Активних днів: <b>{totalDays}</b>
                      {' '}({filtered.length} інтервалів)
                      {regimes?.calculated_at && (
                        <span style={{ marginLeft: 8, opacity: 0.6 }}>
                          розраховано: {regimes.calculated_at.slice(0, 10)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (regimesPollRef.current) { clearInterval(regimesPollRef.current); regimesPollRef.current = null; }
                          setRegimesLoading(true);
                          recalculateRegimes().catch(() => {});
                          regimesPollRef.current = setInterval(() => {
                            getAnalyzerRegimes(dataType).then(r => {
                              if (r.data?.intervals?.length) {
                                setRegimes(r.data);
                                setRegimesLoading(false);
                                clearInterval(regimesPollRef.current);
                                regimesPollRef.current = null;
                              }
                            }).catch(() => {});
                          }, 3000);
                        }}
                        style={{
                          marginLeft: 8, background: 'none', border: 'none',
                          color: C.primary, cursor: 'pointer', fontSize: 11, padding: 0,
                        }}
                      >
                        🔄 Перерахувати
                      </button>
                    </div>
                  );
                })()}
                {regimesLoading && (
                  <div style={{ marginTop: 8, fontSize: 11, color: C.textMd }}>
                    ⏳ Розрахунок режимів... (може зайняти хвилину)
                  </div>
                )}
              </div>
            </div>

          </div>
          {/* end 2-column layout */}

          {/* ══ Advanced mechanism blocks ══════════════════════════════════════ */}
          <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Розширені механізми
            </div>

              {/* Block 1: Buy Signal Filters */}
              <AccordionBlock title="🔍 Фільтри сигналу купівлі (Buy Signal Filters) — cap, overpriced, dip">
                {/* 1a: Buy Cap — two-flag pattern (BT-2) */}
                <SubBlock label="[1a] Враховувати Buy Cap" enabled={buyCapEnabled} onToggle={setBuyCapEnabled}
                  hint="Не купуємо якщо акція вже виросла більше X% — у grid підуть ці значення">
                  {buyCapEnabled && <RangeField
                    label="Макс. зріст для купівлі (Buy Cap, %)"
                    tooltip="Верхній ліміт зростання акції. Якщо акція вже зросла більше ніж це значення за вікно T-Buy — сигнал ігнорується. Захищає від купівлі 'на піку' після великого ралі. Рекомендовано: 5–15%."
                    mode={buyCapMode} fixed={buyCapFixed} range={buyCapRange}
                    onModeToggle={() => setBuyCapMode(m => m === 'fixed' ? 'range' : 'fixed')}
                    onFixed={setBuyCapFixed} onRange={setBuyCapRange} step="0.5" />}
                </SubBlock>
                <SubBlock label="[1a] Не враховувати Buy Cap (baseline)" enabled={buyCapOffEnabled} onToggle={setBuyCapOffEnabled}
                  hint="У grid додасться варіант без Buy Cap для порівняння">
                </SubBlock>

                {/* 1b: Overpriced */}
                <SubBlock label="[1b] Не купувати вище середньої ціни (Overpriced)" enabled={overpricedEnabled} onToggle={setOverpricedEnabled}
                  hint="Не купувати якщо поточна ціна на X% вища за MA(N барів)">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    <RangeField
                      label="Ціна вища за MA на (Overpriced, %)"
                      tooltip="Якщо поточна ціна перевищує середню ковзну (MA) на цей відсоток — не купуємо. Фільтрує перекуплені акції. Наприклад, 5% означає: не купуємо якщо ціна більш ніж на 5% вища за MA."
                      mode={ovpPctMode} fixed={ovpPctFixed} range={ovpPctRange}
                      onModeToggle={() => setOvpPctMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setOvpPctFixed} onRange={setOvpPctRange} step="1" />
                    <RangeField
                      label="MA — кількість барів для розрахунку"
                      tooltip="Кількість барів (свічок) для розрахунку середньої ковзної ціни. Більше значення = більш довгострокова MA, менше = більш чутлива до недавніх змін."
                      mode={ovpPerMode} fixed={ovpPerFixed} range={ovpPerRange}
                      onModeToggle={() => setOvpPerMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setOvpPerFixed} onRange={setOvpPerRange} step="24" integer />
                  </div>
                </SubBlock>

                {/* 1c: Dip Filter */}
                <SubBlock label="[1c] Купувати тільки після відкату (Dip Filter)" enabled={dipEnabled} onToggle={setDipEnabled}
                  hint="BUY-сигнал спрацьовує тільки якщо ціна нижче MA — є куди рости">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    <RangeField
                      label="Ціна нижча за MA на (Dip, %)"
                      tooltip="Купуємо тільки якщо поточна ціна нижча за MA на цей відсоток або більше. Логіка 'купуй на відкаті': чекаємо, поки ціна відступить від максимуму, щоб є потенціал для зростання."
                      mode={dipPctMode} fixed={dipPctFixed} range={dipPctRange}
                      onModeToggle={() => setDipPctMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setDipPctFixed} onRange={setDipPctRange} step="1" />
                    <RangeField
                      label="MA — кількість барів для розрахунку"
                      tooltip="Кількість барів (свічок) для розрахунку середньої ковзної при фільтрі відкату. Рекомендовано 12–48 барів для hourly даних або 5–21 для денних."
                      mode={dipPerMode} fixed={dipPerFixed} range={dipPerRange}
                      onModeToggle={() => setDipPerMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setDipPerFixed} onRange={setDipPerRange} step="12" integer />
                  </div>
                </SubBlock>
              </AccordionBlock>

              {/* Block 2: Rotation & Opportunity */}
              <AccordionBlock title="🔄 Ротація та можливості (Rotation &amp; Opportunity) — автоматична ротація портфелю">
                {/* 2a: Rotation */}
                <SubBlock label="[2a] Ротація на сильніший сигнал (Rotation, Мех. 1)" enabled={rotEnabled} onToggle={setRotEnabled}
                  hint="Якщо інша акція зросла в rotation_factor разів більше нашої від моменту покупки → ротуємо">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    <RangeField
                      label="Мін. утримання до ротації (Rotation Hold, барів)"
                      tooltip="Мінімальна кількість барів, яку потрібно утримувати позицію перед тим, як вона стане кандидатом на ротацію. Захищає від надто частої ротації відразу після купівлі. Наприклад, 3 бари hourly = не ротуємо перші 3 години."
                      mode={rotHoldMode} fixed={rotHoldFixed} range={rotHoldRange}
                      onModeToggle={() => setRotHoldMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setRotHoldFixed} onRange={setRotHoldRange} step="1" integer />
                    <RangeField
                      label="Множник переваги нової акції (Rotation Factor, X)"
                      tooltip="У скільки разів нова акція має рости швидше нашої поточної, щоб відбулась ротація. Наприклад, factor=2 означає: якщо наша +5%, то ротуємо тільки якщо є акція з +10% за той самий час. Вищий factor = рідша ротація."
                      mode={rotFactorMode} fixed={rotFactorFixed} range={rotFactorRange}
                      onModeToggle={() => setRotFactorMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setRotFactorFixed} onRange={setRotFactorRange} step="0.5" />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: C.textMd, padding: '6px 10px', background: '#F0FDF4', borderRadius: 6 }}>
                    Приклад: наша <b>+5%</b> → продаємо якщо є акція{' '}
                    <b style={{ color: C.success }}>+{(5 * rotFactorFixed).toFixed(1)}%</b> від нашої дати покупки
                  </div>
                </SubBlock>

                {/* 2b: Opportunity */}
                <SubBlock label="[2b] Продаж при появі кращої можливості (Opportunity, Мех. 3)" enabled={oppEnabled} onToggle={setOppEnabled}
                  hint="Постійно шукаємо акції з сильнішим window-сигналом. Якщо є краща на min_gain% → ротуємо">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                    <RangeField
                      label="Мін. утримання до перевірки (Opp. Hold, барів)"
                      tooltip="Мінімальна кількість барів утримання позиції, після якої починаємо перевіряти наявність кращих можливостей на ринку. Запобігає надто ранньому виходу через opportunity-сигнал."
                      mode={oppHoldMode} fixed={oppHoldFixed} range={oppHoldRange}
                      onModeToggle={() => setOppHoldMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setOppHoldFixed} onRange={setOppHoldRange} step="1" integer />
                    <RangeField
                      label="Мін. перевага нового сигналу (Opp. Gain, %)"
                      tooltip="На скільки відсотків новий сигнал має бути кращим за поточну позицію, щоб відбулась ротація. Наприклад, 2% = купуємо нову акцію тільки якщо її сигнал щонайменше на 2% сильніший за нашу поточну позицію."
                      mode={oppGainMode} fixed={oppGainFixed} range={oppGainRange}
                      onModeToggle={() => setOppGainMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setOppGainFixed} onRange={setOppGainRange} step="0.5" />
                  </div>
                </SubBlock>

                {/* 2c: продаж лише під покупку (менеджер 15.10, опція 1) */}
                <SubBlock label="[2c] Продавати лише під покупку" enabled={swapDeferEnabled} onToggle={setSwapDeferEnabled}
                  hint="Сигнал продажу виконується лише тоді, коли є що купити за звичайними сигналами, а купити нема за що: немає вільного місця або грошей менше ніж на 2/3 частки. Тоді продається позиція з найбільшим прибутком, і на всі отримані гроші купується нова. Сигнал продажу має діяти на цьому самому барі."
                  warning="Відкладаються ВСІ правила продажу, включно з Hard Stop і Take Profit: поки купувати нічого, збиткова позиція не продається. Завжди виконуються лише примусові закриття — делістинг, зміна компанії під тікером, кінець періоду, вимога брокера при плечі.">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMd, marginTop: 4 }}>
                    <input type="checkbox" checked={swapDeferOffEnabled}
                      onChange={e => setSwapDeferOffEnabled(e.target.checked)} />
                    Додати варіант без цієї опції для порівняння
                  </label>
                </SubBlock>

                {/* 2d: продаж під покупку за часткою Take Profit (менеджер 15.10, опція 2) */}
                <SubBlock label="[2d] Продавати під покупку за часткою Take Profit" enabled={swapTpEnabled} onToggle={setSwapTpEnabled}
                  hint="Поки Take Profit чекає на свою ціль, позицію можна продати під покупку, якщо прибуток уже досяг заданої частки цілі. Діє лише коли купити нема за що. Може працювати разом із [2c]."
                  warning={!tpAnyEnabled ? 'Діє лише з увімкненим Take Profit [4] — зараз він вимкнений, і опція нічого не змінить.' : undefined}>
                  <RangeField
                    label="Частка цілі Take Profit, %"
                    tooltip="Яку частину цілі Take Profit має досягти прибуток, щоб позицію можна було продати під покупку. Наприклад, при Take Profit 10% і частці 80% — продаж можливий від +8%."
                    mode={swapTpMode} fixed={swapTpFixed} range={swapTpRange}
                    onModeToggle={() => setSwapTpMode(m => m === 'fixed' ? 'range' : 'fixed')}
                    onFixed={setSwapTpFixed} onRange={setSwapTpRange} step="5" />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMd, marginTop: 8 }}>
                    <input type="checkbox" checked={swapTpOffEnabled}
                      onChange={e => setSwapTpOffEnabled(e.target.checked)} />
                    Додати варіант без цієї опції для порівняння
                  </label>
                </SubBlock>
              </AccordionBlock>

              {/* Block 3: Hold to Cost */}
              <AccordionBlock
                title={`⏳ Утримання до беззбитковості (Hold to Cost) — продати тільки в нулі або в плюсі${htcEnabled && stagnation.enabled ? ' ⚠️ конфлікт' : ''}`}
              >
                {htcEnabled && stagnation.enabled && (
                  <div style={{ fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                    <b>⚠️ Конфлікт правил продажу:</b> Hold to Cost і Stagnation Sell обидва активні.<br />
                    <b>Пріоритет: Hold to Cost має вищий пріоритет</b> — Stagnation Sell <b>не спрацює</b> поки позиція нижче ціни купівлі.<br />
                    Stagnation Sell буде виконано лише після того, як HTC відпустить позицію (ціна = cost або вичерпано max барів).
                  </div>
                )}
                {/* Block 3 — two-flag pattern (BT-3) */}
                <SubBlock label="[3] Враховувати Hold to Cost" enabled={htcEnabled} onToggle={setHtcEnabled}
                  hint="Якщо потрібно продати → чекати поки ціна не підніметься до ціни купівлі (або N барів). Має вищий пріоритет за Stagnation Sell і базове правило продажу."
                  warning="Увага: може тримати збиткові позиції довго! Базове правило продажу (Drop%) також блокується до досягнення cost або вичерпання барів.">
                  {htcEnabled && <>
                    <RangeField
                      label="Макс. барів очікування до продажу на збиток (HTC Bars)"
                      tooltip="Максимальна кількість барів, протягом яких стратегія 'чекає' поки ціна піднімається до рівня купівлі. Якщо за цей час ціна не досягла cost — позиція закривається на збиток. Захищає від нескінченного утримання збиткових позицій."
                      mode={htcBarsMode} fixed={htcBarsFixed} range={htcBarsRange}
                      onModeToggle={() => setHtcBarsMode(m => m === 'fixed' ? 'range' : 'fixed')}
                      onFixed={setHtcBarsFixed} onRange={setHtcBarsRange} step="12" integer />
                    <div style={{ marginTop: 10, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, fontWeight: 600 }}>
                        <input type="checkbox" checked={htcCostEnabled}
                          onChange={e => setHtcCostEnabled(e.target.checked)} />
                        Враховувати комісії й спред у собівартості
                      </label>
                      <div style={{ fontSize: 11, color: C.textMd, marginTop: 4 }}>
                        Hold to Cost відпускає позицію не на ціні купівлі, а на беззбитковості:
                        ціна купівлі + комісія купівлі + комісія продажу + спред, плюс націнка нижче.
                        Без цього продаж «у нуль» закривається в мінус на комісіях. Hard Stop і Take Profit не змінюються.
                      </div>
                      {htcCostEnabled && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, marginTop: 6 }}>
                          Націнка понад беззбитковість, %
                          <input type="number" min="0" step="0.05" value={htcCostMarkup}
                            onChange={e => setHtcCostMarkup(e.target.value === '' ? '' : Number(e.target.value))}
                            style={{ width: 80, padding: '3px 6px', border: `1px solid ${C.border}`, borderRadius: 4 }} />
                        </label>
                      )}
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11, color: C.textMd, background: '#F0FDF4', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #22C55E' }}>
                      <b>Пріоритет продажу (від вищого до нижчого):</b><br />
                      {ladderHs && <>1. <b>Hard Stop</b> — безумовний<br /></>}
                      {tpAnyEnabled && <>{ladderHs ? 2 : 1}. <b>Take Profit</b> [4] — поки озброєний,
                      сам глушить правила нижче<br /></>}
                      {nBase}. <b>Базове правило</b> (Drop%) і <b>No-growth</b><br />
                      {ladderStag && <>{nBase + 1}. <b>Stagnation Sell</b><br /></>}
                      <b>Hold to Cost</b> ← <b>цей блок</b> — не щабель драбини, а фільтр: скасовує вже
                      ухвалений продаж за {ladderStag ? 'правилами' : 'правилом'} {htcSuppressesLabel},
                      поки ціна &lt; cost і не вичерпано max барів.
                      {htcExemptLabel && <> {htcExemptLabel} з-під нього виключено.</>}
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, fontWeight: 600 }}>
                        <input type="checkbox" checked={swapHtcEnabled}
                          onChange={e => setSwapHtcEnabled(e.target.checked)} />
                        Дозволити продаж під покупку позиції, яку тримає Hold to Cost
                      </label>
                      <div style={{ fontSize: 11, color: C.textMd, marginTop: 4 }}>
                        Hold to Cost тримає збиткову позицію, але якщо є що купити, а грошей немає
                        (немає вільного місця або менше ніж на 2/3 частки), її продають і купують нову.
                        Разом із [2c] «Продавати лише під покупку» дає той самий результат, що й вимкнений Hold to Cost.
                      </div>
                      {swapHtcEnabled && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMd, marginTop: 6 }}>
                          <input type="checkbox" checked={swapHtcOffEnabled}
                            onChange={e => setSwapHtcOffEnabled(e.target.checked)} />
                          Додати варіант без цієї галочки для порівняння
                        </label>
                      )}
                    </div>
                  </>}
                </SubBlock>
                <SubBlock label="[3] Не враховувати Hold to Cost (baseline)" enabled={htcOffEnabled} onToggle={setHtcOffEnabled}
                  hint="У grid додасться варіант без Hold to Cost для порівняння">
                </SubBlock>
              </AccordionBlock>

              {/* Block 4: Take Profit */}
              <AccordionBlock title="🎯 Take Profit — продати при досягненні цільового прибутку">
                {/* Shared params: shown when any TP sub-block is enabled */}
                {tpAnyEnabled && (
                  <div style={{ padding: '10px 12px', marginBottom: 8, background: '#FAF5FF', borderRadius: 8, border: '1px solid #DDD6FE' }}>
                    <div style={{ fontSize: 11, color: '#5B21B6', fontWeight: 600, marginBottom: 8 }}>
                      Поки TP не спрацював: Drop%/No-growth/Stagnation пригнічені · Hard Stop активний завжди · Hold to Cost не блокує ні Hard Stop, ні TP
                    </div>
                    {tpMutesSellRules && (
                      <div style={{ fontSize: 11, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>
                        <b>⚠️ [4a] із терміном 0 вимикає правила продажу зовсім.</b> Продати одразу
                        нічим не обмежене в часі, тож поки ціль не досягнута, правило озброєне на
                        кожному барі — Drop%, No-growth і Stagnation не спрацьовують ЖОДНОГО разу.
                        Вийти позиція зможе лише тим, що TP не глушить: сам Take Profit, Hard Stop,
                        захист на ніч/вихідні, ротація та можливість, події шарів реалізму, кінець періоду.
                        {tpMutedAxes.length > 0 && (
                          <><br /><b>Ці осі дадуть однакові рядки{tpMutedHalfOnly ? ' у половині сітки з [4a]' : ''}:</b>{' '}
                          {tpMutedAxes.join(' · ')}. Поставте термін дії більший за 0 або
                          перебирайте їх окремою сіткою без [4a].</>
                        )}
                        <br />Механізму [4b] це не стосується: після досягнення цілі правила вмикаються назад.
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                      <RangeField
                        label="Ціль прибутку, % від ціни купівлі"
                        tooltip="TP спрацьовує коли pnl_pct ≥ take_profit_pct."
                        mode={tpPctMode} fixed={tpPctFixed} range={tpPctRange}
                        onModeToggle={() => setTpPctMode(m => m === 'fixed' ? 'range' : 'fixed')}
                        onFixed={setTpPctFixed} onRange={setTpPctRange} step="1" />
                      <RangeField
                        label="Термін дії правила (барів; 0 = нескінченно)"
                        tooltip="Скільки барів після купівлі TP-правило активне. 0 = ніколи не відключається. ~720 барів ≈ 1 місяць."
                        mode={tpExpiryMode} fixed={tpExpiryFixed} range={tpExpiryRange}
                        onModeToggle={() => setTpExpiryMode(m => m === 'fixed' ? 'range' : 'fixed')}
                        onFixed={setTpExpiryFixed} onRange={setTpExpiryRange} step="360" integer />
                    </div>
                  </div>
                )}

                {/* 4a: Sell immediately */}
                <SubBlock label="[4a] Продати одразу (Sell Immediately, Мех. 1)" enabled={tpImmediateEnabled} onToggle={setTpImmediateEnabled}
                  hint="Досягли +X% → негайний продаж. Простий фіксований Take Profit.">
                  {tpImmediateEnabled && (
                    <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                      ⚡ При +{tpPctFixed}% від ціни купівлі — продати одразу
                    </div>
                  )}
                </SubBlock>

                {/* 4b: Let it run */}
                <SubBlock label="[4b] Продовжити після TP (Let It Run, Мех. 2)" enabled={tpLetItRunEnabled} onToggle={setTpLetItRunEnabled}
                  hint="Досягли +X% → знімаємо захист, тримаємо далі. Drop%/Stagnation починають слідкувати — продаємо на відкаті від піку.">
                  {tpLetItRunEnabled && (
                    <div style={{ fontSize: 11, color: C.textMd, background: '#EDE9FE', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #7C3AED' }}>
                      🔓 При +{tpPctFixed}% — тримаємо далі; Drop%/Stagnation активуються після досягнення TP
                      {(tpExpiryFixed > 0 || tpExpiryMode === 'range') && <><br />• Після закінчення терміну: стандартні правила активні без TP</>}
                    </div>
                  )}
                </SubBlock>
              </AccordionBlock>

              {/* Block 5: Margin 2:1 */}
              <AccordionBlock title="🏦 Маржинальне плече 2:1 — торгівля з кредитом брокера">
                <SubBlock label="[5] Плече (Margin)" enabled={marginEnabled} onToggle={setMarginEnabled}
                  hint="Торгівля в кредит: купівельна спроможність = плече × власний капітал − вартість позицій, борг зростає на відсотки щодня, а при пробитті порогу автоматично продаються найприбутковіші позиції, доки борг не повернеться в норму. Той самий алгоритм, що в живого бота. ⚠️ Плече не впливає на те, ЯКА стратегія краща — шукайте без нього, а драбину значень 1→2 переганяйте на знайденому рядку: сторінка результату → «Драбина умов». Там кожен щабель рахується двічі: чистий кредит без обмежень і кредит разом із реальною маржинальністю. ⚠️ Під що брокер дає кредит, береться з РЕАЛЬНИХ вердиктів IBKR (2886 паперів, опитано 25.08), а для тікерів без вердикту — зі смуги обігу, де залежність виміряна: 9% при обігу до $1 млн проти 93.6% понад $100 млн. Старе правило «дешевші за $5 не маржинальні» більше не використовується: воно вгадувало 69% і помилялось у бік щедрості — 865 дорогих паперів отримували плече, якого брокер під них не дає, проти 18 дешевих, яким його дарма забороняли (JBLU, OPEN, PLUG, AMC). ⚠️ Плече понад 2:1 через ніч буває лише на portfolio margin (від $110 тис. на рахунку), тож у симуляції стеля вище 2 діє лише поки капітал вище порогу.">
                  {marginEnabled && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 16px', marginBottom: 8 }}>
                        {[
                          { key: 'cap',            label: 'Плече (cap)', step: 0.1, max: 2,
                            tip: 'Позиції ≤ cap × власний капітал. Стеля 2.0 — це межа IBKR за Reg-T для позицій, які переносяться через ніч; 4:1 буває лише всередині дня на PDT-рахунках, а наші позиції живуть днями, тож більше за 2 ввести не можна. Виміряно, що оптимум зазвичай нижчий за стелю.' },
                          { key: 'maintPct',       label: 'Maintenance-поріг, %', step: 1,
                            tip: 'Вимога брокера (IBKR ≈ 25%). Нижче — margin call: рахуємо як подію (blow-up прапор), ліквідацію брокера не моделюємо.' },
                          { key: 'interestAprPct', label: 'Ставка кредиту (APR), %', step: 0.5,
                            tip: 'Щоденні відсотки на борг: borrowed × APR/360, раз на день.' },
                          { key: 'delevStartPct',  label: 'Початок продажів (SAFE), %', step: 1,
                            tip: 'Делеверидж стартує коли equity < SAFE × позиції. Бот: 30% (25% maintenance + 5% буфер).' },
                          { key: 'delevStopPct',   label: 'Кінець продажів (TARGET), %', step: 1,
                            tip: 'Продаємо мінімум (best-P&L first, часткові) до equity ≥ TARGET × позиції. Бот: 35%.' },
                          // Поле «Мін. ціна marginable» прибране з форми 27.08: нові
                          // прогони судять маржинальність за реальними вердиктами
                          // брокера, тож це число ні на що не впливало б. Значення
                          // й далі їде в тілі запиту — воно потрібне, якщо модель
                          // явно перемкнути на 'price' (env MARGIN_MARGINABILITY).
                        ].map(f => (
                          <label key={f.key} style={{ fontSize: 11, color: C.textMd }}>
                            <span style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                              {f.label}<Tooltip text={f.tip} />
                            </span>
                            <input
                              type="number" step={f.step} max={f.max} value={marginCfg[f.key]}
                              onChange={e => setMarginCfg(prev => {
                                if (e.target.value === '') return { ...prev, [f.key]: '' };
                                const v = Number(e.target.value);
                                return { ...prev, [f.key]: f.max != null && v > f.max ? f.max : v };
                              })}
                              style={{ width: '100%', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                            />
                          </label>
                        ))}
                      </div>
                      <AxisRangeRow
                        label="Перебирати плече діапазоном"
                        range={marginCapRange} setRange={setMarginCapRange}
                        step={0.1} count={marginCapDim}
                        note="Замість одного значення плеча перебирає діапазон — кожне значення окремою комбінацією. Фіксоване поле «Плече (cap)» вище при цьому не використовується. Межі ті самі: більше за 1 і не більше за 2. ⚠️ Це множник до всієї сітки: 10 значень плеча = удесятеро більше комбінацій, і всі вони рахуються на процесорі. Те саме можна подивитись дешевше на вже знайденому рядку — сторінка результату, «Драбина умов»."
                      />
                      <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                        ⚠️ Обмеження денної торгівлі (PDT) не моделюється. Порядок порогів: підтримка ≤ SAFE &lt; TARGET. Рахується на процесорі.
                      </div>
                    </>
                  )}
                </SubBlock>
              </AccordionBlock>

              {/* Block 6: Реалізм ліквідності R2 */}
              <AccordionBlock title="💧 Реалізм ліквідності — обіг торгів, розмір заявки, набір частинами">
                <SubBlock label="[6] Ліквідність — обіг і ліміт заявки" enabled={liqEnabled} onToggle={setLiqEnabled}
                  hint="Не купує паперів, якими фактично не торгували, і не дає заявці перевищити задану частку денного обігу. Обіг рахується за попередні дні — поточний день не враховується. Право продати позицію обігом НЕ обмежується: папір, що випав із фільтра, не зависає. Але два перемикачі нижче на продаж таки впливають — плата за вплив здорожчує і продаж теж, а «виходити частинами» ділить вихід по днях.">
                  {liqEnabled && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 16px', marginBottom: 8 }}>
                        {[
                          { key: 'minAdvUsd', label: 'Мін. обіг, $/день', step: 250000,
                            tip: 'Медіанний денний доларовий обіг у вікні перед сигналом. Тікер нижче порогу не є кандидатом на цьому барі, але може стати ним пізніше. 0 = вимкнено. Дефолт $1.75M = $250k медіанного годинного × 7 барів RTH.' },
                          { key: 'positionAdvCapPct', label: 'Ліміт заявки, % обігу', step: 0.5,
                            tip: 'Позиція обрізається до cap × денний обіг. 1% — нижній край безпечної зони. Якщо обрізане менше за 2/3 планової позиції, покупка пропускається взагалі й решта лишається в кеші — крім випадку, коли ввімкнено [6.1]: тоді позиція набирається частинами. 0 = вимкнено.' },
                          { key: 'maxFlatBarPct', label: 'Макс. пласких барів, %', step: 5,
                            tip: 'Частка барів, де high == low (котирування є, торгів немає). Виміряно: фантоми 78–98%, реальні дорогі папери з малим free float — до 59%. 0 = вимкнено.' },
                          { key: 'advWindowDays', label: 'Вікно обігу, днів', step: 1,
                            tip: 'Скільки торгових днів перед сигналом бере медіана. Поточний день не входить — без зазирання вперед.' },
                          { key: 'impactBpsPerPct', label: 'Вплив на ціну, б.п. / 1%', step: 1,
                            tip: 'Сліпедж, що росте з частки обігу: заявка на 1% обігу дорожчає на стільки базисних пунктів. Діє лише коли ввімкнено перемикач нижче.' },
                        ].map(f => (
                          <label key={f.key} style={{ fontSize: 11, color: C.textMd }}>
                            <span style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                              {f.label}<Tooltip text={f.tip} />
                            </span>
                            <input
                              type="number" step={f.step} value={liqCfg[f.key]}
                              onChange={e => setLiqCfg(prev => ({ ...prev, [f.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
                              style={{ width: '100%', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                            />
                          </label>
                        ))}
                      </div>
                      <AxisRangeRow
                        label="Перебирати мінімальний обіг діапазоном"
                        range={minAdvRange} setRange={setMinAdvRange}
                        step={250000} count={minAdvDim}
                        note="Замість одного порога обігу перебирає діапазон — кожне значення окремою комбінацією. Фіксоване поле «Мін. обіг» вище при цьому не використовується. ⚠️ Це множник до всієї сітки, і поріг обігу міняє САМ СКЛАД торгованих компаній, тож рядки з різними порогами — це різні всесвіти, а не та сама стратегія з іншим числом."
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMd, marginBottom: 8 }}>
                        <input type="checkbox" checked={impactEnabled} onChange={e => setImpactEnabled(e.target.checked)} />
                        Враховувати плату за вплив на ціну
                        <Tooltip text="Вимкнено за замовчуванням, щоб окремо бачити, скільки знімає кожне обмеження. Ліміт розміру вже не дає неможливих заявок; ця плата додає вартість самих великих." />
                      </label>

                      {/* Пріоритет черги купівлі (менеджер 2026-08-19). Живе тут,
                          бо «обіг» і «сплеск» рахуються з даних цього ж блоку. */}
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: C.textMd, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                          Кого купувати першим, коли кандидатів більше, ніж вільних місць
                          <Tooltip text="Коли сигнал спрацював одразу в кількох компаній, а вільних місць у портфелі менше, хтось лишається за бортом. Це правило вирішує, хто саме. Обрати можна кілька — тоді кожен варіант рахується окремо і множить кількість комбінацій." />
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {BUY_PRIORITIES.map(bp => {
                            const active = buyPriorities.includes(bp.key);
                            return (
                              <button
                                key={bp.key}
                                type="button"
                                onClick={() => setBuyPriorities(prev => {
                                  const next = prev.includes(bp.key)
                                    ? prev.filter(x => x !== bp.key)
                                    : [...prev, bp.key];
                                  // Порожній вибір нічого не означав би — лишаємо
                                  // поведінку, що була завжди.
                                  return next.length ? next : ['dip'];
                                })}
                                style={{
                                  padding: '4px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                                  border: `1px solid ${active ? C.primary : C.border}`,
                                  background: active ? C.primaryLt : C.cardBg,
                                  color: active ? C.primary : C.textMd,
                                  fontWeight: active ? 600 : 400,
                                }}
                                title={bp.tip}
                              >{bp.label}</button>
                            );
                          })}
                        </div>
                        {buyPriorities.length > 1 && (
                          <div style={{ fontSize: 11, color: C.textMd, marginTop: 4 }}>
                            Обрано {buyPriorities.length} — кількість комбінацій множиться на {buyPriorities.length}.
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMd, background: '#DBEAFE', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #3B82F6' }}>
                        ℹ️ Закриває лише обіг торгів. Капіталізація на момент угоди — блок [7], компанії, що зникли з біржі — блок [8]. Рахується на процесорі (без відеокарти), тож тримайте сітку в тисячах комбінацій, а не в сотнях тисяч.
                      </div>
                    </>
                  )}
                </SubBlock>

                <SubBlock label="[6.1] Набирати позицію частинами" enabled={sliceEnabled} onToggle={setSliceEnabled}
                  hint="Якщо ліміт заявки вище не дає купити позицію цілком, вона набирається частинами: одна частина на торговий день, поки не набереться планований розмір. Без цього покупка, обрізана більш ніж на третину, просто пропускається, і на паперах із малим обігом позицій не буває. Ціна входу — зважена середня, вік позиції рахується від першої частини.">
                  {sliceEnabled && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 8 }}>
                        {[
                          { key: 'minPositionUsd', label: 'Мін. частина, $', step: 50,
                            tip: 'Частина, дрібніша за це, не купується. Орієнтир $350: комісія $2 туди-назад проти медіанної угоди +1.9% зʼїдає близько 30% прибутку з такої частини. 0 = вимкнено.' },
                          { key: 'minSharePrice', label: 'Мін. ціна акції, $', step: 0.25,
                            tip: 'Папір дешевший не купується. $1.75 — складова комісії $0.005 за акцію з\'їдає прибуток незалежно від розміру позиції (на $0.50 це 105% прибутку). 0 = вимкнено.' },
                        ].map(f => (
                          <label key={f.key} style={{ fontSize: 11, color: C.textMd }}>
                            <span style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                              {f.label}<Tooltip text={f.tip} />
                            </span>
                            <input
                              type="number" step={f.step} value={sliceCfg[f.key]}
                              onChange={e => setSliceCfg(prev => ({ ...prev, [f.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
                              style={{ width: '100%', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                            />
                          </label>
                        ))}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMd, marginBottom: 8 }}>
                        <input type="checkbox" checked={partialSells} onChange={e => setPartialSells(e.target.checked)} />
                        Виходити з позиції теж частинами
                        <Tooltip text="Вихід теж обмежується денним обігом: за день продається не більше того, що ринок поглинає, решта довиходить наступними днями — і лише поки правило продажу спрацьовує. Якщо папір відскочив і умови виходу більше немає, решта лишається в портфелі. Собівартість решти і вік позиції не міняються. Кінець періоду, делістинг і маржинальний делеверидж продаються повністю завжди." />
                      </label>
                      <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                        ⚠️ Має сенс лише з увімкненим лімітом заявки вище: без обрізання ділити нічого.
                        <br />⚠️ Почне впливати приблизно від $70 тис. на рахунку. Частина заявки — 1% денного обігу, тож при мінімальному обігу $1.75 млн це $17.5 тис.; на менших рахунках заявка в ліміт не впирається.
                        <br />ℹ️ Кожна наступна частина перевіряє умову заново, на своєму дні й за ціною свого дня: частина купується лише якщо папір саме тоді пройшов би повний вхід (зростання, фільтри падіння й перекупленості, капіталізація, обіг). Якщо умови немає — частини цього дня немає, а план набору лишається: повернеться умова, добереться позиція. Так само на виході: решта позиції продається лише поки правило продажу тримається. Рахується на процесорі.
                      </div>
                    </>
                  )}
                </SubBlock>
              </AccordionBlock>

              {/* Block 7: Архівна капіталізація R4 */}
              <AccordionBlock title="🕰️ Архівна капіталізація — всесвіт на момент угоди">
                <SubBlock label="[7] Капіталізація на момент угоди" enabled={pitEnabled} onToggle={setPitEnabled}
                  hint="Капіталізація перевіряється на кожному барі за звітністю того часу, а не за сьогоднішнім зрізом. Компанія стає кандидатом лише в ті періоди, коли справді належала до обраного класу — і навпаки, компанія, що тоді була великою, а зараз ні, у бектест потрапляє. Продаж не обмежується.">
                  {pitEnabled && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMd, marginBottom: 8 }}>
                        <input type="checkbox" checked={pitRequireData} onChange={e => setPitRequireData(e.target.checked)} />
                        Тікери без архівних даних не торгувати
                        <Tooltip text={`Архівна капіталізація є для ${delistCoverage.mcap_coverage_pct ?? '…'}% ростера; решта — переважно фонди, ETF та компанії, відсутні в реєстрах SEC. Увімкнено: такий папір не торгується взагалі. Вимкнено: для нього діє сьогоднішня капіталізація.`} />
                      </label>
                      <div style={{ fontSize: 11, color: C.textMd, background: '#DBEAFE', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #3B82F6' }}>
                        ℹ️ Поріг береться з тих самих налаштувань капіталізації вище — обраних класів (Mega/Large/Mid/Small) або полів «від/до» — тут змінюється лише момент перевірки, задавати його вдруге не треба. Дані місяця беруться з попереднього, щоб не зазирати вперед. Джерело — звітність SEC EDGAR. Рахується на процесорі.
                        <br />⚠️ Архівні дані є з <b>{delistCoverage.mcap_first_month || '…'}</b>. Оскільки місяць береться з попереднього, прогін, що починається цим самим місяцем, матиме його <b>без жодної компанії</b> — задавайте початок хоча б на місяць пізніше.
                      </div>
                    </>
                  )}
                </SubBlock>
              </AccordionBlock>

              {/* Block 8: Делістинги R3d */}
              <AccordionBlock title="🪦 Делістинги — компанії, які зникли">
                <SubBlock label="[8] Враховувати компанії, що зникли з біржі" enabled={delistEnabled} onToggle={setDelistEnabled}
                  hint="Впускає в бектест компанії, що зникли з біржі. Позиція закривається в день останньої торгівлі: банкрутство — за нулем, поглинання — за останньою ціною (премія вже в ній). Без цього всесвіт складається лише з компаній, які дожили до сьогодні. Джерело — форми SEC 25/25-NSE.">
                  {delistEnabled && (
                    <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                      ⚠️ <b>Котирування зниклих компаній є з {delistCoverage.coverage_floor || '…'}</b>. Якщо початок періоду раніше — частина із них не матиме історії, і результат буде завищеним.
                      <br />Доступно <b>{delistCoverage.events ?? '…'}</b> подій із {delistCoverage.known ?? '…'} відомих за формами SEC. Компанії, для яких тип події невідомий, не допускаються: «поглинання» і «банкрутство» дають протилежний результат, тож вгадувати не можна.
                      <br />⚠️ Вмикайте разом із <b>[6] Ліквідністю</b> — у зниклих компаній немає метаданих, тож фільтр капіталізації їх не бачить і в бектест потрапляють субцентові папери. Рахується на процесорі.
                    </div>
                  )}
                </SubBlock>

                <SubBlock label="[8.1] Враховувати зміну компанії під тікером" enabled={reorgEnabled} onToggle={setReorgEnabled}
                  hint="Під одним тікером із часом можуть торгуватись різні компанії: вихід із банкрутства зі списанням старих акцій, злиття з порожньою оболонкою, виділення. У ряді цін це виглядає як звичайне продовження, тож позиція, яку тримають через цю дату, приносить прибуток від паперу, якого власник не мав. Позиція закривається на останньому дні старої компанії, а новий папір стає доступним для купівлі, щойно набере власну історію. Джерело — дата появи інструмента у довіднику біржі.">
                  {reorgEnabled && (
                    <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                      ⚠️ Числа зміняться проти прогонів без цього прапорця — порівнювати їх між собою не можна.
                      <br />ℹ️ Відомо <b>{delistCoverage.reorg_events ?? '…'}</b> таких змін, із них підтверджено формами SEC <b>{delistCoverage.reorg_classified ?? '…'}</b>. Ціна закриття — остання ціна старої компанії; нуль ставиться лише там, де тип події підтверджений. Рахується на процесорі.
                    </div>
                  )}
                </SubBlock>

                <div style={{ fontSize: 11, color: C.textMd, background: '#EFF6FF', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #3B82F6', marginTop: 8 }}>
                  ℹ️ Розрахунок завжди йде лише по простих акціях компаній: фонди й ETF, BDC, депозитарні розписки й варанти не беруть участі. Порожні SPAC-оболонки не купуються до дати злиття, а після великого спецдивіденду папір якийсь час не купується (вікно дорівнює найдовшому вікну стратегії). Тому розрахунок завжди йде на процесорі.
                </div>
              </AccordionBlock>

              {/* Block 9: Пер-барна належність до всесвіту (Фаза 2) */}
              <AccordionBlock title="📅 Всесвіт по барах — компанії, які з'явились пізніше">
                <SubBlock label="[9] Належність рахувати на кожному барі" enabled={perBarUniverse} onToggle={setPerBarUniverse}
                  hint="Компанія стає доступною зі свого першого бару, а не або-або на все вікно. Без цього компанія, що вийшла на біржу всередині періоду, викидається цілком — навіть за ті роки, коли вже торгувалась, — і склад всесвіту залежить від довжини вікна (старт 2020 → 2979 компаній, старт 2022 → 3682). Потрібне, якщо ви задаєте внутрішні періоди: інакше їхні числа не збігатимуться з окремим прогоном за ті самі дати.">
                  {perBarUniverse && (
                    <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                      ⚠️ Числа зміняться проти прогонів без цього прапорця — порівнювати їх між собою не можна.
                      <br />ℹ️ Покриває лише появу компанії. Її зникнення — окремий блок <b>[8] Делістинги</b>: без нього збиток від виходу з біржі не реалізується ніколи. Рахується на процесорі.
                    </div>
                  )}
                </SubBlock>
              </AccordionBlock>

              {/* Block 11: Динамічна кількість компаній */}
              <AccordionBlock title="📈 Кількість компаній — стартова, а не стеля">
                <SubBlock label="[10] Кількість компаній може зростати" enabled={dynNEnabled} onToggle={setDynNEnabled}
                  hint="«Кількість компаній» стає стартовим значенням, а не стелею: вільні кошти — від продажу компанії, залишку після часткової покупки чи поповнення — відкривають додаткові компанії понад це число. Розмір нової позиції = база ÷ дільник, де дільник — більше зі стартової кількості й тієї, що фактично утримуємо (поки компаній менше за стартове число, ділиться на стартове). Базу обираєте нижче.">
                  {dynNEnabled && (
                    <>
                      <label style={{ fontSize: 11, color: C.textMd, display: 'block', marginBottom: 8 }}>
                        <span style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                          База для розміру позиції
                          <Tooltip text="Від чого рахувати розмір нової позиції. «Акції + кеш» — від усього рахунку разом із вільними грошима. «Лише акції» — від вартості відкритих позицій, без кешу; після вдалого періоду це роздуває частку на компанію, бо база росте разом із подорожчанням позицій, тому не дефолт. Діє один із двох варіантів — той, що обраний у списку." />
                        </span>
                        <select value={dynNMode} onChange={e => setDynNMode(Number(e.target.value))}
                          style={{ width: '100%', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
                          <option value={0}>(акції + кеш) ÷ кількість компаній — дефолт</option>
                          <option value={1}>лише акції ÷ кількість компаній</option>
                        </select>
                      </label>
                      <div style={{ fontSize: 11, color: C.textMd, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                        ⚠️ Потребує увімкненого <b>[6] Ліквідність</b> із ненульовим лімітом заявки: саме ліміт робить покупку частковою і лишає вільні кошти. Без нього стартові компанії забирають увесь кеш — виміряно, що сам по собі цей прапорець не додав жодної компанії. <b>[6.1] Дроблення</b> посилює ефект, але окремо від [6] не працює й воно.
                        <br />ℹ️ Перша позиція = весь капітал ÷ стартова кількість компаній. Верхньої межі немає — обмежує лише кількість доступних тікерів. Рахується на процесорі.
                      </div>
                    </>
                  )}
                </SubBlock>
              </AccordionBlock>
          </div>

          {/* ══ Parallel + stagnation + risk + advanced settings ══════════════ */}
          <>

          {/* Parallel strategy: full-width params */}
          {selectedStrategy === 'parallel' && (
            <div style={{ marginTop: 16, marginBottom: 14 }}>
              <ParallelParams
                split={parallelSplit}
                onSplitChange={setParallelSplit}
                momentum={momentumParams}
                onMomentum={setMomentumParams}
                mr={mrParams}
                onMr={setMrParams}
                capital={Number(form.initialCapital) || 100000}
              />
            </div>
          )}

          {/* Stagnation Sell + Risk Protection side by side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: selectedStrategy !== 'parallel' ? '1fr 1fr' : '1fr',
            gap: 16, marginTop: 16, marginBottom: 14,
          }}>

            {/* Stagnation Sell */}
            {selectedStrategy !== 'parallel' && (
              <div style={{ border: `1px solid ${htcEnabled && stagnation.enabled ? '#FCD34D' : C.border}`, borderRadius: 8, padding: '12px 16px', background: stagnation.enabled ? '#EFF6FF' : C.cardBg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center' }}>
                    Продаж при стагнації (Stagnation)
                    <Tooltip text="Якщо ціна акції 'застрягла' в заданому діапазоні відносно ціни купівлі протягом N барів — продаємо. Рятує від позицій, які не рухаються ані вгору, ані вниз, заморожуючи капітал." />
                  </div>
                  {htcEnabled && stagnation.enabled && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>
                      нижчий пріоритет ніж HTC
                    </span>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={stagnation.enabled}
                    onChange={e => setStagnation(s => ({ ...s, enabled: e.target.checked }))}
                    style={{ width: 14, height: 14, accentColor: C.primary, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: stagnation.enabled ? C.primary : C.text }}>Увімкнути продаж при стагнації (Stagnation Sell)</span>
                </label>
                <div style={{ paddingLeft: 22, opacity: stagnation.enabled ? 1 : 0.45, pointerEvents: stagnation.enabled ? 'auto' : 'none' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 16px', marginBottom: 10 }}>
                      {/* Price range Min */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <label style={{ ...S.label, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                            Мін. ціновий діапазон (Stag. Min, %)
                            <Tooltip text="Нижня межа стагнаційного діапазону (відносно ціни купівлі). Наприклад, -0.5% означає: вважаємо стагнацією якщо ціна не впала нижче -0.5% від ціни купівлі." />
                          </label>
                          <button
                            onClick={() => setStagnation(prev => ({ ...prev, min_pct: { ...prev.min_pct, mode: prev.min_pct.mode === 'single' ? 'range' : 'single' } }))}
                            style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none', background: stagnation.min_pct.mode === 'range' ? '#DBEAFE' : '#F1F5F9', color: stagnation.min_pct.mode === 'range' ? C.primary : C.textMd, fontWeight: 600 }}>
                            {stagnation.min_pct.mode === 'range' ? 'Діапазон' : 'Фіксоване'}
                          </button>
                        </div>
                        {stagnation.min_pct.mode === 'single' ? (
                          <input type="number" step="0.1" style={S.input} value={stagnation.min_pct.value} onChange={e => setStagnation(s => ({ ...s, min_pct: { ...s.min_pct, value: parseFloat(e.target.value) || 0 } }))} />
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
                              <input type="number" step="any" style={S.input} value={stagnation.min_pct.min} onChange={e => setStagnation(s => ({ ...s, min_pct: { ...s.min_pct, min: e.target.value } }))} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
                              <input type="number" step="any" style={S.input} value={stagnation.min_pct.max} onChange={e => setStagnation(s => ({ ...s, min_pct: { ...s.min_pct, max: e.target.value } }))} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
                              <input type="number" step="any" min={0.01} style={S.input} value={stagnation.min_pct.step} onChange={e => setStagnation(s => ({ ...s, min_pct: { ...s.min_pct, step: e.target.value } }))} />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Price range Max */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <label style={{ ...S.label, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                            Макс. ціновий діапазон (Stag. Max, %)
                            <Tooltip text="Верхня межа стагнаційного діапазону. Наприклад, +0.5% означає: ціна 'застрягла' якщо вона залишається нижче +0.5% від ціни купівлі протягом N барів. Якщо ціна перевищила цю межу — продаж не відбувається." />
                          </label>
                          <button
                            onClick={() => setStagnation(prev => ({ ...prev, max_pct: { ...prev.max_pct, mode: prev.max_pct.mode === 'single' ? 'range' : 'single' } }))}
                            style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none', background: stagnation.max_pct.mode === 'range' ? '#DBEAFE' : '#F1F5F9', color: stagnation.max_pct.mode === 'range' ? C.primary : C.textMd, fontWeight: 600 }}>
                            {stagnation.max_pct.mode === 'range' ? 'Діапазон' : 'Фіксоване'}
                          </button>
                        </div>
                        {stagnation.max_pct.mode === 'single' ? (
                          <input type="number" step="0.1" style={S.input} value={stagnation.max_pct.value} onChange={e => setStagnation(s => ({ ...s, max_pct: { ...s.max_pct, value: parseFloat(e.target.value) || 0 } }))} />
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
                              <input type="number" step="any" style={S.input} value={stagnation.max_pct.min} onChange={e => setStagnation(s => ({ ...s, max_pct: { ...s.max_pct, min: e.target.value } }))} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
                              <input type="number" step="any" style={S.input} value={stagnation.max_pct.max} onChange={e => setStagnation(s => ({ ...s, max_pct: { ...s.max_pct, max: e.target.value } }))} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
                              <input type="number" step="any" min={0.01} style={S.input} value={stagnation.max_pct.step} onChange={e => setStagnation(s => ({ ...s, max_pct: { ...s.max_pct, step: e.target.value } }))} />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Period (bars) */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <label style={{ ...S.label, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              Період спостереження (Stag. Bars)
                              <Tooltip text="Кількість барів, протягом яких ціна повинна перебувати в стагнаційному діапазоні, щоб спрацював продаж. Більше значення = більший буфер часу (менше помилкових продажів), менше = швидша реакція на 'заморожені' позиції." />
                            </span>
                            {(() => {
                              const barsToTime = (bars, dt) => {
                                switch(dt) {
                                  case 'daily':  return bars === 1 ? ' = 1 день' : ` = ${bars} днів`;
                                  case 'hourly': {
                                    if (bars < 24) return ` = ${bars} год`;
                                    const d = Math.floor(bars / 6.5);
                                    const h = Math.round(bars % 6.5);
                                    return d > 0 ? ` = ~${d}д ${h}г` : ` = ${h}г`;
                                  }
                                  case '5m': {
                                    const mins5 = bars * 5;
                                    return mins5 < 60 ? ` = ${mins5} хв` : ` = ${(mins5/60).toFixed(1)}г`;
                                  }
                                  case '1m':
                                    return bars < 60 ? ` = ${bars} хв` : ` = ${(bars/60).toFixed(1)}г`;
                                  default: return '';
                                }
                              };
                              if (stagnation.bars.mode === 'single') {
                                return <span style={{ color: C.textSm, fontWeight: 400, marginLeft: 6 }}>{barsToTime(stagnation.bars.value, dataType)}</span>;
                              } else {
                                const tMin = barsToTime(stagnation.bars.min, dataType).replace(/^ = /, '');
                                const tMax = barsToTime(stagnation.bars.max, dataType).replace(/^ = /, '');
                                return <span style={{ color: C.textSm, fontWeight: 400, marginLeft: 6 }}>= {tMin}–{tMax}</span>;
                              }
                            })()}
                          </label>
                          <button
                            onClick={() => setStagnation(prev => ({ ...prev, bars: { ...prev.bars, mode: prev.bars.mode === 'single' ? 'range' : 'single' } }))}
                            style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer', border: 'none', background: stagnation.bars.mode === 'range' ? '#DBEAFE' : '#F1F5F9', color: stagnation.bars.mode === 'range' ? C.primary : C.textMd, fontWeight: 600 }}>
                            {stagnation.bars.mode === 'range' ? 'Діапазон' : 'Фіксоване'}
                          </button>
                        </div>
                        {stagnation.bars.mode === 'single' ? (
                          <input
                            type="number" step="1" min="1" style={S.input}
                            value={stagnation.bars.value}
                            onChange={e => setStagnation(prev => ({ ...prev, bars: { ...prev.bars, value: parseInt(e.target.value) || 1 } }))}
                          />
                        ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Від</div>
                              <input
                                type="number" step={stagnation.bars.step} min={1} style={S.input}
                                value={stagnation.bars.min}
                                onChange={e => setStagnation(prev => ({ ...prev, bars: { ...prev.bars, min: +e.target.value } }))}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>До</div>
                              <input
                                type="number" step={stagnation.bars.step} min={stagnation.bars.min + stagnation.bars.step} style={S.input}
                                value={stagnation.bars.max}
                                onChange={e => setStagnation(prev => ({ ...prev, bars: { ...prev.bars, max: +e.target.value } }))}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 10, color: C.textSm, marginBottom: 2 }}>Крок</div>
                              <input
                                type="number" min={1} style={S.input}
                                value={stagnation.bars.step}
                                onChange={e => setStagnation(prev => ({ ...prev, bars: { ...prev.bars, step: +e.target.value } }))}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.textSm, background: '#F8FAFC', borderRadius: 6, padding: '6px 10px' }}>
                      Якщо акція торгується в діапазоні{' '}
                      <b>{stagnation.min_pct.mode === 'single' ? `${stagnation.min_pct.value}%` : `${stagnation.min_pct.min}%–${stagnation.min_pct.max}%`}</b>
                      ...
                      <b>{stagnation.max_pct.mode === 'single' ? `+${stagnation.max_pct.value}%` : `+${stagnation.max_pct.min}%–+${stagnation.max_pct.max}%`}</b>
                      {' '}протягом{' '}
                      {stagnation.bars.mode === 'single'
                        ? <b>{stagnation.bars.value}</b>
                        : <b>{stagnation.bars.min}–{stagnation.bars.max} (крок {stagnation.bars.step})</b>
                      } барів → продати
                    </div>
                    {htcEnabled && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#92400E', background: '#FEF3C7', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #F59E0B' }}>
                        <b>Увага:</b> Hold to Cost активний — Stagnation Sell <b>не спрацює</b> поки ціна позиції нижче ціни купівлі. Stagnation Sell виконається лише після того, як HTC відпустить позицію.
                      </div>
                    )}
                  </div>
              </div>
            )}

            {/* Risk Protection */}
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 16px', background: riskProt.enabled ? '#FFFBEB' : C.cardBg }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={riskProt.enabled}
                    onChange={e => setRiskProt(r => ({ ...r, enabled: e.target.checked }))}
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: C.warning }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13, color: riskProt.enabled ? C.warning : C.text, display: 'flex', alignItems: 'center' }}>
                    ⚡ Захист від ризику (Risk Protection)
                    <Tooltip text="Автоматично закриває позиції перед подіями підвищеного ризику: вихідні дні (гепи на відкритті понеділка) або нічний час (торгівля поза ринковими годинами). Зменшує просадки від геп-ризику." />
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: riskProt.enabled ? 1 : 0.45, pointerEvents: riskProt.enabled ? 'auto' : 'none' }}>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <label style={S.checkItem} title="Продати всі позиції в п'ятницю о 15:30 ET, щоб уникнути геп-ризику при відкритті в понеділок">
                      <input
                        type="checkbox"
                        checked={riskProt.sell_weekend}
                        onChange={e => setRiskProt(r => ({ ...r, sell_weekend: e.target.checked }))}
                        style={{ accentColor: C.primary }}
                      />
                      <span style={{ fontWeight: 500 }}>Перед вихідними (Weekend)</span>
                      <span style={{ fontSize: 11, color: C.textSm }}>(п'ятниця 15:30)</span>
                    </label>
                    <label style={S.checkItem} title="Продати позиції щодня о 15:45 ET, щоб уникнути нічного ризику (торгівля поза ринковими годинами)">
                      <input
                        type="checkbox"
                        checked={riskProt.sell_overnight}
                        onChange={e => setRiskProt(r => ({ ...r, sell_overnight: e.target.checked }))}
                        style={{ accentColor: C.primary }}
                      />
                      <span style={{ fontWeight: 500 }}>Щовечора (Overnight)</span>
                      <span style={{ fontSize: 11, color: C.textSm }}>(щодня 15:45)</span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ ...S.label, margin: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                      Макс. збиток для продажу (Max Loss, %):
                      <Tooltip text="Продавати тільки якщо збиток позиції не перевищує цього відсотка. Якщо збиток більший — не продаємо (тримаємо до відновлення). Захищає від реалізації великих збитків на панічних розпродажах." />
                    </label>
                    <input
                      type="number"
                      style={{ ...S.input, width: 80 }}
                      value={riskProt.max_loss_to_sell_pct}
                      step="0.1" min="0" max="100"
                      onChange={e => setRiskProt(r => ({ ...r, max_loss_to_sell_pct: parseFloat(e.target.value) || 0 }))}
                    />
                    <span style={{ fontSize: 11, color: C.textSm, maxWidth: 280 }}>
                      Продати тільки якщо збиток ≤ цього %. Якщо більший — тримаємо далі.
                    </span>
                  </div>

                  <div style={{ fontSize: 11, color: C.textSm, background: '#FEF3C7', borderRadius: 6, padding: '6px 10px' }}>
                    Приклад (max_loss={riskProt.max_loss_to_sell_pct}%):
                    AAPL -0.25% → продаємо ✅ &nbsp;|&nbsp; NVDA -2% → тримаємо ❌ &nbsp;|&nbsp; MSFT +0.5% → продаємо ✅
                  </div>
                </div>
            </div>

          </div>
          {/* end stagnation + risk grid */}

          {/* Advanced Settings */}
          {selectedStrategy !== 'parallel' && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14, background: C.cardBg }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: C.primaryLt, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: C.primary }}>Додаткові налаштування (Advanced Settings)</span>
              </div>
              <div style={{ padding: '4px 16px 16px', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center' }}>
                        Логіка продажу (Sell Logic)
                        <Tooltip text="Визначає як відраховується поріг падіння (Drop%) при вирішенні про продаж. Rolling Window — перевіряє падіння за останні T-Sell барів (ковзне вікно). From Buy Price — перевіряє падіння від ціни купівлі (абсолютний стоп-лос)." />
                      </div>
                      {[
                        { val: 'rolling',  label: 'Ковзне вікно (Rolling Window)', desc: 'Продає якщо впала >= Drop% за останні T-Sell барів (наближено до класичного бектесту)' },
                        { val: 'from_buy', label: 'Від ціни купівлі (From Buy Price)', desc: 'Продає якщо впала >= Drop% від ціни купівлі' },
                      ].map(opt => (
                        <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 10, cursor: 'pointer' }}>
                          <input
                            type="radio" name="sellMode" value={opt.val}
                            checked={sellMode === opt.val}
                            onChange={() => setSellMode(opt.val)}
                            style={{ marginTop: 2, accentColor: C.primary, flexShrink: 0 }}
                          />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: sellMode === opt.val ? C.primary : C.text }}>{opt.label}</div>
                            <div style={{ fontSize: 11, color: C.textSm, marginTop: 2 }}>{opt.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center' }}>
                      Внутрішньоденний сигнал (Intraday)
                      <Tooltip text="Додатковий фільтр: купуємо тільки якщо акція вже зросла на X% від відкриття поточного торгового дня (09:30 ET). Відсіює акції що просто тримаються на рівні без реального моментуму." />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: intraday.enabled ? 12 : 0, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={intraday.enabled}
                        onChange={e => setIntraday(s => ({ ...s, enabled: e.target.checked }))}
                        style={{ width: 14, height: 14, accentColor: C.primary, cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Увімкнути внутрішньоденний моментум (Intraday)</span>
                      {!intraday.enabled && (
                        <span style={{ fontSize: 11, color: C.textSm }}>Купити якщо виросла X% від відкриття дня (09:30)</span>
                      )}
                    </label>
                    {intraday.enabled && (
                      <div style={{ paddingLeft: 22 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px 16px', marginBottom: 10 }}>
                          <div>
                            <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
                      Від відкриття дня (Intraday, %)
                      <Tooltip text="Мінімальне зростання від відкриття торгового дня (09:30 ET), щоб спрацював внутрішньоденний сигнал купівлі. Наприклад, 3% = купуємо тільки якщо з початку дня акція виросла на 3% і більше." />
                    </label>
                            <input
                              type="number" step="0.5" min="0.1" style={S.input}
                              value={intraday.pct}
                              onChange={e => setIntraday(s => ({ ...s, pct: parseFloat(e.target.value) || 1.0 }))}
                            />
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: C.textSm, background: '#F8FAFC', borderRadius: 6, padding: '6px 10px' }}>
                          Купити якщо акція виросла <b>{intraday.pct}%</b> від відкриття дня (09:30) — незалежно від вчорашньої ціни.
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center' }}>
                      Пікове вікно (Peak Window)
                      <Tooltip text="Замість порівняння з 'N барів тому', порівнюємо з екстремумом (мінімумом/максимумом) за вікно. BUY: від мінімуму T-buy вікна → раніше реагуємо на відскок. SELL: від максимуму T-sell вікна → раніше фіксуємо падіння з піку." />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={usePeakWindowBuy}
                          onChange={e => setUsePeakWindowBuy(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span style={{ fontSize: 13, color: C.textMd }}>
                          <b>BUY:</b> порівнює з <b>мінімумом</b> у вікні T-buy
                          <div style={{ fontSize: 11, color: C.textSm }}>реагує раніше, але частіше входить на шумі</div>
                        </span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={usePeakWindowSell}
                          onChange={e => setUsePeakWindowSell(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span style={{ fontSize: 13, color: C.textMd }}>
                          <b>SELL:</b> порівнює з <b>максимумом</b> у вікні T-sell
                          <div style={{ fontSize: 11, color: C.textSm }}>реагує раніше, але частіше виходить на шумі</div>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center' }}>
                      Жорсткий стоп (Hard Stop)
                      <Tooltip text="Абсолютний стоп-лос: продаємо негайно якщо збиток від купівлі досяг цього % незалежно від інших правил. Після спрацювання — cooldown (не купуємо цей тікер N барів). Захищає від катастрофічних втрат на одній акції." />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <label style={{ ...S.label, display: 'flex', alignItems: 'center' }}>
                          Жорсткий стоп (Hard Stop, %)
                          <span style={{ fontSize: 11, color: C.textSm, fontWeight: 400, marginLeft: 6 }}>
                            (0 = вимкнено)
                          </span>
                        </label>
                        <input
                          type="number"
                          value={hardStopPct}
                          min={0} max={50} step={0.5}
                          placeholder="15"
                          onChange={e => setHardStopPct(e.target.value === '' ? 0 : +e.target.value)}
                          style={S.input}
                        />
                        {hardStopPct > 0 && (
                          <div style={{ fontSize: 11, color: C.textSm, marginTop: 3 }}>
                            Продати якщо збиток від купівлі ≥ {hardStopPct}%
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ ...S.label, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          Пауза після стопу (Hard Stop Cooldown, барів)
                          <span style={{ fontSize: 11, color: C.textSm, fontWeight: 400, marginLeft: 6 }}>
                            {hardStopCooldownBars > 0
                              ? `≈ ${Math.round(hardStopCooldownBars / 6.5)} днів`
                              : '(0 = вимкнено)'
                            }
                          </span>
                        </label>
                        <input
                          type="number"
                          value={hardStopCooldownBars}
                          min={0} max={500} step={1}
                          placeholder="48"
                          onChange={e => setHardStopCooldownBars(e.target.value === '' ? 0 : +e.target.value)}
                          style={S.input}
                        />
                        {hardStopCooldownBars > 0 && (
                          <div style={{ fontSize: 11, color: C.textSm, marginTop: 3 }}>
                            Не купувати цю акцію {hardStopCooldownBars} барів після Hard Stop
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          )}

          </>

          {/* Combination counter + Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px',
              background: totalCombos > 1 ? '#DBEAFE' : '#F8FAFC',
              border: `1px solid ${totalCombos > 1 ? '#BFDBFE' : C.border}`, borderRadius: 8,
            }}>
              {selectedStrategy === 'parallel' ? (
                <>
                  <span style={{ fontSize: 13, color: C.textMd }}>Комбінацій:</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>{totalCombos}</span>
                  <span style={{ fontSize: 11, color: C.textMd }}>
                    (MH: {Math.max(1, momentumParams.num_cos?.length || 1)} × MR: {Math.max(1, mrParams.num_cos?.length || 1)})
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 13, color: C.textMd }}>Комбінацій:</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: totalCombos > 1 ? C.primary : C.textMd }}>
                    {(runningBatch ? runningBatch.count : totalCombos).toLocaleString()}
                  </span>
                  {/* Total simulations badge (combos × periods) — only when periods present */}
                  {!runningBatch && additionalPeriods.length > 0 && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, color: '#7C3AED',
                      background: '#EDE9FE', borderRadius: 5, padding: '1px 7px',
                    }}>
                      {(runCombos * (1 + additionalPeriods.length)).toLocaleString()} сим
                    </span>
                  )}
                  {runningBatch && progress ? (
                    <span style={{ fontSize: 11, color: C.textMd }}>
                      ({progress.current.toLocaleString()} / {progress.total.toLocaleString()} виконано)
                    </span>
                  ) : runningBatch ? (
                    <span style={{ fontSize: 11, color: C.textMd }}>завантаження…</span>
                  ) : additionalPeriods.length > 0 ? (
                    <span style={{ fontSize: 11, color: C.textMd }}>
                      {boolCombos > 1
                        ? `${baseCombos.toLocaleString()} баз × ${boolCombos.toLocaleString()} bool × (1+${additionalPeriods.length})`
                        : `${totalCombos.toLocaleString()} комбо × (1+${additionalPeriods.length} periods)`}
                    </span>
                  ) : boolCombos > 1 ? (
                    <span style={{ fontSize: 11, color: C.textMd }}>
                      ({baseCombos.toLocaleString()} базових × {boolCombos.toLocaleString()} булевих)
                    </span>
                  ) : null}
                  {!runningBatch && timeEstimate && (
                    <span style={{ fontSize: 11, color: timeEstimate.color, fontWeight: 600 }}
                          title={timeEstimate.basis}>
                      · {timeEstimate.text}
                    </span>
                  )}
                  {!runningBatch && totalCombos > 1 && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      background: canUseGpu ? '#DCFCE7' : mixedGpuCpu ? '#DBEAFE' : '#FEF9C3',
                      color:      canUseGpu ? '#15803D' : mixedGpuCpu ? '#1D4ED8' : '#92400E',
                    }}>
                      {canUseGpu ? 'GPU' : mixedGpuCpu ? 'GPU+CPU' : 'CPU'}
                    </span>
                  )}
                  {runningBatch && progress && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      background: '#DCFCE7', color: '#15803D',
                    }}>
                      {Math.round((progress.current / Math.max(1, progress.total)) * 100)}%
                    </span>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Зберегти топ:</label>
              <select
                value={saveTopN}
                onChange={e => setSaveTopN(e.target.value)}
                disabled={loading}
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                <option value="100">100</option>
                <option value="500">500</option>
                <option value="1000">1 000</option>
                <option value="5000">5 000</option>
                <option value="all">Всі</option>
              </select>
              {saveTopN === 'all' && totalCombos > 100_000 && (
                <span style={{ fontSize: 11, color: '#B45309', fontWeight: 600 }}>
                  ⚠️ ~{Math.round(totalCombos / 1000)}k записів
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Мін. Avg.Yr% доходність:</label>
              <input
                type="number"
                step="0.1"
                value={minReturnPct}
                onChange={e => setMinReturnPct(e.target.value)}
                disabled={loading}
                placeholder="без фільтра"
                title="Зберігати лише стратегії, у яких Avg. Yr% (середня річна дохідність по періодах; якщо періодів немає — річна за все вікно) ≥ вказаного %. Порожньо = зберегти всі. 0 = лише прибуткові (≥ 0%)."
                style={{ width: 80, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text }}
              />
              <SignToggle value={minReturnPct} onChange={setMinReturnPct} disabled={loading} />
            </div>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                color: strategyType === 'advanced' ? C.textMd : C.textSm,
                whiteSpace: 'nowrap',
                cursor: strategyType === 'advanced' ? 'pointer' : 'not-allowed',
              }}
              title={strategyType === 'advanced'
                ? `Комбінація перевіряється по періодах і відкидається, щойно порушено правило: ${describeCullRule(cullFloorPct, cullLossTolPct, additionalPeriods.length)}. Решта періодів для неї не рахується, у таблицю вона не потрапляє. При нулях в обох полях це «прибуткова в усіх періодах»; поле «гірший за» задає глибину, після якої період закриває комбінацію одразу, «допустимо збиткових» — скільки мінусових періодів пробачається.`
                : 'Доступно для розширеного пошуку (коли увімкнено хоча б один механізм із блоків нижче).'}
            >
              <input
                type="checkbox"
                checked={cullNegativePeriods}
                onChange={() => setCullNegativePeriods(v => !v)}
                disabled={loading || strategyType !== 'advanced'}
              />
              Відсіювати за періодами
            </label>
            {cullNegativePeriods && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>
                    Відкидати, якщо період гірший за:
                  </label>
                  <input
                    type="number" step="1" value={cullFloorPct}
                    onChange={e => setCullFloorPct(e.target.value)}
                    disabled={loading}
                    title="Період із річною дохідністю нижче цього числа закриває комбінацію ОДРАЗУ, скільки б збиткових ще не дозволяв допуск. 0 = будь-який мінус — вирок. −20 = дозволяємо просідання до −20%, глибше ні."
                    style={{ width: 64, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text }}
                  />
                  <SignToggle value={cullFloorPct} onChange={setCullFloorPct} disabled={loading} />
                  <span style={{ fontSize: 12, color: C.textSm }}>%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>
                    Допустимо збиткових періодів:
                  </label>
                  <input
                    type="number" step="5" min="0" max="100" value={cullLossTolPct}
                    onChange={e => setCullLossTolPct(e.target.value)}
                    disabled={loading}
                    title="Скільки періодів дозволено мати в мінусі (але не нижче порогу вище), у % від їх кількості. Рахується разом із головним вікном і округлюється ВНИЗ: 20% від 12 періодів = 2 пробачених, третій збитковий закриває комбінацію. 0 = жодного."
                    style={{ width: 56, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text }}
                  />
                  <span style={{ fontSize: 12, color: C.textSm }}>%</span>
                </div>
                <span style={{ fontSize: 12, color: C.textSm, whiteSpace: 'nowrap' }}
                      title="Фактичне правило з поточними числами. Кількість пробачених рахується від усіх періодів разом із головним вікном, з округленням униз — так само, як у рушії.">
                  → {describeCullRule(cullFloorPct, cullLossTolPct, additionalPeriods.length)}
                </span>
                {/* Поріг рушій порівнює як Є: +30 вибраковує кожну комбінацію, у
                    якої хоч одне вікно дало менше ПЛЮС 30 % річних. Спіймано
                    24.09: сітка пішла з +30, тоді як усі попередні — з −30,
                    а підпис вище додатного порогу не показує взагалі. */}
                {parseFloat(cullFloorPct) > 0 && (
                  <span style={{ fontSize: 12, color: '#B45309', fontWeight: 600 }}
                        title="Рушій вибраковує комбінацію, щойно будь-яке вікно дало річну дохідність нижчу за цей поріг.">
                    ⚠️ Поріг додатний: вибраковується кожна комбінація, у якої хоч одне
                    вікно дало менше +{parseFloat(cullFloorPct)}% річних. Для «не глибше
                    мінус {parseFloat(cullFloorPct)}%» введіть −{parseFloat(cullFloorPct)}.
                  </span>
                )}
              </>
            )}
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                color: strategyType === 'advanced' ? C.textMd : C.textSm,
                whiteSpace: 'nowrap',
                cursor: strategyType === 'advanced' ? 'pointer' : 'not-allowed',
              }}
              title={'Етап 1: усі комбінації рахуються лише на частині періодів (рівномірно по всій історії). '
                + 'Етап 2: найкращі за Avg. Yr дораховуються на решті періодів, у тих самих рядках — Avg. Yr перезаписується. '
                + 'Правила відсіву діють на кожному етапі від його періодів: при допуску 25 % на етапі 1 — не більше 25 збиткових зі 100, '
                + 'на етапі 2 — не більше 25 % від усіх періодів разом з етапом 1. '
                + 'Ціна прискорення: стратегія, що пройшла б усі періоди, може вибути на етапі 1 (випадково більше збиткових у своїй сотні) '
                + 'або не потрапити в топ за Avg. Yr. '
                + '«Мін. Avg.Yr%» і «Зберегти топ» застосовуються після етапу 2.'}
            >
              <input
                type="checkbox"
                checked={twoStageEnabled}
                onChange={() => setTwoStageEnabled(v => !v)}
                disabled={loading || strategyType !== 'advanced'}
              />
              Двоетапний пошук
            </label>
            {twoStageEnabled && strategyType === 'advanced' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>
                    Етап 1: періодів
                  </label>
                  <input
                    type="number" step="10" min="1" value={twoStagePeriods}
                    onChange={e => setTwoStagePeriods(e.target.value)}
                    disabled={loading}
                    title="Скільки періодів рахувати для ВСІХ комбінацій. Беруться рівномірно з усього набору періодів."
                    style={{ width: 64, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>
                    На етап 2: топ
                  </label>
                  <input
                    type="number" step="100" min="1" value={twoStageTopN}
                    onChange={e => setTwoStageTopN(e.target.value)}
                    disabled={loading}
                    title="Скільки найкращих за Avg. Yr після етапу 1 дораховувати на всіх періодах. Решта видаляються."
                    style={{ width: 72, fontSize: 12, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', color: C.text }}
                  />
                  <span style={{ fontSize: 12, color: C.textSm, whiteSpace: 'nowrap' }}>за Avg. Yr</span>
                </div>
                <span style={{ fontSize: 12, color: twoStageActive ? C.textSm : '#B45309', whiteSpace: 'nowrap' }}>
                  {twoStageActive
                    ? `→ ${twoStageK} з ${additionalPeriods.length} періодів, потім топ ${twoStageN.toLocaleString()} — на решті ${additionalPeriods.length - twoStageK}`
                      + (cullNegativePeriods
                        ? ` · етап 1: ${describeCullRule(cullFloorPct, cullLossTolPct, twoStageK)}`
                        : '')
                    : `⚠️ Періодів (${additionalPeriods.length}) має бути більше, ніж на етапі 1 — інакше пошук звичайний`}
                </span>
              </>
            )}
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                color: strategyType === 'advanced' ? C.textMd : C.textSm,
                whiteSpace: 'nowrap',
                cursor: strategyType === 'advanced' ? 'pointer' : 'not-allowed',
              }}
              title={'Не весь простір комбінацій, а поколіннями: перше покоління — випадкові точки, '
                + 'кожне наступне — наполовину невеликі зсуви навколо найкращих знайдених, наполовину нові випадкові. '
                + 'На готових сітках по 196 000 комбінацій так знаходились стратегії з топ-10 сітки за ~1 000 точок '
                + 'замість ~13 500 у випадкової вибірки. Ціна: знаходяться найкращі, але не ВСІ прохідні стратегії. '
                + 'Діє, коли комбінацій більше за бюджет; інакше рахується звичайний повний перебір. '
                + 'Разом із двоетапним: покоління рахуються на періодах етапу 1, а коли бюджет вичерпано, '
                + 'топ за Avg. Yr з УСІХ знайдених дораховується на решті періодів.'}
            >
              <input
                type="checkbox"
                checked={smartEnabled}
                onChange={() => setSmartEnabled(v => !v)}
                disabled={loading || strategyType !== 'advanced'}
              />
              Смарт-пошук
            </label>
            {smartEnabled && strategyType === 'advanced' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Бюджет, точок</label>
                  <input type="number" step="1000" min="100" value={smartBudget}
                    onChange={e => setSmartBudget(e.target.value)} disabled={loading}
                    title="Скільки комбінацій порахувати всього. Решта простору не рахується."
                    style={{ ...SMART_INPUT, width: 80 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Покоління</label>
                  <input type="number" step="100" min="50" value={smartGenSize}
                    onChange={e => setSmartGenSize(e.target.value)} disabled={loading}
                    title="Скільки комбінацій в одному поколінні. Одночасно рахуються два покоління, щоб парк не простоював на хвості."
                    style={SMART_INPUT} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Випадкових, %</label>
                  <input type="number" step="10" min="0" max="100" value={smartExplorePct}
                    onChange={e => setSmartExplorePct(e.target.value)} disabled={loading}
                    title="Частка нових випадкових точок у поколінні. Решта — зсуви навколо найкращих. 0 = лише навколо знайдених (ризик застрягти в першій купці), 100 = звичайна випадкова вибірка."
                    style={{ ...SMART_INPUT, width: 56 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ fontSize: 12, color: C.textMd, whiteSpace: 'nowrap' }}>Навколо топ</label>
                  <input type="number" step="5" min="1" value={smartTopK}
                    onChange={e => setSmartTopK(e.target.value)} disabled={loading}
                    title="Навколо скількох найкращих рядків шукати в кожному поколінні."
                    style={{ ...SMART_INPUT, width: 56 }} />
                  <select value={smartMetric} onChange={e => setSmartMetric(e.target.value)} disabled={loading}
                    title="За якою метрикою визначати найкращих."
                    style={{ ...SMART_INPUT, width: 'auto' }}>
                    <option value="avg_yr">за Avg. Yr</option>
                    <option value="worst_yr">за Worst Yr</option>
                    <option value="main_yr">за Yearly (головне вікно)</option>
                  </select>
                </div>
                <span style={{ fontSize: 12, color: smartActive ? C.textSm : '#B45309', whiteSpace: 'nowrap' }}>
                  {smartActive
                    ? `→ ${smartBudgetN.toLocaleString()} з ${totalCombos.toLocaleString()} комбінацій (${(smartBudgetN * 100 / totalCombos).toFixed(totalCombos > smartBudgetN * 100 ? 2 : 1)} %)`
                      + (twoStageActive ? ` · потім топ ${twoStageN.toLocaleString()} — на решті періодів` : '')
                    : `⚠️ Комбінацій (${totalCombos.toLocaleString()}) не більше за бюджет — порахується повний перебір`}
                </span>
              </>
            )}
            {(() => {
              const blocked = strategyType === 'advanced' && periodsOutside.count > 0;
              return (
                <button
                  style={{ ...S.btnPrimary, opacity: (loading || blocked) ? 0.6 : 1 }}
                  onClick={() => handleRun()}
                  disabled={loading || blocked}
                  title={blocked ? `${periodsOutside.count} періодів поза головним вікном — розширте вікно (кнопка біля періодів)` : undefined}
                >
                  {loading ? (runningBatch ? `Виконується (${runningBatch.count} комбо)…` : 'Запуск…')
                   : blocked ? '⛔ Періоди поза головним вікном' : '▶ Запустити прорахунок'}
                </button>
              );
            })()}
            <button style={S.btnOutline} onClick={handleReset}>↺ Reset</button>
          </div>

          {/* Коментар до сітки: потрапляє на плашку «📊 Створено …», там же
              правиться кнопкою «i». */}
          {!runningBatch && (
            <div style={{ marginTop: 8 }}>
              <textarea
                value={runComment}
                onChange={e => setRunComment(e.target.value)}
                disabled={loading}
                rows={2}
                placeholder="Коментар до прорахунку (необовʼязково): напр. «Mega+Large+Mid + hold to cost діапазоном + 10 компаній»"
                style={{
                  width: '100%', maxWidth: 720, boxSizing: 'border-box', fontSize: 12.5,
                  padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`,
                  fontFamily: 'inherit', resize: 'vertical', color: C.text,
                }}
              />
            </div>
          )}

          {/* ── Перебір по готовому блоку ── */}
          {strategyType === 'advanced' && !runningBatch && (
            <div style={{ marginTop: 8 }}>
              <button style={S.btnOutline} onClick={openBlockPanel} disabled={loading}>
                {blockOpen ? '▾' : '▸'} Перебір по готовому блоку
              </button>
              {blockOpen && (
                <div style={{
                  marginTop: 8, padding: '12px 14px', border: `1px solid ${C.border}`,
                  borderRadius: 8, background: '#F8FAFC', fontSize: 12.5, color: C.text,
                }}>
                  <div style={{ color: C.textMd, marginBottom: 10, lineHeight: 1.5 }}>
                    Кожна стратегія блоку перераховується з новими значеннями обраних осей.
                    Значення осей беруться з форми вище. Вікно, капітал, всесвіт і решта
                    параметрів беруться з рядків блоку. Періоди, відсів, двоетапний пошук,
                    «Зберегти топ» і «Мін. Avg.Yr%» — з форми, як у звичайному пошуку.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <label style={{ color: C.textMd }}>Блок:</label>
                    <select
                      value={blockBatchId}
                      onChange={e => { setBlockBatchId(e.target.value); setBlockPreview(null); }}
                      style={{ fontSize: 12, padding: '4px 6px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', minWidth: 320 }}
                    >
                      <option value="">— оберіть сітку —</option>
                      {blocks.map(b => (
                        <option key={b.batch_id} value={b.batch_id}>
                          {b.name || b.batch_id.slice(0, 8)} · {b.rows.toLocaleString()} рядків
                          {b.start_time ? ` · ${b.start_time}…${b.stop_time}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ color: C.textMd, marginBottom: 6 }}>Перебрати заново:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 10 }}>
                    {blockAxesDefs.map(a => (
                      <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={blockAxes.includes(a.key)}
                               onChange={() => toggleBlockAxis(a.key)} />
                        {a.label}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      style={S.btnOutline}
                      onClick={runBlockPreview}
                      disabled={!blockBatchId || blockAxes.length === 0 || blockPreviewing}
                    >
                      {blockPreviewing ? 'Рахую…' : 'Порахувати комбінації'}
                    </button>
                    {blockPreview && !blockPreview.error && (
                      <span>
                        {blockPreview.rows.toLocaleString()} рядків ×{' '}
                        {Object.values(blockPreview.per_axis).join(' × ')}
                        {' = '}<b>{(blockPreview.combos ?? blockPreview.bound).toLocaleString()}</b> комбінацій
                        {blockPreview.combos != null && blockPreview.combos < blockPreview.bound
                          ? ` (з ${blockPreview.bound.toLocaleString()}: однакові злиті)` : ''}
                        {blockPreview.start_time ? ` · вікно блоку ${blockPreview.start_time}…${blockPreview.stop_time}` : ''}
                      </span>
                    )}
                    {blockPreview?.error && (
                      <span style={{ color: '#B91C1C' }}>⛔ {blockPreview.error}</span>
                    )}
                    {(() => {
                      const ok = blockPreview && !blockPreview.error && blockPreview.combos > 0
                        && !blockPreview.window_error;
                      return (
                        <button
                          style={{ ...S.btnPrimary, opacity: (loading || !ok) ? 0.6 : 1 }}
                          disabled={loading || !ok}
                          onClick={() => handleRun({ batchId: blockBatchId, axes: blockAxes })}
                          title={ok ? undefined : 'Спершу порахуйте комбінації'}
                        >
                          ▶ Запустити по блоку
                        </button>
                      );
                    })()}
                  </div>
                  {blockPreview?.window_error && (
                    <div style={{ marginTop: 8, color: '#B91C1C' }}>⛔ {blockPreview.window_error}</div>
                  )}
                  {(blockPreview?.warnings || []).map((w, i) => (
                    <div key={i} style={{
                      marginTop: 8, fontSize: 11.5, color: '#92400E', background: '#FEF3C7',
                      border: '1px solid #FCD34D', borderRadius: 6, padding: '6px 10px',
                    }}>⚠️ {w}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Осі, що не розрізняють комбінації. Стоїть біля лічильника, бо це
              питання не про правило (його описано в блоці [4]), а про те, що
              частина заявлених комбінацій дасть однакові рядки. */}
          {!runningBatch && tpMutedAxes.length > 0 && (
            <div style={{
              marginTop: 6, fontSize: 11.5, color: '#92400E', background: '#FEF3C7',
              border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 12px',
            }}>
              ⚠️ <b>Частина осей не розрізнятиме комбінації{tpMutedHalfOnly ? ' у половині сітки з [4a]' : ''}:</b>{' '}
              {tpMutedAxes.join(' · ')}. Take Profit [4a] із терміном 0 глушить
              Drop%/No-growth/Stagnation на кожному барі, тож ці значення дадуть однакові
              рядки. Термін більший за 0 знімає глушіння.
            </div>
          )}

          {/* Progress bar (visible while running) */}
          {runningBatch && (
            <div style={{ marginTop: 14, padding: '12px 16px', background: '#EFF6FF', border: `1px solid #BFDBFE`, borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>
                  Розраховується…
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm('Скасувати поточний прорахунок?')) return;
                    try {
                      await cancelAnalyzerBatch(runningBatch.batch_id);
                    } catch (_) {}
                    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                    setRunningBatch(null);
                    setLoading(false);
                    setProgress(null);
                    loadResults(0);
                  }}
                  style={{ padding: '3px 10px', fontSize: 12, border: '1px solid #FCA5A5', borderRadius: 5, background: '#FEF2F2', color: '#DC2626', cursor: 'pointer' }}
                >
                  ✕ Скасувати
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 10, background: '#DBEAFE', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 5,
                    background: C.primary,
                    width: `${progress ? progress.progress : 0}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.primary, whiteSpace: 'nowrap' }}>
                  {progress ? progress.progress : 0}%
                </span>
              </div>
              {progress && (
                <div style={{ fontSize: 11, color: C.textMd, marginTop: 5 }}>
                  {/* Двоетапний пошук: на етапі 2 лічильник — це вже відібрані
                      лідери, а не вся сітка; без підпису це читалось би як
                      «пошук почався спочатку». */}
                  {progress.stage === 1 && <><b>Етап 1 з 2</b> · </>}
                  {progress.stage === 2 && (
                    <><b>Етап 2 з 2</b> (дораховуються лідери етапу 1
                      {progress.stage1_rows != null && <> — відібрано з {Number(progress.stage1_rows).toLocaleString()}</>}) · </>
                  )}
                  Комбінацій: {progress.current} / {progress.total}
                  {/* Без цих чисел відсів виглядає як поломка: лічильник біжить,
                      а таблиця майже порожня — хоча відсіяти ~90 % і є нормою. */}
                  {progress.culled != null && (
                    <> · пройшло {Number(progress.passed || 0).toLocaleString()} /
                      {' '}відсіяно {Number(progress.culled || 0).toLocaleString()}</>
                  )}
                </div>
              )}
            </div>
          )}

          <>
            <div style={S.saveRow}>
              <span style={{ fontSize: 13, color: C.textMd }}>Назва:</span>
              <input style={{ ...S.input, width: 220 }} placeholder="Назва прорахунку…" value={searchName} onChange={e => setSearchName(e.target.value)} />
              <button style={S.btnSm} onClick={handleSaveSearch}>💾 Зберегти назву прорахунку</button>
            </div>

            {savedSearches.filter(s => !s.params?._type).length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 12, color: C.textSm, alignSelf: 'center' }}>Збережені:</span>
                {savedSearches.filter(s => !s.params?._type).map(s => (
                  <span key={s.id} style={S.savedTag} onClick={() => handleLoadSearch(s)}>
                    {s.name}
                    <span style={{ color: C.danger, marginLeft: 2 }} onClick={e => handleDeleteSearch(s.id, e)}>×</span>
                  </span>
                ))}
              </div>
            )}
          </>

        </div>

        {/* ── Results Table ── */}
        <div style={S.card}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
            {/* Filter bar: chips + toggle button + save filter */}
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <FilterBar
                filters={filters}
                appliedFilters={appliedFilters}
                showPanel={showFilterPanel}
                onTogglePanel={() => setShowFilterPanel(p => !p)}
                onRemove={removeFilter}
                onToggleOperator={toggleOperatorFn}
                onReset={() => { setActiveSearchId(null); resetFilters(); }}
                hasPendingChanges={hasPendingChanges}
                onApply={applyFilters}
              />
              <StablePresetButton onApply={applyStablePreset} />
              {appliedFilters.length > 0 && (
                <button
                  onClick={handleSaveFilterTag}
                  style={{
                    padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${C.border}`, background: '#F8FAFC', color: C.textMd,
                    cursor: 'pointer',
                  }}
                >
                  💾 Зберегти фільтр
                </button>
              )}
            </div>

            {/* Collapsible filter panel */}
            {showFilterPanel && (
              <FilterPanel
                filters={filters}
                onUpdate={updateFilter}
                onRemove={removeFilter}
                onToggleOperator={toggleOperatorFn}
                onAdd={addFilter}
                onApply={applyFilters}
                hasPendingChanges={hasPendingChanges}
              />
            )}

            {/* Stable preset fallback banner */}
            {lastStablePreset != null && total === 0 && !loading && (
              <div style={{
                marginTop: 10, padding: '10px 14px', background: '#FFFBEB',
                border: '1px solid #FCD34D', borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              }}>
                <span style={{ fontSize: 13, color: '#92400E' }}>
                  ⚠ За порогами «{lastStablePreset === 'ideal' ? 'Ідеал' : lastStablePreset === 'min' ? 'Мінімум' : 'Власні'}» 0 стратегій.
                  Найкращі за Profit.Mo% можуть бути нижче порогів.
                </span>
                <button onClick={applyStableFallback} style={{
                  padding: '6px 14px', background: '#D97706', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                  Показати топ-20 за Profit.Mo%
                </button>
              </div>
            )}

            {/* Favorites button + saved filter tags — below filters */}
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {/* Favorites toggle */}
              <button
                onClick={() => {
                  const next = !showFavoritesOnly;
                  // Update ref synchronously BEFORE other state changes so that
                  // useEffect([appliedFilters]) sees the correct value when it fires.
                  showFavoritesRef.current = next;
                  if (next) {
                    // Activating favorites — deactivate saved filter tags and the
                    // batch. Фільтри користувача лишаються: обране = id_filter,
                    // і сервер накладає на нього решту фільтрів через AND.
                    setActiveSearchId(null);
                    setPage(0);
                    setTotal(0);
                    batchFilterRef.current = '';
                    setBatchFilter('');
                    setQuickIdSearch('');
                    quickIdRef.current = '';
                  }
                  setShowFavoritesOnly(next);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                  border: `2px solid ${showFavoritesOnly ? '#F59E0B' : C.border}`,
                  background: showFavoritesOnly ? '#FEF3C7' : '#F8FAFC',
                  color: showFavoritesOnly ? '#D97706' : C.textMd,
                  cursor: 'pointer',
                }}
              >
                <span>{showFavoritesOnly ? '★' : '☆'}</span>
                <span>{starredIds.size} favorites</span>
              </button>

              {/* Saved filter/batch tags */}
              {savedSearches.filter(s => s.params?._type === 'batch' || s.params?._type === 'filter').map(s => {
                const isBatch = s.params?._type === 'batch';
                const isActive = activeSearchId === s.id;
                return (
                  <span
                    key={s.id}
                    onClick={() => handleLoadSearch(s)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px 3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                      cursor: 'pointer',
                      border: `2px solid ${isActive ? C.primary : (isBatch ? '#BFDBFE' : '#D1FAE5')}`,
                      background: isActive ? C.primaryLt : (isBatch ? '#EFF6FF' : '#F0FDF4'),
                      color: isActive ? C.primary : (isBatch ? '#2563EB' : '#15803D'),
                    }}
                  >
                    <span>{isBatch ? '📊' : '🔍'}</span>
                    <span>{s.name}</span>
                    {isBatch && (
                      <span
                        onClick={e => handleCopySearchToForm(s, e)}
                        title={s.params?.ui
                          ? 'Скопіювати параметри пошуку цієї сітки у форму'
                          : 'Для цієї сітки параметри пошуку не збережено: знімок форми '
                            + 'зберігається лише для сіток, запущених після 25.09'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', marginLeft: 2,
                          padding: '0 2px', cursor: s.params?.ui ? 'pointer' : 'not-allowed',
                          opacity: s.params?.ui ? 1 : 0.35,
                        }}
                      >
                        {/* два аркуші — «копіювати» */}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                             strokeLinejoin="round" aria-hidden="true">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </span>
                    )}
                    {isBatch && (
                      <span
                        onClick={e => openCommentEdit(s, e)}
                        title={s.params?.comment || 'Додати коментар'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 15, height: 15, borderRadius: '50%', marginLeft: 1,
                          fontSize: 10, fontWeight: 700, fontStyle: 'italic', fontFamily: 'Georgia, serif',
                          cursor: 'pointer',
                          border: '1.5px solid currentColor',
                          background: s.params?.comment ? 'currentColor' : 'transparent',
                          opacity: s.params?.comment ? 1 : 0.45,
                        }}
                      >
                        <span style={{ color: s.params?.comment ? '#fff' : 'inherit' }}>i</span>
                      </span>
                    )}
                    <span
                      onClick={e => handleDeleteSearch(s.id, e)}
                      style={{
                        marginLeft: 2, color: C.textSm, fontSize: 14, lineHeight: 1,
                        cursor: 'pointer', padding: '0 2px',
                      }}
                    >×</span>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Results bar: stat pills + recalc + columns */}
          <ResultsBar
            total={total}
            grandTotal={grandTotal}
            hasFilters={appliedFilters.length > 0 || showFavoritesOnly || !!quickIdSearch}
            favoritesCount={starredIds.size}
            showFavoritesOnly={showFavoritesOnly}
            onToggleFavorites={() => setShowFavoritesOnly(f => !f)}
            recalcProgress={recalcProgress}
            setRecalcProgress={setRecalcProgress}
            onRecalcAllConfirm={() => setRecalcConfirm(true)}
            visibleColumns={visibleColumns}
            onToggleColumn={toggleColumn}
            onSelectAllColumns={selectAllColumns}
            onClearAllColumns={clearAllColumns}
            colMenuOpen={colMenuOpen}
            onToggleColMenu={handleToggleColMenu}
            checkedIds={checkedIds}
            recalcSelectedProgress={recalcSelectedProgress}
            onRecalcSelected={handleRecalcSelected}
            onRecheckSelected={() => { setRecheckErr(''); setRecheckMsg(''); setRecheckOpen(true); }}
            onDeleteSelected={handleDeleteSelected}
            onDeleteFiltered={handleDeleteFiltered}
            deletingFiltered={deletingFiltered}
            onDeselectAll={() => setCheckedIds(new Set())}
            loading={loading}
            runningBatch={runningBatch}
            settingsSavedAt={settingsSavedAt}
          />

          {/* Підсумок запуску перевірки — поза модалкою, щоб лишався на очах
              після її закриття (пачка рахується у фоні хвилини-години). */}
          {(recheckMsg || recheckErr) && (
            <div style={{
              margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: 12,
              background: recheckErr ? '#FEF2F2' : '#F0FDF4',
              border: `1px solid ${recheckErr ? '#FECACA' : '#BBF7D0'}`,
              color: recheckErr ? C.danger : '#166534',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ flex: 1 }}>{recheckErr ? `⚠️ ${recheckErr}` : `✓ ${recheckMsg}`}</span>
              <span onClick={() => { setRecheckMsg(''); setRecheckErr(''); }}
                    style={{ cursor: 'pointer', fontWeight: 700 }}>✕</span>
            </div>
          )}

          {/* Модалка перевірки: налаштування ті самі, що в генераторі форми —
              одна пара полів на обидва місця, щоб числа не розʼїхались. */}
          {recheckOpen && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} onClick={() => !recheckBusy && setRecheckOpen(false)}>
              <div onClick={e => e.stopPropagation()} style={{
                background: '#fff', borderRadius: 10, padding: 22, width: 520, maxWidth: '94%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  Перевірити {checkedIds.size} {checkedIds.size === 1 ? 'рядок' : 'рядків'} на нових вікнах
                </div>
                <div style={{ fontSize: 12, color: C.textMd, marginBottom: 14, lineHeight: 1.5 }}>
                  Одні й ті самі вікна дістануться всім вибраним рядкам — інакше їх не
                  порівняти між собою. Вікна допишуться в самі рядки, нових рядків у
                  таблиці не зʼявиться.
                </div>
                {/* Два режими — ті самі, що у формі пошуку. Перебір тут і є
                    способом ДОВЕСТИ кандидата: прохід на сотні вікон нічого не
                    каже про решту, бо гарантія одностороння. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  {[['random', 'випадкові вікна'], ['sweep', 'усі точки входу']].map(([v, label]) => (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4,
                                            fontSize: 12, color: C.textMd, cursor: 'pointer' }}>
                      <input type="radio" name="recheckmode" checked={recheckMode === v}
                        onChange={() => setRecheckMode(v)} />
                      {label}
                    </label>
                  ))}
                </div>
                {recheckMode === 'sweep' && (
                  <div style={{ fontSize: 11, color: C.textSm, lineHeight: 1.55, marginBottom: 8 }}>
                    По одному вікну з <b>кожного торгового дня</b> — повна перевірка
                    кандидата. Точки входу, уже пораховані в цих рядках, удруге не
                    беруться, тож вікна саме <b>додадуться</b> до наявних.
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: C.textSm }}>Довжина</span>
                  <input type="number" min={1} value={genCfg.months}
                    onChange={e => setGenCfg({ ...genCfg, months: e.target.value })}
                    style={{ ...S.input, width: 66, padding: '4px 6px', fontSize: 12 }} />
                  <span style={{ fontSize: 12, color: C.textSm }}>міс. ×</span>
                  <input type="number" min={1} max={100} value={genCfg.count}
                    disabled={recheckMode === 'sweep'}
                    title={recheckMode === 'sweep'
                      ? 'У режимі перебору кількість задають самі дані'
                      : ''}
                    onChange={e => setGenCfg({ ...genCfg, count: e.target.value })}
                    style={{ ...S.input, width: 66, padding: '4px 6px', fontSize: 12,
                             background: recheckMode === 'sweep' ? '#F1F5F9' : '#fff' }} />
                  <span style={{ fontSize: 12, color: C.textSm }}>шт.</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.textMd, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!genCfg.extraEnabled}
                      onChange={e => setGenCfg({ ...genCfg, extraEnabled: e.target.checked })} />
                    + випадково
                  </label>
                  <input type="number" min={0} disabled={!genCfg.extraEnabled} value={genCfg.extraFrom}
                    onChange={e => setGenCfg({ ...genCfg, extraFrom: e.target.value })}
                    style={{ ...S.input, width: 58, padding: '4px 6px', fontSize: 12,
                             background: genCfg.extraEnabled ? '#fff' : '#F1F5F9' }} />
                  <span style={{ fontSize: 12, color: C.textSm }}>…</span>
                  <input type="number" min={0} disabled={!genCfg.extraEnabled} value={genCfg.extraTo}
                    onChange={e => setGenCfg({ ...genCfg, extraTo: e.target.value })}
                    style={{ ...S.input, width: 58, padding: '4px 6px', fontSize: 12,
                             background: genCfg.extraEnabled ? '#fff' : '#F1F5F9' }} />
                  <span style={{ fontSize: 12, color: C.textSm }}>дн.</span>
                </div>
                <div style={{ fontSize: 11, color: C.textMd, marginTop: 10 }}>
                  {recheckMode === 'sweep'
                    ? `≈ ${(checkedIds.size * 1200).toLocaleString()} симуляцій (точна кількість — скільки торгових днів уміститься)`
                    : `≈ ${(checkedIds.size * (1 + (Number(genCfg.count) || 0))).toLocaleString()} симуляцій`}
                  {' '}на {checkedIds.size} {checkedIds.size === 1 ? 'машину' : 'машин'} парку
                  {checkedIds.size === 1 && ' — один рядок дістанеться одному процесу однієї машини'}
                </div>
                {recheckErr && (
                  <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>⚠️ {recheckErr}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button onClick={() => setRecheckOpen(false)} disabled={recheckBusy}
                    style={{ padding: '6px 14px', background: '#fff', color: C.textMd,
                             border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13,
                             cursor: recheckBusy ? 'default' : 'pointer' }}>Скасувати</button>
                  <button onClick={handleRecheckSelected} disabled={recheckBusy}
                    style={{ padding: '6px 16px', background: recheckBusy ? '#94A3B8' : '#7C3AED',
                             color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
                             fontWeight: 600, cursor: recheckBusy ? 'default' : 'pointer' }}>
                    {recheckBusy ? 'Запускаю…' : '🎲 Запустити'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results table */}
          <ResultsTable
            results={displayResults}
            loading={loading}
            pageOffset={pageOffset}
            sortCol={sortCol}
            sortDir={sortDir}
            onSort={handleSort}
            visibleColumns={visibleColumns}
            checkedIds={checkedIds}
            starredIds={starredIds}
            showFavoritesOnly={showFavoritesOnly}
            onToggleCheck={handleToggleCheck}
            onToggleStar={handleToggleStar}
            onSelectAll={handleSelectAll}
            onViewDetail={handleViewDetail}
            onCopyToForm={handleCopyToForm}
            onRunMock={handleRunMock}
            onRecalcOne={handleRecalcOne}
            onDelete={handleDelete}
            recalcingId={recalcingId}
            onToggleFavorites={() => setShowFavoritesOnly(f => !f)}
            onOpenPeriods={openPeriodsPopup}
            runningBotNames={runningBotNames}
          />

          {/* Pagination */}
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            loadedCount={results.length}
            onPageChange={handlePageChange}
            onGoToPage={handleGoToPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>

      </div>

      {/* ── Periods Popup ── */}
      {periodsPopupRow && (
        <PeriodsPopup
          rowId={periodsPopupRow.id}
          rowName={periodsPopupRow.name}
          periods={periodsPopupData}
          onClose={() => { setPeriodsPopupRow(null); setPeriodsPopupData(null); }}
          onAppended={(updatedData) => {
            const pa = updatedData.period_aggregates || {};
            const _new = Array.isArray(updatedData.periods_results) ? updatedData.periods_results : [];
            // Лічильники рядка перераховуємо тут: список їх бере від сервера,
            // а ця відповідь несе вже інший набір вікон.
            const _summary = {
              total:    _new.length,
              included: _new.filter(p => p && !p.is_main && !p.error && p.included !== false).length,
              off:      _new.filter(p => p && !p.is_main && p.included === false).length,
            };
            setPeriodsPopupData(_new);
            setResults(prev => prev.map(r => r.id === periodsPopupRow.id ? {
              ...r,
              periods_summary:             _summary,
              periods_included_count:      _summary.included,
              periods_results:             updatedData.periods_results,
              avg_yearly_return_periods:   updatedData.avg_yearly_return_periods,
              worst_yearly_return_periods: updatedData.worst_yearly_return_periods,
              best_yearly_return_periods:  updatedData.best_yearly_return_periods ?? pa.best_yearly_return,
              avg_monthly_return_periods:       pa.avg_monthly_return,
              worst_max_drawdown_periods:       pa.worst_max_drawdown,
              pooled_profitable_months_periods: pa.pooled_profitable_months_pct,
              pooled_sharpe_periods:            pa.pooled_sharpe,
              worst_calmar_periods:             pa.worst_calmar,
              avg_deals_per_year_periods:       updatedData.avg_deals_per_year_periods ?? pa.avg_deals_per_year,
            } : r));
            setPeriodsPopupRow(prev => prev ? {
              ...prev,
              periods_results: updatedData.periods_results,
            } : prev);
          }}
        />
      )}

      {/* ── Recalculate All confirmation modal ── */}
      {/* {recalcConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: C.cardBg, borderRadius: 12, padding: 28, maxWidth: 420, width: '90%', boxShadow: C.shadowMd }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Перерахувати всі записи?</div>
            <div style={{ fontSize: 13, color: C.textMd, marginBottom: 20, lineHeight: 1.5 }}>
              Перерахувати всі <strong>{total}</strong> записів з поточними параметрами? Це може зайняти кілька хвилин.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={S.btnOutline} onClick={() => setRecalcConfirm(false)}>Скасувати</button>
              <button style={S.btnPrimary} onClick={handleRecalcAll}>Підтвердити</button>
            </div>
          </div>
        </div>
      )} */}

      {/* Коментар до сітки — відкривається кнопкою «i» на плашці «📊 Створено …» */}
      {commentEdit && (
        <div
          onClick={() => !commentEdit.saving && setCommentEdit(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: C.cardBg, borderRadius: 12, padding: 20, maxWidth: 560, width: '100%', boxShadow: C.shadowMd }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Коментар</div>
            <div style={{ fontSize: 12, color: C.textSm, marginBottom: 10 }}>📊 {commentEdit.name}</div>
            <textarea
              autoFocus
              rows={5}
              value={commentEdit.text}
              onChange={e => setCommentEdit(c => ({ ...c, text: e.target.value }))}
              onKeyDown={e => {
                if (e.key === 'Escape') setCommentEdit(null);
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveCommentEdit();
              }}
              placeholder="Довільний текст про цю сітку…"
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 10px',
                borderRadius: 7, border: `1px solid ${C.border}`, fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={S.btnOutline} onClick={() => setCommentEdit(null)} disabled={commentEdit.saving}>Скасувати</button>
              <button style={S.btnPrimary} onClick={saveCommentEdit} disabled={commentEdit.saving}>
                {commentEdit.saving ? 'Зберігаю…' : 'Зберегти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
